/* Vendor-side purchasing UI: POs → Invoices, vendor roster.
   Two exports:
     <PurchasingPanelNav view setView /> — renders inside the side rail.
     <PurchasingPage view setView />     — renders inside the main pane.
   Wires up against /api/purchasing/*. */
import React, { useEffect, useMemo, useRef, useState } from "react";
import {
  Plus, Send, Trash2, Pencil, Mail, ArrowRight, ArrowLeft, Check, FileText,
  Building2, ClipboardList, X, Handshake, AlertCircle, Users, Download,
  Repeat, Pause, Play, Zap, Archive, RotateCcw, Search,
} from "lucide-react";

/* ─── Sortable column header ────────────────────────────────────────
   Hoisted to module scope so its component identity is stable across
   re-renders. Defining it inside a parent component recreates the
   function each render → React tears down and remounts the button →
   click events fired during a re-render get dropped. (That was the
   "have to click six times" bug.) */
const NUMERIC_SORT_KEYS = new Set(["orders", "due", "overdue", "total", "outstanding", "lastOrder"]);
function SortHead({ k, label, align = "left", sort, setSort }) {
  const active = sort.key === k;
  return (
    <button
      type="button"
      onClick={() => setSort(s => {
        if (s.key === k) return { key: k, dir: s.dir === "asc" ? "desc" : "asc" };
        return { key: k, dir: NUMERIC_SORT_KEYS.has(k) ? "desc" : "asc" };
      })}
      style={{
        background: "transparent", border: "none", padding: 0, cursor: "pointer",
        fontSize: 10, fontWeight: 700, color: active ? "#000" : "#6B7280",
        textTransform: "uppercase", letterSpacing: "0.06em",
        textAlign: align, fontFamily: "Inter, sans-serif", width: "100%",
      }}
    >
      {label}{active ? (sort.dir === "asc" ? " ↑" : " ↓") : ""}
    </button>
  );
}

/* Helper hook — useState seeded from localStorage and synced back. */
function usePersistentSort(storageKey, defaultSort) {
  const [sort, setSort] = useState(() => {
    if (typeof window === "undefined") return defaultSort;
    try {
      const raw = window.localStorage.getItem(storageKey);
      if (raw) {
        const parsed = JSON.parse(raw);
        if (parsed && typeof parsed.key === "string" && (parsed.dir === "asc" || parsed.dir === "desc")) {
          return parsed;
        }
      }
    } catch {}
    return defaultSort;
  });
  useEffect(() => {
    try { window.localStorage.setItem(storageKey, JSON.stringify(sort)); } catch {}
  }, [storageKey, sort]);
  return [sort, setSort];
}

/* Compact date — "Mar 4" within the current year, "Mar 4 '24" for
   prior years. Used in the customer list's Contact column for
   "Last order Mar 4 / Reminded Mar 12" metadata lines. */
const fmtShortDate = (iso) => {
  if (!iso) return "";
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return "";
  const now = new Date();
  const sameYear = d.getFullYear() === now.getFullYear();
  return sameYear
    ? d.toLocaleDateString("en-US", { month: "short", day: "numeric" })
    : d.toLocaleDateString("en-US", { month: "short", day: "numeric", year: "2-digit" });
};

const fmtMoney = (n) => {
  const v = Number(n) || 0;
  return `$${v.toLocaleString("en-US", { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;
};
const fmtDate = (d) => d ? new Date(d + "T00:00:00").toLocaleDateString("en-US", { month: "short", day: "numeric", year: "numeric" }) : "—";
const today = () => new Date().toISOString().slice(0, 10);
const addDays = (s, n) => { const d = new Date(s + "T00:00:00"); d.setDate(d.getDate() + n); return d.toISOString().slice(0, 10); };

const STATUS_TINT = {
  draft:     { bg: "#F3F4F6", fg: "#374151", border: "#E5E7EB" },
  sent:      { bg: "#EFF6FF", fg: "#1D4ED8", border: "#BFDBFE" },
  received:  { bg: "#F0F9FF", fg: "#075985", border: "#BAE6FD" },
  invoiced:  { bg: "#F5F3FF", fg: "#6D28D9", border: "#DDD6FE" },
  cancelled: { bg: "#F3F4F6", fg: "#9CA3AF", border: "#E5E7EB" },
  open:      { bg: "#FEF3C7", fg: "#92400E", border: "#FDE68A" },
  overdue:   { bg: "#FEF2F2", fg: "#B91C1C", border: "#FECACA" },
  paid:      { bg: "#ECFDF5", fg: "#047857", border: "#A7F3D0" },
};

function StatusPill({ status }) {
  const t = STATUS_TINT[status] || STATUS_TINT.draft;
  return (
    <span style={{
      display: "inline-block", padding: "3px 9px", borderRadius: 99,
      fontSize: 10, fontWeight: 700, letterSpacing: 0.4, textTransform: "uppercase",
      background: t.bg, color: t.fg, border: `1px solid ${t.border}`, fontFamily: "Inter, sans-serif",
    }}>{status}</span>
  );
}

export function PurchasingPanelNav({ view, setView, counts = {} }) {
  const Item = ({ icon: Icon, label, key: k, badge }) => (
    <button
      onClick={() => setView(k)}
      style={{
        display: "flex", alignItems: "center", gap: 10, width: "100%",
        padding: "10px 14px", borderRadius: 10, border: "none",
        background: view === k ? "#EDEBEA" : "transparent",
        color: view === k ? "#18181B" : "#52525B",
        fontSize: 15, fontWeight: view === k ? 700 : 500,
        cursor: "pointer", fontFamily: "Inter, sans-serif",
        textAlign: "left",
      }}
    >
      <Icon size={16} strokeWidth={1.8} style={{ flexShrink: 0 }} />
      <span style={{ flex: 1 }}>{label}</span>
      {badge ? <span style={{ fontSize: 11, fontWeight: 700, background: "#F4F4F5", color: "#71717A", padding: "2px 7px", borderRadius: 99 }}>{badge}</span> : null}
    </button>
  );
  return (
    <div style={{ padding: "0 8px" }}>
      <div style={{ padding: "16px 14px 12px" }}>
        <h2 style={{ fontSize: 18, fontWeight: 800, margin: 0, color: "#18181B" }}>Finance</h2>
        <p style={{ fontSize: 13, color: "#71717A", margin: "2px 0 0", lineHeight: 1.45 }}>Vendors, customers, POs, and invoices.</p>
      </div>
      <Item icon={Building2} label="Vendors" key="vendors" badge={counts.vendors} />
      <Item icon={Handshake} label="Contracts" key="contracts" badge={counts.contracts} />
      <Item icon={ClipboardList} label="Purchase orders" key="pos" badge={counts.pos} />
      <Item icon={FileText} label="Invoices" key="invoices" badge={counts.invoices} />
      <Item icon={AlertCircle} label="Due invoices" key="due" badge={counts.due} />
      <Item icon={Users} label="Customers" key="clients" badge={counts.clients} />
    </div>
  );
}

export function PurchasingPage({ view, setView, newSignal = null, greenVendors = [], approvedSamples = [] }) {
  const [vendors, setVendors] = useState([]);
  const [pos, setPOs] = useState([]);
  const [invoices, setInvoices] = useState([]);
  const [contracts, setContracts] = useState([]);
  const [recurring, setRecurring] = useState([]);
  const [editingRecurring, setEditingRecurring] = useState(null); // null | "new" | rule
  const [clients, setClients] = useState([]);
  const [defaults, setDefaults] = useState({ paymentTerms: "Net 30", paymentMethod: "ACH" });
  const [loading, setLoading] = useState(true);
  const [err, setErr] = useState("");
  const [editingPO, setEditingPO] = useState(null);            // null | "new" | po
  const [editingInvoice, setEditingInvoice] = useState(null);  // null | "new" | invoice
  const [editingVendor, setEditingVendor] = useState(null);    // null | "new" | vendor
  const [editingContract, setEditingContract] = useState(null); // null | "new" | contract

  const refresh = async () => {
    setLoading(true); setErr("");
    try {
      const [vR, pR, iR, cR, clR, dR, rR] = await Promise.all([
        fetch("/api/purchasing/vendors",            { credentials: "same-origin" }),
        fetch("/api/purchasing/pos",                { credentials: "same-origin" }),
        fetch("/api/purchasing/invoices",           { credentials: "same-origin" }),
        fetch("/api/purchasing/contracts",          { credentials: "same-origin" }),
        fetch("/api/clients",                       { credentials: "same-origin" }),
        fetch("/api/settings/payment-defaults",     { credentials: "same-origin" }),
        fetch("/api/purchasing/recurring",          { credentials: "same-origin" }),
      ]);
      const v = vR.ok  ? await vR.json()  : { vendors: [] };
      const p = pR.ok  ? await pR.json()  : { pos: [] };
      const i = iR.ok  ? await iR.json()  : { invoices: [] };
      const c = cR.ok  ? await cR.json()  : { contracts: [] };
      const cl = clR.ok ? await clR.json() : { clients: [] };
      const d = dR.ok  ? await dR.json()  : { defaults: { paymentTerms: "Net 30", paymentMethod: "ACH" } };
      const rec = rR.ok ? await rR.json() : { recurring: [] };
      setVendors(v.vendors || []);
      setPOs(p.pos || []);
      setInvoices(i.invoices || []);
      setContracts(c.contracts || []);
      setClients(cl.clients || []);
      setDefaults(d.defaults || { paymentTerms: "Net 30", paymentMethod: "ACH" });
      setRecurring(rec.recurring || []);
    } catch (e) { setErr(e.message); } finally { setLoading(false); }
  };
  useEffect(() => { refresh(); }, []);

  // Locally merge a patch into a single client without re-fetching the
  // whole purchasing payload. Used by ClientsView for instant archive
  // / unarchive — server-side update fires in parallel; UI never shows
  // a loading state for tiny per-row toggles.
  // Accepts either a plain patch object OR a function (current → patch)
  // for cases where the new fields depend on the current customer
  // record (e.g. removing an order from shopifyUnpaidOrders and
  // recomputing totalDue / totalOverdue from what's left).
  const patchClientLocal = (id, patch) => {
    setClients(list => list.map(c => {
      if (c.id !== id) return c;
      const p = typeof patch === "function" ? patch(c) : patch;
      return { ...c, ...p };
    }));
  };

  // Side-nav "New …" CTA bumps a counter on App-side state. When that
  // changes, open the matching modal. The counter pattern guarantees
  // the same kind can be requested twice in a row without React
  // skipping the effect because the value didn't change.
  useEffect(() => {
    if (!newSignal) return;
    if (newSignal.kind === "po")        setEditingPO("new");
    if (newSignal.kind === "invoice")   setEditingInvoice("new");
    if (newSignal.kind === "vendor")    setEditingVendor("new");
    if (newSignal.kind === "contract")  setEditingContract("new");
    if (newSignal.kind === "recurring") setEditingRecurring("new");
  }, [newSignal]);

  return (
    <div>
      {err && (
        <div style={{ marginBottom: 16, padding: "10px 14px", background: "#FEF2F2", border: "1px solid #FECACA", borderRadius: 8, color: "#991B1B", fontSize: 13 }}>
          {err}
        </div>
      )}

      {view === "contracts" && (
        <ContractsView
          contracts={contracts} vendors={vendors} loading={loading}
          onNew={() => setEditingContract("new")}
          onEdit={(c) => setEditingContract(c)}
        />
      )}
      {view === "recurring" && (
        <RecurringView
          recurring={recurring}
          loading={loading}
          onNew={() => setEditingRecurring("new")}
          onEdit={(r) => setEditingRecurring(r)}
          refresh={refresh}
        />
      )}
      {view === "due" && (
        <DueInvoicesView invoices={invoices} loading={loading} onEdit={(inv) => setEditingInvoice(inv)} />
      )}
      {view === "clients" && (
        <ClientsView clients={clients} loading={loading} defaults={defaults} refresh={refresh} patchClientLocal={patchClientLocal} newSignal={newSignal} />
      )}
      {view === "pos" && (
        <POsView
          pos={pos} vendors={vendors} loading={loading}
          onNew={() => setEditingPO("new")}
          onEdit={(po) => setEditingPO(po)}
          refresh={refresh}
        />
      )}
      {view === "invoices" && (
        <InvoicesView
          invoices={invoices} vendors={vendors} loading={loading}
          onNew={() => setEditingInvoice("new")}
          onEdit={(inv) => setEditingInvoice(inv)}
          refresh={refresh}
        />
      )}
      {view === "vendors" && (
        <VendorsView
          vendors={vendors} loading={loading}
          greenVendors={greenVendors}
          approvedSamples={approvedSamples}
          onNew={() => setEditingVendor("new")}
          onEdit={(v) => setEditingVendor(v)}
          refresh={refresh}
        />
      )}

      {editingPO && (
        <POModal
          po={editingPO === "new" ? null : editingPO}
          vendors={vendors}
          greenVendors={greenVendors}
          approvedSamples={approvedSamples}
          contracts={contracts}
          onClose={() => setEditingPO(null)}
          onSaved={async () => { setEditingPO(null); await refresh(); }}
          onAfterConvert={() => { setEditingPO(null); setView("invoices"); refresh(); }}
        />
      )}
      {editingInvoice && (
        <InvoiceModal
          invoice={editingInvoice === "new" ? null : editingInvoice}
          vendors={vendors}
          greenVendors={greenVendors}
          onClose={() => setEditingInvoice(null)}
          onSaved={async () => { setEditingInvoice(null); await refresh(); }}
        />
      )}
      {editingVendor && (
        <VendorModal
          vendor={editingVendor === "new" ? null : editingVendor}
          onClose={() => setEditingVendor(null)}
          onSaved={async () => { setEditingVendor(null); await refresh(); }}
        />
      )}
      {editingContract && (
        <ContractModal
          contract={editingContract === "new" ? null : editingContract}
          vendors={vendors}
          greenVendors={greenVendors}
          onClose={() => setEditingContract(null)}
          onSaved={async () => { setEditingContract(null); await refresh(); }}
        />
      )}
      {editingRecurring && (
        <RecurringModal
          rule={editingRecurring === "new" ? null : editingRecurring}
          vendors={vendors}
          contracts={contracts}
          onClose={() => setEditingRecurring(null)}
          onSaved={async () => { setEditingRecurring(null); await refresh(); }}
        />
      )}
    </div>
  );
}

/* ─────────────────── POs view ─────────────────── */
function POsView({ pos, vendors, loading, onNew, onEdit, refresh }) {
  const [filter, setFilter] = useState("all");
  const filtered = useMemo(() => {
    if (filter === "all") return pos;
    return pos.filter(p => p.status === filter);
  }, [pos, filter]);
  const sendPO = async (po) => {
    try {
      const r = await fetch(`/api/purchasing/pos/${po.id}/send`, { method: "POST", credentials: "same-origin", headers: { "Content-Type": "application/json" }, body: JSON.stringify({}) });
      if (!r.ok) { const d = await r.json().catch(() => ({})); throw new Error(d.error || "Couldn't send"); }
      await refresh();
    } catch (e) { alert(e.message); }
  };
  const convert = async (po) => {
    if (!confirm(`Convert ${po.number} to an invoice? Goods are recorded as received and a vendor invoice is created.`)) return;
    try {
      const r = await fetch(`/api/purchasing/pos/${po.id}/convert-to-invoice`, { method: "POST", credentials: "same-origin", headers: { "Content-Type": "application/json" }, body: JSON.stringify({}) });
      if (!r.ok) { const d = await r.json().catch(() => ({})); throw new Error(d.error || "Couldn't convert"); }
      await refresh();
    } catch (e) { alert(e.message); }
  };
  const remove = async (po) => {
    if (!confirm(`Delete ${po.number}? Can't be undone.`)) return;
    try {
      const r = await fetch(`/api/purchasing/pos/${po.id}`, { method: "DELETE", credentials: "same-origin" });
      if (!r.ok) { const d = await r.json().catch(() => ({})); throw new Error(d.error || "Couldn't delete"); }
      await refresh();
    } catch (e) { alert(e.message); }
  };
  return (
    <div>
      <PageHeader
        title="Purchase orders"
        subtitle="Draft, send, and track POs to your vendors."
      />
      <Tabs
        tabs={[
          { v: "all", label: "All", n: pos.length },
          { v: "draft", label: "Draft", n: pos.filter(p => p.status === "draft").length },
          { v: "sent", label: "Sent", n: pos.filter(p => p.status === "sent").length },
          { v: "received", label: "Received", n: pos.filter(p => p.status === "received").length },
          { v: "invoiced", label: "Invoiced", n: pos.filter(p => p.status === "invoiced").length },
        ]}
        active={filter} onChange={setFilter}
      />
      {loading ? <Empty>Loading…</Empty>
        : filtered.length === 0 ? <Empty>No purchase orders yet.</Empty>
        : (
          <div style={{ background: "#fff", border: "1px solid #F3F4F6", borderRadius: 12, overflow: "hidden" }}>
            {filtered.map((po, i) => (
              <div key={po.id} style={{
                display: "grid", gridTemplateColumns: "120px 1fr 110px 130px 110px 1fr",
                alignItems: "center", gap: 14, padding: "14px 18px",
                borderBottom: i === filtered.length - 1 ? "none" : "1px solid #F3F4F6",
                cursor: "pointer",
              }} onClick={() => onEdit(po)}>
                <div style={{ fontSize: 13, fontWeight: 700, color: "#111", fontVariantNumeric: "tabular-nums" }}>{po.number}</div>
                <div style={{ minWidth: 0 }}>
                  <div style={{ fontSize: 14, fontWeight: 600, color: "#111", whiteSpace: "nowrap", overflow: "hidden", textOverflow: "ellipsis" }}>
                    {po.vendorSnapshot?.name || <span style={{ color: "#9CA3AF" }}>(no vendor)</span>}
                  </div>
                  <div style={{ fontSize: 12, color: "#6B7280", marginTop: 2 }}>{po.lineItems?.length || 0} item{po.lineItems?.length === 1 ? "" : "s"}</div>
                </div>
                <div style={{ fontSize: 13, color: "#374151" }}>{fmtDate(po.issueDate)}</div>
                <StatusPill status={po.status} />
                <div style={{ fontSize: 14, fontWeight: 700, color: "#111", textAlign: "right", fontVariantNumeric: "tabular-nums" }}>{fmtMoney(po.total)}</div>
                <div style={{ display: "inline-flex", gap: 6, justifyContent: "flex-end" }} onClick={(e) => e.stopPropagation()}>
                  {po.status !== "invoiced" && po.status !== "cancelled" && (
                    <IconBtn label="Send to vendor" onClick={() => sendPO(po)}><Mail size={13} /></IconBtn>
                  )}
                  {po.status !== "invoiced" && po.status !== "cancelled" && (
                    <IconBtn label="Convert to invoice" onClick={() => convert(po)} dark><ArrowRight size={13} /></IconBtn>
                  )}
                  <IconBtn label="Edit" onClick={() => onEdit(po)}><Pencil size={13} /></IconBtn>
                  {po.status === "draft" && (
                    <IconBtn label="Delete" onClick={() => remove(po)} danger><Trash2 size={13} /></IconBtn>
                  )}
                </div>
              </div>
            ))}
          </div>
        )}
    </div>
  );
}

/* ─────────────────── Invoices view ─────────────────── */
function InvoicesView({ invoices, vendors, loading, onNew, onEdit, refresh }) {
  const [filter, setFilter] = useState("all");
  const filtered = useMemo(() => {
    if (filter === "all") return invoices;
    if (filter === "unpaid") return invoices.filter(i => i.status === "open" || i.status === "overdue");
    return invoices.filter(i => i.status === filter);
  }, [invoices, filter]);
  const markPaid = async (inv) => {
    try {
      const r = await fetch(`/api/purchasing/invoices/${inv.id}/mark-paid`, { method: "POST", credentials: "same-origin", headers: { "Content-Type": "application/json" }, body: JSON.stringify({}) });
      if (!r.ok) { const d = await r.json().catch(() => ({})); throw new Error(d.error || "Couldn't mark paid"); }
      await refresh();
    } catch (e) { alert(e.message); }
  };
  const remove = async (inv) => {
    if (!confirm(`Delete ${inv.number}? Can't be undone.`)) return;
    try {
      const r = await fetch(`/api/purchasing/invoices/${inv.id}`, { method: "DELETE", credentials: "same-origin" });
      if (!r.ok) { const d = await r.json().catch(() => ({})); throw new Error(d.error || "Couldn't delete"); }
      await refresh();
    } catch (e) { alert(e.message); }
  };
  return (
    <div>
      <PageHeader
        title="Invoices"
        subtitle="Vendor bills — opened from a PO or created standalone."
      />
      <Tabs
        tabs={[
          { v: "all", label: "All", n: invoices.length },
          { v: "unpaid", label: "Unpaid", n: invoices.filter(i => i.status === "open" || i.status === "overdue").length },
          { v: "overdue", label: "Overdue", n: invoices.filter(i => i.status === "overdue").length },
          { v: "paid", label: "Paid", n: invoices.filter(i => i.status === "paid").length },
        ]}
        active={filter} onChange={setFilter}
      />
      {loading ? <Empty>Loading…</Empty>
        : filtered.length === 0 ? <Empty>No invoices yet.</Empty>
        : (
          <div style={{ background: "#fff", border: "1px solid #F3F4F6", borderRadius: 12, overflow: "hidden" }}>
            {filtered.map((inv, i) => (
              <div key={inv.id} style={{
                display: "grid", gridTemplateColumns: "120px 1fr 130px 100px 110px 1fr",
                alignItems: "center", gap: 14, padding: "14px 18px",
                borderBottom: i === filtered.length - 1 ? "none" : "1px solid #F3F4F6",
                cursor: "pointer",
              }} onClick={() => onEdit(inv)}>
                <div style={{ fontSize: 13, fontWeight: 700, color: "#111", fontVariantNumeric: "tabular-nums" }}>{inv.number}</div>
                <div style={{ minWidth: 0 }}>
                  <div style={{ fontSize: 14, fontWeight: 600, color: "#111", whiteSpace: "nowrap", overflow: "hidden", textOverflow: "ellipsis" }}>
                    {inv.vendorSnapshot?.name || <span style={{ color: "#9CA3AF" }}>(no vendor)</span>}
                  </div>
                  <div style={{ fontSize: 12, color: "#6B7280", marginTop: 2 }}>
                    {inv.sourcePoNumber ? `from ${inv.sourcePoNumber}` : `${inv.lineItems?.length || 0} item${inv.lineItems?.length === 1 ? "" : "s"}`}
                  </div>
                </div>
                <div style={{ fontSize: 13, color: "#374151" }}>Due {fmtDate(inv.dueDate)}</div>
                <StatusPill status={inv.status} />
                <div style={{ fontSize: 14, fontWeight: 700, color: "#111", textAlign: "right", fontVariantNumeric: "tabular-nums" }}>{fmtMoney(inv.total)}</div>
                <div style={{ display: "inline-flex", gap: 6, justifyContent: "flex-end" }} onClick={(e) => e.stopPropagation()}>
                  {inv.status !== "paid" && inv.status !== "cancelled" && (
                    <IconBtn label="Mark paid" onClick={() => markPaid(inv)} dark><Check size={13} /></IconBtn>
                  )}
                  <IconBtn label="Edit" onClick={() => onEdit(inv)}><Pencil size={13} /></IconBtn>
                  <IconBtn label="Delete" onClick={() => remove(inv)} danger><Trash2 size={13} /></IconBtn>
                </div>
              </div>
            ))}
          </div>
        )}
    </div>
  );
}

/* ─────────────────── Vendors view ─────────────────── */
function VendorsView({ vendors, loading, greenVendors = [], approvedSamples = [], onNew, onEdit, refresh }) {
  const remove = async (v) => {
    if (!confirm(`Delete ${v.name}? Existing POs and invoices keep a snapshot of this vendor.`)) return;
    try {
      const r = await fetch(`/api/purchasing/vendors/${v.id}`, { method: "DELETE", credentials: "same-origin" });
      if (!r.ok) { const d = await r.json().catch(() => ({})); throw new Error(d.error || "Couldn't delete"); }
      await refresh();
    } catch (e) { alert(e.message); }
  };

  // Count approved samples per lab green vendor (matched by name).
  // Lets the user see at a glance how many lots they've sourced from
  // each Lab vendor without leaving the page.
  const sampleCountByVendor = useMemo(() => {
    const map = new Map();
    for (const s of approvedSamples) {
      const k = String(s.sourcedFrom || "").trim().toLowerCase();
      if (!k) continue;
      map.set(k, (map.get(k) || 0) + 1);
    }
    return map;
  }, [approvedSamples]);

  const hasGreen = greenVendors.length > 0;
  const hasOther = vendors.length > 0;
  const totalCount = greenVendors.length + vendors.length;

  return (
    <div>
      <PageHeader
        title="Vendors"
        subtitle="Coffee suppliers (auto-imported from the Lab) plus any other vendor you buy from — packaging, services, equipment."
      />

      {loading ? <Empty>Loading…</Empty>
        : (totalCount === 0 ? <Empty>No vendors yet.</Empty>
        : (
          <div style={{ display: "flex", flexDirection: "column", gap: 18 }}>
            {/* ── Coffee suppliers (Lab green vendors) ─────────────── */}
            <section>
              <div style={{ display: "flex", alignItems: "baseline", justifyContent: "space-between", marginBottom: 8 }}>
                <h3 style={{ fontSize: 12, fontWeight: 700, color: "#6B7280", textTransform: "uppercase", letterSpacing: "0.07em", margin: 0 }}>
                  Coffee suppliers <span style={{ color: "#9CA3AF", fontWeight: 500, marginLeft: 4, textTransform: "none", letterSpacing: 0 }}>({greenVendors.length})</span>
                </h3>
                <span style={{ fontSize: 11, color: "#9CA3AF" }}>Synced from Cupping &amp; QC → Sources</span>
              </div>
              {hasGreen ? (
                <div style={{ background: "#fff", border: "1px solid #F3F4F6", borderRadius: 12, overflow: "hidden" }}>
                  {greenVendors.map((v, i) => {
                    const count = sampleCountByVendor.get(String(v.name || "").trim().toLowerCase()) || 0;
                    return (
                      <div key={v.id} style={{
                        display: "grid", gridTemplateColumns: "1fr 240px 160px 60px",
                        alignItems: "center", gap: 14, padding: "14px 18px",
                        borderBottom: i === greenVendors.length - 1 ? "none" : "1px solid #F3F4F6",
                      }}>
                        <div>
                          <div style={{ fontSize: 14, fontWeight: 600, color: "#111", display: "flex", alignItems: "center", gap: 8 }}>
                            {v.name}
                            <span style={{ fontSize: 9, fontWeight: 700, letterSpacing: "0.08em", textTransform: "uppercase", color: "#2C5766", background: "#EAF1F3", border: "1px solid #B8CED5", borderRadius: 99, padding: "2px 7px" }}>Lab</span>
                          </div>
                        </div>
                        <div style={{ fontSize: 13, color: "#374151", whiteSpace: "nowrap", overflow: "hidden", textOverflow: "ellipsis" }}>{v.email || "—"}</div>
                        <div style={{ fontSize: 13, color: "#6B7280" }}>{v.paymentTerms || "—"}</div>
                        <div style={{ fontSize: 12, color: count > 0 ? "#000" : "#9CA3AF", textAlign: "right", fontVariantNumeric: "tabular-nums" }}>
                          {count > 0 ? `${count} sample${count === 1 ? "" : "s"}` : "—"}
                        </div>
                      </div>
                    );
                  })}
                </div>
              ) : (
                <div style={{ background: "#FAFAFA", borderRadius: 12, padding: "20px 24px", color: "#9CA3AF", fontSize: 13, border: "1px dashed #E5E7EB" }}>
                  No coffee suppliers yet. Add them under <strong style={{ color: "#000", fontWeight: 600 }}>Cupping &amp; QC → Green Vendors</strong>.
                </div>
              )}
            </section>

            {/* ── Other vendors (manual purchasing roster) ─────────── */}
            <section>
              <div style={{ display: "flex", alignItems: "baseline", justifyContent: "space-between", marginBottom: 8 }}>
                <h3 style={{ fontSize: 12, fontWeight: 700, color: "#6B7280", textTransform: "uppercase", letterSpacing: "0.07em", margin: 0 }}>
                  Other vendors <span style={{ color: "#9CA3AF", fontWeight: 500, marginLeft: 4, textTransform: "none", letterSpacing: 0 }}>({vendors.length})</span>
                </h3>
                <span style={{ fontSize: 11, color: "#9CA3AF" }}>Packaging, services, equipment — anything non-coffee</span>
              </div>
              {hasOther ? (
                <div style={{ background: "#fff", border: "1px solid #F3F4F6", borderRadius: 12, overflow: "hidden" }}>
                  {vendors.map((v, i) => (
                    <div key={v.id} style={{
                      display: "grid", gridTemplateColumns: "1fr 240px 200px 90px",
                      alignItems: "center", gap: 14, padding: "14px 18px",
                      borderBottom: i === vendors.length - 1 ? "none" : "1px solid #F3F4F6",
                      cursor: "pointer",
                    }} onClick={() => onEdit(v)}>
                      <div style={{ fontSize: 14, fontWeight: 600, color: "#111" }}>{v.name}</div>
                      <div style={{ fontSize: 13, color: "#374151", whiteSpace: "nowrap", overflow: "hidden", textOverflow: "ellipsis" }}>{v.email || "—"}</div>
                      <div style={{ fontSize: 13, color: "#6B7280", whiteSpace: "nowrap", overflow: "hidden", textOverflow: "ellipsis" }}>{v.paymentTerms || "—"}</div>
                      <div style={{ display: "inline-flex", gap: 6, justifyContent: "flex-end" }} onClick={(e) => e.stopPropagation()}>
                        <IconBtn label="Edit" onClick={() => onEdit(v)}><Pencil size={13} /></IconBtn>
                        <IconBtn label="Delete" onClick={() => remove(v)} danger><Trash2 size={13} /></IconBtn>
                      </div>
                    </div>
                  ))}
                </div>
              ) : (
                <div style={{ background: "#FAFAFA", borderRadius: 12, padding: "20px 24px", color: "#9CA3AF", fontSize: 13, border: "1px dashed #E5E7EB" }}>
                  No other vendors yet. Use the <strong style={{ color: "#000", fontWeight: 600 }}>+ New vendor</strong> CTA on the left to add one.
                </div>
              )}
            </section>
          </div>
        ))}
    </div>
  );
}

/* ─────────────────── PO modal ─────────────────── */
function POModal({ po, vendors, greenVendors = [], approvedSamples = [], contracts = [], onClose, onSaved, onAfterConvert }) {
  // Vendor picker accepts "vnd:<id>" for purchasing vendors and
  // "src:<id>" for Cupping & QC green vendors. The src: branch sends
  // a one-shot vendorSnapshot to the server instead of a vendorId,
  // so the PO carries vendor data without us forcing a duplicate
  // record into the purchasing roster.
  const initialChoice = (() => {
    if (po?.vendorId) {
      // existing PO: was it picked from the green-vendor list?
      if (greenVendors.some(g => g.id === po.vendorId)) return `src:${po.vendorId}`;
      if (vendors.some(v => v.id === po.vendorId)) return `vnd:${po.vendorId}`;
    }
    if (vendors[0]) return `vnd:${vendors[0].id}`;
    if (greenVendors[0]) return `src:${greenVendors[0].id}`;
    return "";
  })();
  const [form, setForm] = useState(() => ({
    vendorChoice: initialChoice,
    contractId: po?.contractId || "",
    issueDate: po?.issueDate || today(),
    expectedDate: po?.expectedDate || addDays(today(), 14),
    notes: po?.notes || "",
    taxPct: po?.taxPct || 0,
    lineItems: po?.lineItems?.length ? po.lineItems.map(l => ({ ...l })) : [emptyLine()],
  }));

  // Active contracts that match the currently-selected purchasing
  // vendor. We don't show contracts for "src:" green-vendor picks
  // because contracts are anchored to vendor records, not Lab sources.
  const eligibleContracts = useMemo(() => {
    const [kind, id] = (form.vendorChoice || "").split(":");
    if (kind !== "vnd") return [];
    return contracts.filter(c => c.vendorId === id && (c.status || "active") === "active");
  }, [contracts, form.vendorChoice]);

  // Resolve the active vendor name from the vendorChoice — used to
  // filter approved samples to only those sourced from THIS vendor.
  // Without this filter the line-item picker offers samples from any
  // green vendor, which would let you slot a Cafe Imports lot into a
  // Genuine Origin PO. Case-insensitive name match against the
  // sample's `sourcedFrom`.
  const activeVendorName = useMemo(() => {
    if (!form.vendorChoice) return "";
    const [kind, id] = form.vendorChoice.split(":");
    if (kind === "vnd") return vendors.find(x => x.id === id)?.name || "";
    if (kind === "src") return greenVendors.find(x => x.id === id)?.name || "";
    return "";
  }, [form.vendorChoice, vendors, greenVendors]);

  const samplesForVendor = useMemo(() => {
    if (!activeVendorName) return [];
    const target = activeVendorName.trim().toLowerCase();
    return approvedSamples.filter(s =>
      String(s.sourcedFrom || "").trim().toLowerCase() === target
    );
  }, [approvedSamples, activeVendorName]);
  const [saving, setSaving] = useState(false);
  const [err, setErr] = useState("");
  const totals = useMemo(() => computeTotals(form.lineItems, form.taxPct), [form.lineItems, form.taxPct]);
  const isExisting = !!po;
  const locked = po && po.status !== "draft";

  // Resolve the vendorChoice into either {vendorId} (for purchasing
  // vendors) or {vendorSnapshot} (for green vendors from Cupping).
  const resolveVendor = () => {
    if (!form.vendorChoice) return {};
    const [kind, id] = form.vendorChoice.split(":");
    if (kind === "vnd") return { vendorId: id };
    if (kind === "src") {
      const g = greenVendors.find(x => x.id === id);
      if (!g) return {};
      return {
        vendorSnapshot: {
          id: g.id,
          name: g.name || "",
          email: g.email || "",
          address: g.address || "",
          phone: g.phone || "",
          paymentTerms: g.paymentTerms || "",
        },
      };
    }
    return {};
  };

  const save = async (then) => {
    setSaving(true); setErr("");
    try {
      const url = isExisting ? `/api/purchasing/pos/${po.id}` : `/api/purchasing/pos`;
      const method = isExisting ? "PATCH" : "POST";
      const { vendorChoice: _vc, ...rest } = form;
      const payload = { ...rest, contractId: form.contractId || null, ...resolveVendor() };
      const r = await fetch(url, { method, credentials: "same-origin", headers: { "Content-Type": "application/json" }, body: JSON.stringify(payload) });
      if (!r.ok) { const d = await r.json().catch(() => ({})); throw new Error(d.error || "Couldn't save"); }
      const body = await r.json();
      if (then === "send" && body.po?.id) {
        await fetch(`/api/purchasing/pos/${body.po.id}/send`, { method: "POST", credentials: "same-origin", headers: { "Content-Type": "application/json" }, body: JSON.stringify({}) });
      }
      if (then === "convert" && body.po?.id) {
        const cr = await fetch(`/api/purchasing/pos/${body.po.id}/convert-to-invoice`, { method: "POST", credentials: "same-origin", headers: { "Content-Type": "application/json" }, body: JSON.stringify({}) });
        if (!cr.ok) { const d = await cr.json().catch(() => ({})); throw new Error(d.error || "Couldn't convert"); }
        onAfterConvert?.();
        return;
      }
      onSaved?.();
    } catch (e) { setErr(e.message); } finally { setSaving(false); }
  };

  return (
    <ModalShell title={isExisting ? `Edit ${po.number}` : "New purchase order"} onClose={onClose} status={po?.status}>
      <Field label="Vendor">
        <select value={form.vendorChoice} onChange={(e) => setForm(f => ({ ...f, vendorChoice: e.target.value }))} disabled={locked} style={fieldInput}>
          <option value="">— pick a vendor —</option>
          {vendors.length > 0 && (
            <optgroup label="Purchasing vendors">
              {vendors.map(v => <option key={v.id} value={`vnd:${v.id}`}>{v.name}{v.email ? ` (${v.email})` : ""}</option>)}
            </optgroup>
          )}
          {greenVendors.length > 0 && (
            <optgroup label="Green vendors (from Lab)">
              {greenVendors.map(g => <option key={g.id} value={`src:${g.id}`}>{g.name}{g.email ? ` (${g.email})` : ""}</option>)}
            </optgroup>
          )}
        </select>
      </Field>
      {eligibleContracts.length > 0 && (
        <Field label="Draw down from contract (optional)" hint="Bag-counted line items reduce this contract's bags-remaining.">
          <select value={form.contractId} onChange={(e) => setForm(f => ({ ...f, contractId: e.target.value }))} disabled={locked} style={fieldInput}>
            <option value="">— none —</option>
            {eligibleContracts.map(c => {
              const remaining = c.bagsContracted != null ? Math.max(0, Number(c.bagsContracted) - (Number(c.bagsReleased) || 0)) : null;
              const label = [c.contractNumber || c.id.slice(-6), c.coffee, remaining != null ? `${remaining} bags left` : null].filter(Boolean).join(" · ");
              return <option key={c.id} value={c.id}>{label}</option>;
            })}
          </select>
        </Field>
      )}
      <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 12 }}>
        <Field label="Issued"><input type="date" value={form.issueDate} onChange={(e) => setForm(f => ({ ...f, issueDate: e.target.value }))} disabled={locked} style={fieldInput} /></Field>
        <Field label="Expected"><input type="date" value={form.expectedDate} onChange={(e) => setForm(f => ({ ...f, expectedDate: e.target.value }))} disabled={locked} style={fieldInput} /></Field>
      </div>
      <LineItemsEditor lineItems={form.lineItems} setLineItems={(li) => setForm(f => ({ ...f, lineItems: li }))} disabled={locked} approvedSamples={samplesForVendor} vendorName={activeVendorName} />
      <div style={{ display: "grid", gridTemplateColumns: "1fr 110px", gap: 12, alignItems: "end" }}>
        <Field label="Notes (visible on the PO email)"><textarea rows={3} value={form.notes} onChange={(e) => setForm(f => ({ ...f, notes: e.target.value }))} disabled={locked} style={{ ...fieldInput, resize: "vertical" }} /></Field>
        <Field label="Tax %"><input type="number" min="0" max="100" step="0.01" value={form.taxPct} onChange={(e) => setForm(f => ({ ...f, taxPct: parseFloat(e.target.value) || 0 }))} disabled={locked} style={fieldInput} /></Field>
      </div>
      <Totals subtotal={totals.subtotal} taxAmt={totals.taxAmt} total={totals.total} taxPct={form.taxPct} />
      {err && <ErrBox>{err}</ErrBox>}
      <div style={{ display: "flex", gap: 8, justifyContent: "flex-end", marginTop: 16, flexWrap: "wrap" }}>
        <BtnGhost onClick={onClose}>Cancel</BtnGhost>
        {!locked && <BtnLight onClick={() => save()} disabled={saving}>{saving ? "Saving…" : "Save draft"}</BtnLight>}
        {!locked && <BtnLight onClick={() => save("send")} disabled={saving || !form.vendorChoice}><Mail size={13} /> Save & email vendor</BtnLight>}
        {isExisting && po.status !== "invoiced" && po.status !== "cancelled" && (
          <BtnDark onClick={() => save("convert")} disabled={saving}><ArrowRight size={13} /> Convert to invoice</BtnDark>
        )}
        {!isExisting && <BtnDark onClick={() => save()} disabled={saving || !form.vendorChoice}>Create PO</BtnDark>}
      </div>
    </ModalShell>
  );
}

/* ─────────────────── Invoice modal ─────────────────── */
function InvoiceModal({ invoice, vendors, greenVendors = [], onClose, onSaved }) {
  const initialChoice = (() => {
    if (invoice?.vendorId) {
      if (greenVendors.some(g => g.id === invoice.vendorId)) return `src:${invoice.vendorId}`;
      if (vendors.some(v => v.id === invoice.vendorId)) return `vnd:${invoice.vendorId}`;
    }
    if (vendors[0]) return `vnd:${vendors[0].id}`;
    if (greenVendors[0]) return `src:${greenVendors[0].id}`;
    return "";
  })();
  const [form, setForm] = useState(() => ({
    vendorChoice: initialChoice,
    issueDate: invoice?.issueDate || today(),
    dueDate: invoice?.dueDate || addDays(today(), 30),
    notes: invoice?.notes || "",
    taxPct: invoice?.taxPct || 0,
    paymentMethod: invoice?.paymentMethod || "",
    lineItems: invoice?.lineItems?.length ? invoice.lineItems.map(l => ({ ...l })) : [emptyLine()],
  }));
  const [saving, setSaving] = useState(false);
  const [err, setErr] = useState("");
  const totals = useMemo(() => computeTotals(form.lineItems, form.taxPct), [form.lineItems, form.taxPct]);
  const isExisting = !!invoice;
  const isPaid = invoice?.status === "paid";

  const resolveVendor = () => {
    if (!form.vendorChoice) return {};
    const [kind, id] = form.vendorChoice.split(":");
    if (kind === "vnd") return { vendorId: id };
    if (kind === "src") {
      const g = greenVendors.find(x => x.id === id);
      if (!g) return {};
      return {
        vendorSnapshot: {
          id: g.id, name: g.name || "", email: g.email || "",
          address: g.address || "", phone: g.phone || "", paymentTerms: g.paymentTerms || "",
        },
      };
    }
    return {};
  };

  const save = async (then) => {
    setSaving(true); setErr("");
    try {
      const url = isExisting ? `/api/purchasing/invoices/${invoice.id}` : `/api/purchasing/invoices`;
      const method = isExisting ? "PATCH" : "POST";
      const { vendorChoice: _vc, ...rest } = form;
      const payload = { ...rest, ...resolveVendor() };
      const r = await fetch(url, { method, credentials: "same-origin", headers: { "Content-Type": "application/json" }, body: JSON.stringify(payload) });
      if (!r.ok) { const d = await r.json().catch(() => ({})); throw new Error(d.error || "Couldn't save"); }
      const body = await r.json();
      if (then === "paid" && body.invoice?.id) {
        await fetch(`/api/purchasing/invoices/${body.invoice.id}/mark-paid`, { method: "POST", credentials: "same-origin", headers: { "Content-Type": "application/json" }, body: JSON.stringify({ paymentMethod: form.paymentMethod }) });
      }
      onSaved?.();
    } catch (e) { setErr(e.message); } finally { setSaving(false); }
  };

  return (
    <ModalShell title={isExisting ? `Edit ${invoice.number}` : "New invoice"} onClose={onClose} status={invoice?.status} subtitle={invoice?.sourcePoNumber ? `From ${invoice.sourcePoNumber}` : null}>
      <Field label="Vendor">
        <select value={form.vendorChoice} onChange={(e) => setForm(f => ({ ...f, vendorChoice: e.target.value }))} disabled={isPaid} style={fieldInput}>
          <option value="">— pick a vendor —</option>
          {vendors.length > 0 && (
            <optgroup label="Purchasing vendors">
              {vendors.map(v => <option key={v.id} value={`vnd:${v.id}`}>{v.name}{v.email ? ` (${v.email})` : ""}</option>)}
            </optgroup>
          )}
          {greenVendors.length > 0 && (
            <optgroup label="Green vendors (from Lab)">
              {greenVendors.map(g => <option key={g.id} value={`src:${g.id}`}>{g.name}{g.email ? ` (${g.email})` : ""}</option>)}
            </optgroup>
          )}
        </select>
      </Field>
      <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 12 }}>
        <Field label="Issued"><input type="date" value={form.issueDate} onChange={(e) => setForm(f => ({ ...f, issueDate: e.target.value }))} disabled={isPaid} style={fieldInput} /></Field>
        <Field label="Due"><input type="date" value={form.dueDate} onChange={(e) => setForm(f => ({ ...f, dueDate: e.target.value }))} disabled={isPaid} style={fieldInput} /></Field>
      </div>
      <LineItemsEditor lineItems={form.lineItems} setLineItems={(li) => setForm(f => ({ ...f, lineItems: li }))} disabled={isPaid} />
      <div style={{ display: "grid", gridTemplateColumns: "1fr 110px", gap: 12, alignItems: "end" }}>
        <Field label="Notes"><textarea rows={3} value={form.notes} onChange={(e) => setForm(f => ({ ...f, notes: e.target.value }))} disabled={isPaid} style={{ ...fieldInput, resize: "vertical" }} /></Field>
        <Field label="Tax %"><input type="number" min="0" max="100" step="0.01" value={form.taxPct} onChange={(e) => setForm(f => ({ ...f, taxPct: parseFloat(e.target.value) || 0 }))} disabled={isPaid} style={fieldInput} /></Field>
      </div>
      <Field label="Payment method (optional)">
        <input type="text" placeholder="ACH / Check #1234 / Card" value={form.paymentMethod} onChange={(e) => setForm(f => ({ ...f, paymentMethod: e.target.value }))} style={fieldInput} />
      </Field>
      <Totals subtotal={totals.subtotal} taxAmt={totals.taxAmt} total={totals.total} taxPct={form.taxPct} />
      {err && <ErrBox>{err}</ErrBox>}
      <div style={{ display: "flex", gap: 8, justifyContent: "flex-end", marginTop: 16, flexWrap: "wrap" }}>
        <BtnGhost onClick={onClose}>Cancel</BtnGhost>
        {!isPaid && <BtnLight onClick={() => save()} disabled={saving}>{saving ? "Saving…" : isExisting ? "Save changes" : "Create invoice"}</BtnLight>}
        {isExisting && !isPaid && <BtnDark onClick={() => save("paid")} disabled={saving}><Check size={13} /> Mark paid</BtnDark>}
        {!isExisting && <BtnDark onClick={() => save("paid")} disabled={saving}><Check size={13} /> Create & mark paid</BtnDark>}
      </div>
    </ModalShell>
  );
}

/* ─────────────────── Vendor modal ─────────────────── */
function VendorModal({ vendor, onClose, onSaved }) {
  const [form, setForm] = useState({
    name: vendor?.name || "",
    email: vendor?.email || "",
    phone: vendor?.phone || "",
    address: vendor?.address || "",
    paymentTerms: vendor?.paymentTerms || "",
    notes: vendor?.notes || "",
  });
  const [saving, setSaving] = useState(false);
  const [err, setErr] = useState("");
  const isExisting = !!vendor;
  const save = async () => {
    setSaving(true); setErr("");
    try {
      const url = isExisting ? `/api/purchasing/vendors/${vendor.id}` : `/api/purchasing/vendors`;
      const method = isExisting ? "PATCH" : "POST";
      const r = await fetch(url, { method, credentials: "same-origin", headers: { "Content-Type": "application/json" }, body: JSON.stringify(form) });
      if (!r.ok) { const d = await r.json().catch(() => ({})); throw new Error(d.error || "Couldn't save"); }
      onSaved?.();
    } catch (e) { setErr(e.message); } finally { setSaving(false); }
  };
  return (
    <ModalShell title={isExisting ? `Edit ${vendor.name}` : "New vendor"} onClose={onClose}>
      <Field label="Name *"><input type="text" autoFocus value={form.name} onChange={(e) => setForm(f => ({ ...f, name: e.target.value }))} style={fieldInput} /></Field>
      <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 12 }}>
        <Field label="Email"><input type="email" value={form.email} onChange={(e) => setForm(f => ({ ...f, email: e.target.value }))} style={fieldInput} /></Field>
        <Field label="Phone"><input type="text" value={form.phone} onChange={(e) => setForm(f => ({ ...f, phone: e.target.value }))} style={fieldInput} /></Field>
      </div>
      <Field label="Address"><textarea rows={2} value={form.address} onChange={(e) => setForm(f => ({ ...f, address: e.target.value }))} style={{ ...fieldInput, resize: "vertical" }} /></Field>
      <Field label="Payment terms"><input type="text" placeholder="Net 30 / Net 15 / Net 0" value={form.paymentTerms} onChange={(e) => setForm(f => ({ ...f, paymentTerms: e.target.value }))} style={fieldInput} /></Field>
      <Field label="Notes"><textarea rows={2} value={form.notes} onChange={(e) => setForm(f => ({ ...f, notes: e.target.value }))} style={{ ...fieldInput, resize: "vertical" }} /></Field>
      {err && <ErrBox>{err}</ErrBox>}
      <div style={{ display: "flex", gap: 8, justifyContent: "flex-end", marginTop: 16 }}>
        <BtnGhost onClick={onClose}>Cancel</BtnGhost>
        <BtnDark onClick={save} disabled={saving || !form.name.trim()}>{saving ? "Saving…" : isExisting ? "Save" : "Create"}</BtnDark>
      </div>
    </ModalShell>
  );
}

/* ─────────────────── Shared bits ─────────────────── */
function emptyLine() {
  return { description: "", qty: 1, unit: "", unitPrice: 0 };
}
function computeTotals(lineItems, taxPct = 0) {
  const subtotal = (lineItems || []).reduce((s, l) => s + (Number(l.qty) || 0) * (Number(l.unitPrice) || 0), 0);
  const taxAmt = +(subtotal * (Number(taxPct) || 0) / 100).toFixed(2);
  return { subtotal: +subtotal.toFixed(2), taxAmt, total: +(subtotal + taxAmt).toFixed(2) };
}

function LineItemsEditor({ lineItems, setLineItems, disabled, approvedSamples = null, vendorName = "" }) {
  const update = (i, k, v) => setLineItems(lineItems.map((l, j) => j === i ? { ...l, [k]: v } : l));
  const addRow = () => setLineItems([...lineItems, emptyLine()]);
  const removeRow = (i) => setLineItems(lineItems.length === 1 ? [emptyLine()] : lineItems.filter((_, j) => j !== i));
  // POs constrain the description to approved cupping samples — pass
  // the list as `approvedSamples`. Invoices leave it null and keep
  // the free-text input.
  const useSamplePicker = Array.isArray(approvedSamples);
  return (
    <div style={{ marginBottom: 14 }}>
      <div style={{ fontSize: 12, fontWeight: 600, color: "#374151", marginBottom: 6 }}>Line items</div>
      {useSamplePicker && approvedSamples.length === 0 && !disabled && (
        <div style={{ marginBottom: 8, padding: "8px 12px", background: "#FFFBEB", border: "1px solid #FDE68A", borderRadius: 8, fontSize: 12, color: "#92400E", lineHeight: 1.5 }}>
          {vendorName
            ? <>No approved samples from <strong>{vendorName}</strong> yet — approve one in Cupping &amp; QC (Sourced from <strong>{vendorName}</strong>) before adding it here.</>
            : <>Pick a vendor first to see their approved samples.</>}
        </div>
      )}
      <div style={{ border: "1px solid #E5E7EB", borderRadius: 8, overflow: "hidden" }}>
        <div style={{ display: "grid", gridTemplateColumns: "1fr 80px 80px 110px 110px 36px", gap: 8, padding: "8px 10px", background: "#F9FAFB", fontSize: 11, fontWeight: 700, color: "#6B7280", textTransform: "uppercase", letterSpacing: 0.4 }}>
          <span>{useSamplePicker ? "Approved sample" : "Description"}</span><span style={{ textAlign: "right" }}>Qty</span><span>Unit</span><span style={{ textAlign: "right" }}>Unit price</span><span style={{ textAlign: "right" }}>Total</span><span></span>
        </div>
        {lineItems.map((l, i) => (
          <div key={i} style={{ display: "grid", gridTemplateColumns: "1fr 80px 80px 110px 110px 36px", gap: 8, padding: "8px 10px", borderTop: "1px solid #F3F4F6", alignItems: "center" }}>
            {useSamplePicker ? (
              <select
                value={l.sampleId || ""}
                onChange={(e) => {
                  const sample = approvedSamples.find(s => s.id === e.target.value);
                  if (!sample) { update(i, "sampleId", ""); update(i, "description", ""); return; }
                  setLineItems(lineItems.map((row, j) => j === i ? { ...row, sampleId: sample.id, description: sample.label } : row));
                }}
                disabled={disabled || approvedSamples.length === 0}
                style={lineInput}
              >
                <option value="">— pick an approved sample —</option>
                {approvedSamples.map(s => <option key={s.id} value={s.id}>{s.label}</option>)}
              </select>
            ) : (
              <input type="text" placeholder="e.g. Ethiopia Yirgacheffe — 60kg bag" value={l.description} onChange={(e) => update(i, "description", e.target.value)} disabled={disabled} style={lineInput} />
            )}
            <input type="number" min="0" step="any" value={l.qty} onChange={(e) => update(i, "qty", parseFloat(e.target.value) || 0)} disabled={disabled} style={{ ...lineInput, textAlign: "right", fontVariantNumeric: "tabular-nums" }} />
            <input type="text" placeholder="bag/lb/kg" value={l.unit} onChange={(e) => update(i, "unit", e.target.value)} disabled={disabled} style={lineInput} />
            <input type="number" min="0" step="0.01" value={l.unitPrice} onChange={(e) => update(i, "unitPrice", parseFloat(e.target.value) || 0)} disabled={disabled} style={{ ...lineInput, textAlign: "right", fontVariantNumeric: "tabular-nums" }} />
            <div style={{ textAlign: "right", fontSize: 13, fontWeight: 600, color: "#111", fontVariantNumeric: "tabular-nums" }}>{fmtMoney((Number(l.qty) || 0) * (Number(l.unitPrice) || 0))}</div>
            <button onClick={() => removeRow(i)} disabled={disabled} style={{ width: 28, height: 28, padding: 0, borderRadius: 6, border: "1px solid #FCA5A5", background: "#FEF2F2", color: "#B91C1C", cursor: disabled ? "not-allowed" : "pointer", display: "inline-flex", alignItems: "center", justifyContent: "center", opacity: disabled ? 0.4 : 1 }}><X size={12} /></button>
          </div>
        ))}
      </div>
      {!disabled && (!useSamplePicker || approvedSamples.length > 0) && (
        <button onClick={addRow} style={{ marginTop: 6, padding: "6px 10px", borderRadius: 7, border: "1px dashed #D1D5DB", background: "#fff", color: "#6B7280", fontSize: 13, fontWeight: 500, cursor: "pointer", fontFamily: "Inter, sans-serif", display: "inline-flex", alignItems: "center", gap: 4 }}>
          <Plus size={11} /> Add line
        </button>
      )}
    </div>
  );
}

function Totals({ subtotal, taxAmt, total, taxPct }) {
  return (
    <div style={{ marginTop: 12, padding: 12, background: "#FAFAFA", borderRadius: 8, fontSize: 13, fontVariantNumeric: "tabular-nums" }}>
      <Row label="Subtotal" value={fmtMoney(subtotal)} />
      {taxPct > 0 && <Row label={`Tax (${taxPct}%)`} value={fmtMoney(taxAmt)} />}
      <Row label="Total" value={fmtMoney(total)} bold />
    </div>
  );
}
function Row({ label, value, bold }) {
  return (
    <div style={{ display: "flex", justifyContent: "space-between", padding: "3px 0", fontWeight: bold ? 800 : 500, color: bold ? "#111" : "#374151", fontSize: bold ? 15 : 13 }}>
      <span>{label}</span><span>{value}</span>
    </div>
  );
}

function PageHeader({ title, subtitle, rightLabel, onRight }) {
  return (
    <div style={{ display: "flex", justifyContent: "space-between", alignItems: "flex-start", marginBottom: 18, gap: 16 }}>
      <div>
        <h1 style={{ fontSize: 22, fontWeight: 700, margin: 0, color: "#000", letterSpacing: "-0.01em" }}>{title}</h1>
        {subtitle && <p style={{ fontSize: 14, color: "#6B7280", margin: "4px 0 0", lineHeight: 1.5 }}>{subtitle}</p>}
      </div>
      {onRight && (
        <button onClick={onRight} style={{ padding: "8px 14px", borderRadius: 8, border: "1px solid #000", background: "#000", color: "#fff", fontSize: 14, fontWeight: 600, cursor: "pointer", fontFamily: "Inter, sans-serif", display: "inline-flex", alignItems: "center", gap: 6, flexShrink: 0 }}>
          <Plus size={13} strokeWidth={2} /> {rightLabel}
        </button>
      )}
    </div>
  );
}
function Tabs({ tabs, active, onChange }) {
  return (
    <div style={{ display: "flex", gap: 6, marginBottom: 16, flexWrap: "wrap" }}>
      {tabs.map(t => (
        <button key={t.v} onClick={() => onChange(t.v)} style={{
          padding: "7px 13px", borderRadius: 8,
          border: `1px solid ${active === t.v ? "#000" : "#E5E7EB"}`,
          background: active === t.v ? "#000" : "#fff",
          color: active === t.v ? "#fff" : "#374151",
          fontSize: 13, fontWeight: 600, cursor: "pointer", fontFamily: "Inter, sans-serif",
        }}>{t.label}{t.n != null && <span style={{ marginLeft: 6, fontSize: 11, fontWeight: 600, opacity: 0.7 }}>{t.n}</span>}</button>
      ))}
    </div>
  );
}
function Empty({ children }) {
  return <div style={{ padding: 32, textAlign: "center", background: "#FAFAFA", borderRadius: 12, color: "#9CA3AF", fontSize: 14 }}>{children}</div>;
}
function ErrBox({ children }) {
  return <div style={{ marginTop: 10, padding: "10px 14px", background: "#FEF2F2", border: "1px solid #FECACA", borderRadius: 8, color: "#991B1B", fontSize: 13 }}>{children}</div>;
}
function IconBtn({ children, label, onClick, danger, dark }) {
  return (
    <button onClick={onClick} title={label} aria-label={label} style={{
      width: 30, height: 30, padding: 0, borderRadius: 7,
      border: `1px solid ${danger ? "#FCA5A5" : dark ? "#000" : "#E5E7EB"}`,
      background: danger ? "#FEF2F2" : dark ? "#000" : "#fff",
      color: danger ? "#B91C1C" : dark ? "#fff" : "#374151",
      cursor: "pointer", fontFamily: "Inter, sans-serif",
      display: "inline-flex", alignItems: "center", justifyContent: "center",
    }}>{children}</button>
  );
}
function BtnDark({ children, ...rest }) {
  return <button {...rest} style={{ padding: "8px 14px", borderRadius: 8, border: "1px solid #000", background: "#000", color: "#fff", fontSize: 14, fontWeight: 600, cursor: "pointer", fontFamily: "Inter, sans-serif", display: "inline-flex", alignItems: "center", gap: 6 }}>{children}</button>;
}
function BtnLight({ children, ...rest }) {
  return <button {...rest} style={{ padding: "8px 14px", borderRadius: 8, border: "1px solid #E5E7EB", background: "#fff", color: "#374151", fontSize: 14, fontWeight: 600, cursor: "pointer", fontFamily: "Inter, sans-serif", display: "inline-flex", alignItems: "center", gap: 6 }}>{children}</button>;
}
function BtnGhost({ children, ...rest }) {
  return <button {...rest} style={{ padding: "8px 14px", borderRadius: 8, border: "1px solid transparent", background: "transparent", color: "#6B7280", fontSize: 14, fontWeight: 500, cursor: "pointer", fontFamily: "Inter, sans-serif" }}>{children}</button>;
}

function Field({ label, hint, children }) {
  return (
    <div style={{ marginBottom: 12 }}>
      <label style={{ display: "block", fontSize: 12, fontWeight: 600, color: "#374151", marginBottom: 6 }}>{label}</label>
      {children}
      {hint && <div style={{ fontSize: 11, color: "#9CA3AF", marginTop: 4 }}>{hint}</div>}
    </div>
  );
}
const fieldInput = {
  width: "100%", boxSizing: "border-box", padding: "9px 12px", borderRadius: 8,
  border: "1px solid #E5E7EB", background: "#fff", fontSize: 14, color: "#111",
  fontFamily: "Inter, sans-serif", outline: "none",
};
const lineInput = { ...fieldInput, padding: "6px 8px", fontSize: 13 };

function ModalShell({ title, subtitle, status, onClose, children }) {
  return (
    <div style={{ position: "fixed", inset: 0, background: "rgba(15,23,42,0.45)", zIndex: 100, display: "flex", alignItems: "flex-start", justifyContent: "center", padding: 32, overflowY: "auto" }} onClick={onClose}>
      <div onClick={(e) => e.stopPropagation()} style={{ background: "#fff", borderRadius: 14, width: "100%", maxWidth: 720, padding: 24, fontFamily: "Inter, sans-serif", boxShadow: "0 18px 48px rgba(0,0,0,0.18)" }}>
        <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", gap: 12, marginBottom: 16 }}>
          <div>
            <h2 style={{ margin: 0, fontSize: 18, fontWeight: 700, color: "#000", letterSpacing: "-0.01em", display: "flex", alignItems: "center", gap: 10 }}>
              {title}
              {status && <StatusPill status={status} />}
            </h2>
            {subtitle && <div style={{ fontSize: 12, color: "#6B7280", marginTop: 2 }}>{subtitle}</div>}
          </div>
          <button onClick={onClose} aria-label="Close" style={{ width: 32, height: 32, padding: 0, borderRadius: 8, border: "1px solid #E5E7EB", background: "#fff", color: "#6B7280", cursor: "pointer", display: "inline-flex", alignItems: "center", justifyContent: "center" }}><X size={14} /></button>
        </div>
        {children}
      </div>
    </div>
  );
}

/* ── Contracts ─────────────────────────────────────────────────────
   Multi-month green-coffee purchase agreements. Sit above POs in the
   pipeline: a contract reserves N bags from a vendor at a fixed
   price; POs draw down against the contract over time. Bags
   remaining is the source of truth for "how much can we still call". */
const APPROVAL_TINT = {
  pending:  { bg: "#FEF3C7", fg: "#92400E", border: "#FDE68A" },
  approved: { bg: "#ECFDF5", fg: "#047857", border: "#A7F3D0" },
  rejected: { bg: "#FEF2F2", fg: "#B91C1C", border: "#FECACA" },
};
function ApprovalPill({ status }) {
  const t = APPROVAL_TINT[status] || APPROVAL_TINT.pending;
  return (
    <span style={{
      display: "inline-block", padding: "3px 9px", borderRadius: 99,
      fontSize: 10, fontWeight: 700, letterSpacing: 0.4, textTransform: "uppercase",
      background: t.bg, color: t.fg, border: `1px solid ${t.border}`, fontFamily: "Inter, sans-serif",
    }}>{status || "pending"}</span>
  );
}
const fmtMonth = (s) => s ? new Date(s + "T00:00:00").toLocaleDateString("en-US", { month: "short", year: "numeric" }) : "—";

function ContractsView({ contracts, vendors, loading, onNew, onEdit }) {
  const vendorName = (id, snap) => {
    if (snap?.name) return snap.name;
    return (vendors.find(v => v.id === id) || {}).name || "—";
  };

  const cols = [
    { l: "Vendor",         w: 160 },
    { l: "Coffee",         w: 180 },
    { l: "Contract #",     w: 120 },
    { l: "ICO",            w: 110 },
    { l: "Est. Arrival",   w: 120 },
    { l: "Approval",       w: 110 },
    { l: "Bags Contracted", w: 110, align: "right" },
    { l: "Bags Remaining",  w: 110, align: "right" },
    { l: "Tariff",         w: 90,  align: "right" },
    { l: "Bag Size",       w: 90 },
    { l: "Intended Profile", w: 140 },
    { l: "Status",         w: 100 },
    { l: "First Month",    w: 110 },
    { l: "Last Month",     w: 110 },
    { l: "Release By",     w: 110 },
    { l: "Delivery By",    w: 110 },
    { l: "Contact",        w: 160 },
    { l: "Website / Login", w: 160 },
    { l: "Final Cost / lb", w: 120, align: "right" },
  ];
  const cellBase = { padding: "10px 12px", fontSize: 13, color: "#111827", whiteSpace: "nowrap", overflow: "hidden", textOverflow: "ellipsis", borderBottom: "1px solid #F3F4F6" };

  return (
    <div>
      <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: 16 }}>
        <h1 style={{ fontSize: 24, fontWeight: 700, color: "#000", margin: 0, letterSpacing: "-0.015em" }}>Contracts</h1>
        <button onClick={onNew} style={{ display: "inline-flex", alignItems: "center", gap: 6, padding: "8px 14px", borderRadius: 8, border: "none", background: "#000", color: "#fff", fontSize: 13, fontWeight: 600, cursor: "pointer", fontFamily: "Inter, sans-serif" }}>
          <Plus size={14} strokeWidth={2.2} /> New contract
        </button>
      </div>
      {loading ? (
        <div style={{ padding: 32, textAlign: "center", color: "#9CA3AF", fontSize: 14 }}>Loading…</div>
      ) : contracts.length === 0 ? (
        <div style={{ padding: 48, textAlign: "center", color: "#9CA3AF", fontSize: 14, background: "#FAFAFA", borderRadius: 12, border: "1px dashed #E5E7EB" }}>
          No contracts yet. Click <strong style={{ color: "#000" }}>New contract</strong> to record your first.
        </div>
      ) : (
        <div style={{ background: "#fff", border: "1px solid #E5E7EB", borderRadius: 12, overflow: "auto" }}>
          <table style={{ borderCollapse: "collapse", width: "100%", minWidth: cols.reduce((s, c) => s + c.w, 0) }}>
            <thead>
              <tr style={{ background: "#F9FAFB" }}>
                {cols.map(c => (
                  <th key={c.l} style={{ width: c.w, minWidth: c.w, padding: "10px 12px", fontSize: 10, fontWeight: 700, color: "#6B7280", textTransform: "uppercase", letterSpacing: "0.06em", textAlign: c.align || "left", borderBottom: "1px solid #E5E7EB", whiteSpace: "nowrap" }}>{c.l}</th>
                ))}
              </tr>
            </thead>
            <tbody>
              {contracts.map(c => {
                const remaining = c.bagsContracted != null
                  ? Math.max(0, Number(c.bagsContracted) - (Number(c.bagsReleased) || 0))
                  : null;
                return (
                  <tr key={c.id} onClick={() => onEdit(c)} style={{ cursor: "pointer" }}
                    onMouseEnter={(e) => e.currentTarget.style.background = "#FAFAFA"}
                    onMouseLeave={(e) => e.currentTarget.style.background = "transparent"}>
                    <td style={{ ...cellBase, fontWeight: 600 }}>{vendorName(c.vendorId, c.vendorSnapshot)}</td>
                    <td style={cellBase}>{c.coffee || "—"}</td>
                    <td style={{ ...cellBase, fontVariantNumeric: "tabular-nums" }}>{c.contractNumber || "—"}</td>
                    <td style={{ ...cellBase, fontVariantNumeric: "tabular-nums" }}>{c.ico || "—"}</td>
                    <td style={cellBase}>{fmtDate(c.estimatedArrivalDate)}</td>
                    <td style={cellBase}><ApprovalPill status={c.approvalStatus} /></td>
                    <td style={{ ...cellBase, textAlign: "right", fontVariantNumeric: "tabular-nums" }}>{c.bagsContracted ?? "—"}</td>
                    <td style={{ ...cellBase, textAlign: "right", fontVariantNumeric: "tabular-nums", fontWeight: 600 }}>{remaining ?? "—"}</td>
                    <td style={{ ...cellBase, textAlign: "right", fontVariantNumeric: "tabular-nums" }}>{c.tariff != null ? `${c.tariff}%` : "—"}</td>
                    <td style={cellBase}>{c.bagSize || "—"}</td>
                    <td style={cellBase}>{c.intendedProfile || "—"}</td>
                    <td style={cellBase}><StatusPill status={c.status || "active"} /></td>
                    <td style={cellBase}>{fmtMonth(c.firstMonth)}</td>
                    <td style={cellBase}>{fmtMonth(c.lastMonth)}</td>
                    <td style={cellBase}>{fmtDate(c.releaseBy)}</td>
                    <td style={cellBase}>{fmtDate(c.deliveryBy)}</td>
                    <td style={cellBase}>{c.contact || "—"}</td>
                    <td style={cellBase}>{c.website ? (
                      <a href={c.website.startsWith("http") ? c.website : `https://${c.website}`} target="_blank" rel="noreferrer" onClick={e => e.stopPropagation()} style={{ color: "#1D4ED8", textDecoration: "none" }}>{c.website}</a>
                    ) : "—"}</td>
                    <td style={{ ...cellBase, textAlign: "right", fontVariantNumeric: "tabular-nums", fontWeight: 600 }}>{c.finalCostPrice != null ? fmtMoney(c.finalCostPrice) : "—"}</td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
}

function ContractModal({ contract, vendors, greenVendors = [], onClose, onSaved }) {
  const isNew = !contract;
  const [form, setForm] = useState(() => ({
    vendorId: contract?.vendorId || "",
    coffee: contract?.coffee || "",
    contractNumber: contract?.contractNumber || "",
    ico: contract?.ico || "",
    estimatedArrivalDate: contract?.estimatedArrivalDate || "",
    approvalStatus: contract?.approvalStatus || "pending",
    bagsContracted: contract?.bagsContracted ?? "",
    bagsReleased: contract?.bagsReleased ?? "",
    tariff: contract?.tariff ?? "",
    bagSize: contract?.bagSize || "",
    intendedProfile: contract?.intendedProfile || "",
    status: contract?.status || "active",
    firstMonth: contract?.firstMonth || "",
    lastMonth: contract?.lastMonth || "",
    releaseBy: contract?.releaseBy || "",
    deliveryBy: contract?.deliveryBy || "",
    contact: contract?.contact || "",
    website: contract?.website || "",
    finalCostPrice: contract?.finalCostPrice ?? "",
    notes: contract?.notes || "",
  }));
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState("");
  const set = (k, v) => setForm(prev => ({ ...prev, [k]: v }));

  const submit = async () => {
    setError("");
    setSaving(true);
    try {
      const url = isNew ? "/api/purchasing/contracts" : `/api/purchasing/contracts/${contract.id}`;
      const method = isNew ? "POST" : "PATCH";
      const r = await fetch(url, {
        method,
        headers: { "Content-Type": "application/json" },
        credentials: "same-origin",
        body: JSON.stringify(form),
      });
      const d = await r.json().catch(() => ({}));
      if (!r.ok) throw new Error(d.error || "Save failed");
      onSaved();
    } catch (e) { setError(e.message); }
    finally { setSaving(false); }
  };

  const remove = async () => {
    if (!confirm("Delete this contract? This can't be undone.")) return;
    setSaving(true);
    try {
      const r = await fetch(`/api/purchasing/contracts/${contract.id}`, { method: "DELETE", credentials: "same-origin" });
      if (!r.ok) throw new Error("Delete failed");
      onSaved();
    } catch (e) { setError(e.message); }
    finally { setSaving(false); }
  };

  const inputCss = { padding: "9px 11px", borderRadius: 8, border: "1px solid #E5E7EB", fontSize: 13, fontFamily: "Inter, sans-serif", background: "#fff", outline: "none", width: "100%", boxSizing: "border-box" };
  const labelCss = { display: "block", fontSize: 11, fontWeight: 600, color: "#6B7280", textTransform: "uppercase", letterSpacing: "0.06em", marginBottom: 4 };
  const Field = ({ label, children }) => (
    <div><label style={labelCss}>{label}</label>{children}</div>
  );

  return (
    <ModalShell title={isNew ? "New contract" : `Contract ${contract.contractNumber || ""}`} subtitle="Multi-month green-coffee purchase agreement" onClose={onClose}>
      {error && <div style={{ marginBottom: 12, padding: "8px 12px", background: "#FEF2F2", border: "1px solid #FECACA", borderRadius: 8, color: "#991B1B", fontSize: 13 }}>{error}</div>}

      <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr 1fr", gap: 12, marginBottom: 12 }}>
        <Field label="Vendor *">
          <select value={form.vendorId} onChange={e => set("vendorId", e.target.value)} style={{ ...inputCss, appearance: "auto" }}>
            <option value="">— select —</option>
            {vendors.length > 0 && (
              <optgroup label="Purchasing vendors">
                {vendors.map(v => <option key={v.id} value={v.id}>{v.name}</option>)}
              </optgroup>
            )}
            {greenVendors.length > 0 && (
              <optgroup label="Green vendors (from Lab)">
                {greenVendors.map(g => <option key={`gv-${g.id || g.name}`} value="">{g.name || "(unnamed)"}</option>)}
              </optgroup>
            )}
          </select>
        </Field>
        <Field label="Coffee">
          <input value={form.coffee} onChange={e => set("coffee", e.target.value)} placeholder="Yirgacheffe G1" style={inputCss} />
        </Field>
        <Field label="Contract #">
          <input value={form.contractNumber} onChange={e => set("contractNumber", e.target.value)} placeholder="ALL-2026-0042" style={inputCss} />
        </Field>
        <Field label="ICO">
          <input value={form.ico} onChange={e => set("ico", e.target.value)} placeholder="074-0123-4567" style={inputCss} />
        </Field>
        <Field label="Estimated arrival">
          <input type="date" value={form.estimatedArrivalDate} onChange={e => set("estimatedArrivalDate", e.target.value)} style={inputCss} />
        </Field>
        <Field label="Approval status">
          <select value={form.approvalStatus} onChange={e => set("approvalStatus", e.target.value)} style={{ ...inputCss, appearance: "auto" }}>
            <option value="pending">Pending</option>
            <option value="approved">Approved</option>
            <option value="rejected">Rejected</option>
          </select>
        </Field>
        <Field label="Bags contracted">
          <input type="number" min="0" step="1" value={form.bagsContracted} onChange={e => set("bagsContracted", e.target.value)} style={inputCss} />
        </Field>
        <Field label="Bags released (drawn down)">
          <input type="number" min="0" step="1" value={form.bagsReleased} onChange={e => set("bagsReleased", e.target.value)} style={inputCss} />
        </Field>
        <Field label="Tariff (%)">
          <input type="number" min="0" step="0.01" value={form.tariff} onChange={e => set("tariff", e.target.value)} style={inputCss} />
        </Field>
        <Field label="Bag size">
          <input value={form.bagSize} onChange={e => set("bagSize", e.target.value)} placeholder="60 kg" style={inputCss} />
        </Field>
        <Field label="Intended for profile">
          <input value={form.intendedProfile} onChange={e => set("intendedProfile", e.target.value)} placeholder="House Blend" style={inputCss} />
        </Field>
        <Field label="Status">
          <select value={form.status} onChange={e => set("status", e.target.value)} style={{ ...inputCss, appearance: "auto" }}>
            <option value="active">Active</option>
            <option value="closed">Closed</option>
            <option value="cancelled">Cancelled</option>
          </select>
        </Field>
        <Field label="First month">
          <input type="month" value={form.firstMonth} onChange={e => set("firstMonth", e.target.value)} style={inputCss} />
        </Field>
        <Field label="Last month">
          <input type="month" value={form.lastMonth} onChange={e => set("lastMonth", e.target.value)} style={inputCss} />
        </Field>
        <Field label="Release by">
          <input type="date" value={form.releaseBy} onChange={e => set("releaseBy", e.target.value)} style={inputCss} />
        </Field>
        <Field label="Delivery by">
          <input type="date" value={form.deliveryBy} onChange={e => set("deliveryBy", e.target.value)} style={inputCss} />
        </Field>
        <Field label="Contact">
          <input value={form.contact} onChange={e => set("contact", e.target.value)} placeholder="Name + email/phone" style={inputCss} />
        </Field>
        <Field label="Website / Login">
          <input value={form.website} onChange={e => set("website", e.target.value)} placeholder="vendor.com" style={inputCss} />
        </Field>
        <Field label="Final cost / lb ($)">
          <input type="number" min="0" step="0.01" value={form.finalCostPrice} onChange={e => set("finalCostPrice", e.target.value)} placeholder="6.85" style={inputCss} />
        </Field>
      </div>

      <Field label="Notes">
        <textarea value={form.notes} onChange={e => set("notes", e.target.value)} rows={3} style={{ ...inputCss, resize: "vertical", minHeight: 70, fontSize: 13 }} />
      </Field>

      <div style={{ display: "flex", justifyContent: "space-between", gap: 8, marginTop: 16 }}>
        <div>
          {!isNew && (
            <button onClick={remove} disabled={saving} style={{ padding: "9px 14px", borderRadius: 8, border: "1px solid #FECACA", background: "#fff", color: "#B91C1C", fontSize: 13, fontWeight: 600, cursor: saving ? "default" : "pointer", fontFamily: "Inter, sans-serif", display: "inline-flex", alignItems: "center", gap: 6 }}>
              <Trash2 size={13} strokeWidth={2} /> Delete
            </button>
          )}
        </div>
        <div style={{ display: "flex", gap: 8 }}>
          <button onClick={onClose} disabled={saving} style={{ padding: "9px 14px", borderRadius: 8, border: "1px solid #E5E7EB", background: "#fff", color: "#000", fontSize: 13, fontWeight: 500, cursor: "pointer", fontFamily: "Inter, sans-serif" }}>Cancel</button>
          <button onClick={submit} disabled={saving || !form.vendorId} style={{ padding: "9px 18px", borderRadius: 8, border: "none", background: (saving || !form.vendorId) ? "#D1D5DB" : "#000", color: "#fff", fontSize: 13, fontWeight: 600, cursor: (saving || !form.vendorId) ? "default" : "pointer", fontFamily: "Inter, sans-serif" }}>
            {saving ? "Saving…" : (isNew ? "Create contract" : "Save changes")}
          </button>
        </div>
      </div>
    </ModalShell>
  );
}


/* ── Due invoices ──────────────────────────────────────────────────
   Vendor invoices we owe, grouped by due/overdue/upcoming. The dueDate
   on each invoice is computed at create time from the issueDate + the
   vendor or workspace default Net X terms; this view just renders. */
function DueInvoicesView({ invoices, loading, onEdit }) {
  if (loading) return <div style={{ padding: 32, textAlign: "center", color: "#9CA3AF" }}>Loading…</div>;
  const today = new Date().toISOString().slice(0, 10);
  const sevenOut = new Date(); sevenOut.setDate(sevenOut.getDate() + 7);
  const sevenStr = sevenOut.toISOString().slice(0, 10);
  const open = (invoices || []).filter(i => i.status !== "paid" && i.status !== "cancelled");
  const overdue = open.filter(i => i.dueDate && i.dueDate < today)
    .sort((a, b) => (a.dueDate || "").localeCompare(b.dueDate || ""));
  const dueSoon = open.filter(i => i.dueDate && i.dueDate >= today && i.dueDate <= sevenStr)
    .sort((a, b) => (a.dueDate || "").localeCompare(b.dueDate || ""));
  const upcoming = open.filter(i => !i.dueDate || i.dueDate > sevenStr)
    .sort((a, b) => (a.dueDate || "").localeCompare(b.dueDate || ""));
  const total = (rows) => rows.reduce((s, i) => s + (Number(i.total) || 0), 0);
  const Section = ({ title, rows, accent }) => rows.length === 0 ? null : (
    <div style={{ marginBottom: 22 }}>
      <div style={{ display: "flex", justifyContent: "space-between", alignItems: "baseline", marginBottom: 8 }}>
        <span style={{ fontSize: 11, fontWeight: 700, color: accent, letterSpacing: "0.06em", textTransform: "uppercase" }}>{title} · {rows.length}</span>
        <span style={{ fontSize: 13, color: "#000", fontWeight: 700, fontVariantNumeric: "tabular-nums" }}>{fmtMoney(total(rows))}</span>
      </div>
      <div style={{ background: "#fff", border: "1px solid #E5E7EB", borderRadius: 10, overflow: "hidden" }}>
        {rows.map(inv => {
          const days = inv.dueDate ? Math.round((new Date(inv.dueDate) - new Date(today)) / 86400000) : null;
          return (
            <div key={inv.id} onClick={() => onEdit && onEdit(inv)}
              onMouseEnter={(e) => e.currentTarget.style.background = "#FAFAFA"}
              onMouseLeave={(e) => e.currentTarget.style.background = "transparent"}
              style={{ display: "grid", gridTemplateColumns: "120px 1fr 110px 110px 110px", alignItems: "center", padding: "12px 16px", borderTop: "1px solid #F3F4F6", cursor: "pointer", fontFamily: "Inter, sans-serif" }}>
              <span style={{ fontSize: 13, fontWeight: 700, color: "#000", fontVariantNumeric: "tabular-nums" }}>{inv.number}</span>
              <span style={{ fontSize: 14, color: "#000", overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>{inv.vendorSnapshot?.name || "—"}</span>
              <span style={{ fontSize: 13, color: "#6B7280" }}>{fmtDate(inv.dueDate)}</span>
              <span style={{ fontSize: 12, color: days != null && days < 0 ? "#B91C1C" : days != null && days <= 7 ? "#CC785C" : "#6B7280", fontWeight: 600 }}>
                {days == null ? "—" : days < 0 ? `${Math.abs(days)}d overdue` : days === 0 ? "today" : `${days}d`}
              </span>
              <span style={{ fontSize: 13, fontWeight: 700, color: "#000", textAlign: "right", fontVariantNumeric: "tabular-nums" }}>{fmtMoney(inv.total)}</span>
            </div>
          );
        })}
      </div>
    </div>
  );
  return (
    <div>
      <div style={{ display: "flex", justifyContent: "space-between", alignItems: "baseline", marginBottom: 14 }}>
        <h1 style={{ fontSize: 22, fontWeight: 700, color: "#000", margin: 0, letterSpacing: "-0.015em" }}>Due invoices</h1>
        <div style={{ fontSize: 13, color: "#3F3F46", fontVariantNumeric: "tabular-nums" }}>
          <span style={{ color: "#B91C1C", marginRight: 14 }}>{fmtMoney(total(overdue))} overdue</span>
          <span style={{ color: "#CC785C", marginRight: 14 }}>{fmtMoney(total(dueSoon))} due soon</span>
          <span style={{ color: "#6B7280" }}>{fmtMoney(total(upcoming))} upcoming</span>
        </div>
      </div>
      {open.length === 0 ? (
        <div style={{ background: "#FAFAFA", borderRadius: 12, padding: 48, textAlign: "center", color: "#9CA3AF", fontSize: 14, border: "1px dashed #E5E7EB" }}>
          No open invoices. Vendor invoices appear here as soon as POs convert into bills.
        </div>
      ) : (
        <>
          <Section title="Overdue"  rows={overdue}  accent="#B91C1C" />
          <Section title="Due soon" rows={dueSoon}  accent="#CC785C" />
          <Section title="Upcoming" rows={upcoming} accent="#6B7280" />
        </>
      )}
    </div>
  );
}

/* ── Customers (clients) ───────────────────────────────────────────
   Workspace-local roster of the people you sell to. One-click import
   pulls every customer from the connected Shopify store. Each row
   shows the running order count + total due + total overdue computed
   from billing invoices. */
function ClientsView({ clients, loading, defaults, refresh, patchClientLocal, newSignal }) {
  const [importing, setImporting] = useState(false);
  const [importMsg, setImportMsg] = useState("");
  const [savingDefaults, setSavingDefaults] = useState(false);
  const [editingDefaults, setEditingDefaults] = useState(false);
  const [defForm, setDefForm] = useState(defaults);
  const [importErr, setImportErr] = useState(null);
  // Customers with zero orders (and zero shopify-unpaid count) collapse
  // into a footer bar by default — Progeny has hundreds of imported
  // contacts who never ordered, and they were drowning the actually-
  // active accounts. Click the bar to expand.
  const [showZeroOrder, setShowZeroOrder] = useState(false);
  const [showArchived, setShowArchived] = useState(false);
  // Multi-token search across name / company / email / altEmails /
  // unpaid order numbers. Comma-separated; a row matches if ANY token
  // hits its corpus, mirroring the customer-detail order search.
  const [clientSearch, setClientSearch] = useState("");
  // Quick-send-statement state. Clicking the per-row Send button
  // fetches that customer's orders and pops the EmailStatementModal
  // without leaving the list. `loadingId` shows the spinner state on
  // the row that's currently fetching.
  const [statementCtx, setStatementCtx] = useState(null); // { client, orders, totals }
  const [statementLoadingId, setStatementLoadingId] = useState(null);
  // Manual "create customer" modal — separate from the Shopify import.
  // Opened by the "+ New" button, the sub-nav CTA, OR a parent signal.
  const [creatingNew, setCreatingNew] = useState(false);

  // Fetch the customer's orders and open the statement modal without
  // navigating away from the list. Stops the row-click so we don't
  // also drill into the customer detail.
  const openStatementFromList = async (e, c) => {
    e.stopPropagation();
    if (statementLoadingId) return;
    setStatementLoadingId(c.id);
    try {
      const r = await fetch(`/api/clients/${encodeURIComponent(c.id)}/orders`, { credentials: "same-origin" });
      if (!r.ok) return;
      const d = await r.json();
      const unpaid = (d.orders || []).filter(o => o.unpaid).map(o => ({
        number: o.number, date: o.date, total: o.total, outstanding: o.outstanding,
        adminUrl: o.adminUrl, orderStatusUrl: o.orderStatusUrl,
        fulfillmentStatus: o.fulfillmentStatus, fulfilledAt: o.fulfilledAt,
        rawId: o.rawId, id: o.id, overdue: !!o.overdue,
        lineItems: Array.isArray(o.lineItems) ? o.lineItems : [],
      }));
      setStatementCtx({
        client: d.client || c,
        orders: unpaid,
        totals: d.totals || { unpaid: 0, overdue: 0 },
      });
    } finally {
      setStatementLoadingId(null);
    }
  };

  // Toggle archive state. Optimistic — flips the row immediately by
  // patching local state; the PATCH fires in the background with no
  // loading spinner. If the server rejects we silently fall back to a
  // full refresh to reconcile.
  const toggleArchived = async (e, c) => {
    e.stopPropagation();
    const nextArchivedAt = c.archivedAt ? null : new Date().toISOString();
    if (patchClientLocal) patchClientLocal(c.id, { archivedAt: nextArchivedAt });
    try {
      const r = await fetch(`/api/clients/${encodeURIComponent(c.id)}`, {
        method: "PATCH", credentials: "same-origin",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ archivedAt: nextArchivedAt }),
      });
      if (!r.ok) throw new Error("PATCH failed");
    } catch {
      // Roll back via authoritative refresh.
      refresh && refresh();
    }
  };
  // Column sort state — { key, dir }. Click a header to set;
  // click again to flip direction.
  const [sort, setSort] = usePersistentSort("clients-sort", { key: "name", dir: "asc" });
  // Drilled-in customer for the detail view.
  const [openClientId, setOpenClientId] = useState(null);
  // Headline-tile filter. "all" = show every customer; "orders" =
  // only customers with unpaid orders; "due" = only with totalDue
  // > 0; "overdue" = only with totalOverdue > 0. Click a tile to
  // toggle.
  const [tileFilter, setTileFilter] = useState("all");
  React.useEffect(() => { setDefForm(defaults); }, [defaults]);

  // Sub-nav CTA bridge — when the parent signals "client-import",
  // run the same Shopify import flow the page-header button used to.
  // "client-new" opens the manual create-customer modal.
  React.useEffect(() => {
    if (!newSignal) return;
    if (newSignal.kind === "client-import") importFromShopify();
    if (newSignal.kind === "client-new")    setCreatingNew(true);
  }, [newSignal]); // eslint-disable-line react-hooks/exhaustive-deps

  const importFromShopify = async () => {
    setImporting(true); setImportMsg(""); setImportErr(null);
    try {
      const r = await fetch("/api/shopify/customers", { credentials: "same-origin" });
      if (!r.ok) {
        const raw = await r.text();
        let parsed;
        try { parsed = JSON.parse(raw); } catch { parsed = null; }
        throw new Error(parsed?.error || "Couldn't fetch from Shopify (is the store connected?)");
      }
      const { customers, source, mode, ordersScanned } = await r.json();
      const post = await fetch("/api/clients/import-shopify", {
        method: "POST",
        credentials: "same-origin",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ customers }),
      });
      const result = await post.json();
      if (!post.ok) throw new Error(result.error || "Import failed");
      const fromOrders = source === "orders";
      const unpaidOnly = mode === "unpaid_only";
      setImportMsg(
        `Imported ${result.created} new, updated ${result.updated} existing.` +
        (fromOrders
          ? unpaidOnly
            ? ` Pulled every customer with a non-cancelled, unpaid order in your store (${ordersScanned || 0} unpaid orders scanned). Click any customer to see and mark each one paid.`
            : ` Pulled from order history (${ordersScanned || 0} scanned).`
          : "")
      );
      refresh && refresh();
    } catch (e) {
      setImportErr({ kind: "generic", message: e.message });
    } finally { setImporting(false); }
  };
  const saveDefaults = async () => {
    setSavingDefaults(true);
    try {
      const r = await fetch("/api/settings/payment-defaults", {
        method: "PUT",
        credentials: "same-origin",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify(defForm),
      });
      if (r.ok) {
        const d = await r.json();
        setDefForm(d.defaults);
        setEditingDefaults(false);
        refresh && refresh();
      }
    } finally { setSavingDefaults(false); }
  };

  // Hooks must run in the same order on every render — keep all
  // useMemo / useEffect calls ABOVE any conditional returns.
  const totals = useMemo(() => clients.reduce((acc, c) => {
    if (c.archivedAt) return acc;
    return {
      orders: acc.orders + (c.orderCount || 0),
      due: acc.due + (Number(c.totalDue) || 0),
      overdue: acc.overdue + (Number(c.totalOverdue) || 0),
    };
  }, { orders: 0, due: 0, overdue: 0 }), [clients]);

  // Build 12-month series across every customer's unpaid orders.
  // Buckets are { customers, orders, due, overdue } per month for
  // the trailing 12 months. We use it to drive the sparkline + the
  // trend arrow on each stat tile.
  const series = useMemo(() => {
    const now = new Date();
    const months = [];
    for (let i = 11; i >= 0; i--) {
      const d = new Date(now.getFullYear(), now.getMonth() - i, 1);
      months.push({
        key: `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, "0")}`,
        label: d.toLocaleString("en-US", { month: "short" }),
        startMs: d.getTime(),
        endMs: new Date(d.getFullYear(), d.getMonth() + 1, 1).getTime(),
        customers: 0, orders: 0, due: 0, overdue: 0,
      });
    }
    const monthIdxOf = (ms) => {
      const d = new Date(ms);
      const k = `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, "0")}`;
      return months.findIndex(m => m.key === k);
    };
    const termDays = (s) => {
      const m = String(s || "").match(/(\d+)/);
      return m ? parseInt(m[1], 10) : 30;
    };
    const defaultDays = termDays(defaults.paymentTerms);
    const todayMs = now.getTime();
    for (const c of clients) {
      if (c.archivedAt) continue;
      // Customers per month — bucket by createdAt of the local
      // client record (when we first saw them). Stays as a flow
      // metric (new customers/month); the sparkline reads as a
      // growth chart, which is the useful framing.
      if (c.createdAt) {
        const idx = monthIdxOf(c.createdAt);
        if (idx >= 0) months[idx].customers += 1;
      }
      // Unpaid orders / due / overdue as a FLOW: each still-unpaid
      // order lands in the bucket of the month it was PLACED. The
      // sparkline then reads as the age profile of what's currently
      // outstanding (recent vs. stale receivables) instead of a
      // cumulative line that's flat across the window because most
      // unpaid orders pre-date it. Orders older than the 12-month
      // window roll into the oldest visible bucket so nothing is lost.
      const days = termDays(c.paymentTerms || defaults.paymentTerms);
      for (const o of (c.shopifyUnpaidOrders || [])) {
        if (!o.createdAt) continue;
        const orderMs = new Date(o.createdAt).getTime();
        if (!Number.isFinite(orderMs)) continue;
        const out = Number(o.outstanding) || 0;
        const dueAtMs = orderMs + days * 86400000;
        let idx = monthIdxOf(orderMs);
        if (idx < 0) idx = orderMs < months[0].startMs ? 0 : -1; // pre-window → oldest bucket
        if (idx < 0) continue;
        const m = months[idx];
        m.orders += 1;
        m.due    += out;
        if (dueAtMs < todayMs) m.overdue += out;
      }
    }
    return months;
  }, [clients, defaults.paymentTerms]);

  // Per-tile delta. For stock metrics (orders/due/overdue) the latest
  // point is the current month-to-date snapshot — meaningful as-is.
  // For flow metrics (customers) the current month is partial and
  // would read as a crash, so we use the last FULL month instead.
  // Compare the latest against the average of the three months before.
  const trendFor = (key) => {
    const len = series.length;
    if (len < 5) return null;
    const isFlow = key === "customers";
    const latestIdx = isFlow ? len - 2 : len - 1;
    const latest = series[latestIdx][key];
    const priorMonths = series.slice(Math.max(0, latestIdx - 3), latestIdx);
    const priorAvg = priorMonths.reduce((s, m) => s + m[key], 0) / Math.max(1, priorMonths.length);
    if (priorAvg === 0 && latest === 0) return null;
    if (priorAvg === 0) return { dir: "up", pct: 100, latest, prior: 0 };
    const pct = Math.round(((latest - priorAvg) / priorAvg) * 100);
    return { dir: pct >= 0 ? "up" : "down", pct: Math.abs(pct), latest, prior: priorAvg };
  };

  // Apply the headline-tile filter (Total orders / Total due /
  // Overdue narrows the list to rows that contributed to that
  // tile's number).
  const filteredByTile = useMemo(() => {
    switch (tileFilter) {
      case "orders":  return clients.filter(c => (Number(c.shopifyUnpaidOrdersCount) || 0) > 0 || (Number(c.orderCount) || 0) > 0);
      case "due":     return clients.filter(c => (Number(c.totalDue) || 0) > 0);
      case "overdue": return clients.filter(c => (Number(c.totalOverdue) || 0) > 0);
      default:        return clients;
    }
  }, [clients, tileFilter]);

  // Comma-separated multi-token search. A token matches if it appears
  // anywhere in the row's corpus (name, company, primary email, alt
  // emails, unpaid order numbers). A row is included if ANY token
  // matches — so "1042, alice" returns rows for either order #1042 or
  // anyone named alice. Tokens are normalized to lowercase and have
  // leading "#" stripped so order numbers can be searched as either
  // "1042" or "#1042".
  // Split on commas OR whitespace — operators paste lists from
  // emails / spreadsheets that come in either form.
  const searchTokens = useMemo(() => (
    (clientSearch || "")
      .split(/[,\s]+/)
      .map(t => t.trim().toLowerCase().replace(/^#/, ""))
      .filter(Boolean)
  ), [clientSearch]);

  // "Order-search mode": every token looks like an order number
  // (digits, optionally with a -SUFFIX like "-WS"). When true, we pivot
  // the view from a customer list to a flat list of matching orders so
  // the operator can mark each one paid without drilling into clients.
  // Triggers for 1+ tokens — searching for a single order number
  // should jump straight to the order, not show the customer.
  const orderTokenRx = /^[0-9]{2,}(-[a-z0-9]+)?$/i;
  const isOrderSearch = searchTokens.length >= 1 && searchTokens.every(t => orderTokenRx.test(t));

  // Flat order results — only computed in order-search mode. Pulls
  // from every client's persisted unpaid_orders snapshot. Each row
  // carries enough to drive mark-paid (client.id + order id) and to
  // render a useful row (number, customer name, totals, status flags).
  const orderResults = useMemo(() => {
    if (!isOrderSearch) return [];
    const tokenSet = new Set(searchTokens);
    const out = [];
    for (const c of clients) {
      const orders = Array.isArray(c.shopifyUnpaidOrders) ? c.shopifyUnpaidOrders : [];
      for (const o of orders) {
        const num = String(o?.number || "").toLowerCase().replace(/^#/, "");
        if (!num) continue;
        const matches = tokenSet.has(num) || [...tokenSet].some(t => num === t || num.includes(t));
        if (!matches) continue;
        out.push({
          orderId: o.id ? `shopify:${o.id}` : null,
          rawId: o.id || null,
          number: o.number,
          total: Number(o.total) || Number(o.outstanding) || 0,
          outstanding: Number(o.outstanding) || 0,
          fulfillmentStatus: o.fulfillmentStatus || null,
          fulfilledAt: o.fulfilledAt || null,
          createdAt: o.createdAt || null,
          adminUrl: o.adminUrl || null,
          clientId: c.id,
          clientName: c.name || c.company || c.email || "(unnamed)",
          clientEmail: c.email || null,
        });
      }
    }
    // Surface the unfound tokens so we can show a "12 of 25 matched" hint.
    const foundNums = new Set(out.map(r => String(r.number || "").toLowerCase().replace(/^#/, "")));
    const missing = searchTokens.filter(t => !foundNums.has(t));
    return Object.assign(out, { missing });
  }, [isOrderSearch, searchTokens, clients]);

  const filteredClients = useMemo(() => {
    if (searchTokens.length === 0) return filteredByTile;
    // Order-search mode renders its own view; the customer list filter
    // doesn't apply.
    if (isOrderSearch) return filteredByTile;
    return filteredByTile.filter(c => {
      const corpus = [];
      if (c.name) corpus.push(String(c.name).toLowerCase());
      if (c.company) corpus.push(String(c.company).toLowerCase());
      if (c.email) corpus.push(String(c.email).toLowerCase());
      if (Array.isArray(c.altEmails)) for (const a of c.altEmails) corpus.push(String(a).toLowerCase());
      if (Array.isArray(c.shopifyUnpaidOrders)) {
        for (const o of c.shopifyUnpaidOrders) {
          if (o?.number) corpus.push(String(o.number).toLowerCase().replace(/^#/, ""));
        }
      }
      return searchTokens.some(t => corpus.some(s => s.includes(t)));
    });
  }, [filteredByTile, searchTokens, isOrderSearch]);

  // Sortable list. Memoised so the column-click doesn't re-sort
  // 500+ rows on every unrelated re-render.
  const sorted = useMemo(() => {
    const sortKey = sort.key;
    const dir = sort.dir === "asc" ? 1 : -1;
    const get = (c) => {
      switch (sortKey) {
        case "name":     return (c.name || "").toLowerCase();
        case "contact":  return (c.email || "").toLowerCase();
        case "orders":   return Number(c.orderCount) || 0;
        case "lastOrder": return c.shopifyLastOrderAt ? new Date(c.shopifyLastOrderAt).getTime() : 0;
        case "due":      return Number(c.totalDue) || 0;
        case "overdue":  return Number(c.totalOverdue) || 0;
        case "terms":    return (c.paymentTerms || defaults.paymentTerms || "").toLowerCase();
        case "method":   return (c.paymentMethod || defaults.paymentMethod || "").toLowerCase();
        default: return 0;
      }
    };
    // Decorate-sort-undecorate so we only compute each row's key
    // once instead of (n log n) × 2 times inside the comparator.
    const decorated = filteredClients.map((c, i) => ({ c, k: get(c), i }));
    decorated.sort((a, b) => {
      if (a.k === b.k) return a.i - b.i;
      if (a.k === "" || a.k == null) return 1;
      if (b.k === "" || b.k == null) return -1;
      return (a.k < b.k ? -1 : 1) * dir;
    });
    return decorated.map(d => d.c);
  }, [filteredClients, sort, defaults.paymentTerms, defaults.paymentMethod]);

  // ── Conditional renders ONLY below this line ──
  if (loading) return <div style={{ padding: 32, textAlign: "center", color: "#9CA3AF" }}>Loading…</div>;

  // Drill-in: render the per-customer detail view instead of the
  // table when a row is opened.
  if (openClientId) {
    const c = clients.find(x => x.id === openClientId);
    if (!c) { setOpenClientId(null); }
    else return <ClientDetailView client={c} onBack={() => setOpenClientId(null)} onUpdated={refresh} defaults={defaults} patchClientLocal={patchClientLocal} />;
  }
  // Numeric/money columns sort highest-first on first click;
  // text columns stay ascending. Click a second time to flip.
  // (NUMERIC_KEYS used by both the customers list AND the customer
  // detail orders list — defined as a module constant, see top of file.)

  const NET_TERMS = ["Net 0", "Net 15", "Net 30", "Net 45", "Net 60", "Net 90"];
  const METHODS = ["ACH", "Card", "Check", "Wire"];

  return (
    <div>
      <div style={{ display: "flex", justifyContent: "space-between", alignItems: "baseline", marginBottom: 14 }}>
        <h1 style={{ fontSize: 22, fontWeight: 700, color: "#000", margin: 0, letterSpacing: "-0.015em" }}>Customers</h1>
        {/* Import-from-Shopify CTA lives in the sub-nav now (Orion
            pattern). The action still runs from this view but the
            button is the panel CTA — see App.jsx purchasing sub-nav. */}
        {importing && (
          <span style={{ fontSize: 12, color: "#6B7280", fontFamily: "Inter, sans-serif" }}>Importing…</span>
        )}
      </div>
      {importMsg && <div style={{ marginBottom: 12, padding: "8px 12px", background: "#F0F9FF", border: "1px solid #BAE6FD", borderRadius: 8, color: "#0C4A6E", fontSize: 13 }}>{importMsg}</div>}
      {importErr?.kind === "scope" && (
        <div style={{ marginBottom: 12, padding: "12px 14px", background: "#FFFBEB", border: "1px solid #FDE68A", borderRadius: 10, color: "#92400E", fontSize: 13, lineHeight: 1.55 }}>
          <div style={{ fontWeight: 700, color: "#7C2D12", marginBottom: 4 }}>Shopify needs your approval to read customers</div>
          <div>
            Your Shopify store hasn't approved the <code style={{ background: "rgba(0,0,0,0.06)", padding: "1px 5px", borderRadius: 4 }}>read_customers</code> scope yet.
            Re-install the Roaster app on your Shopify store (or click "Reconnect store" in <strong>Settings → Store Connect</strong>) so Shopify can ask you to grant the new permission.
            Then come back here and try the import again.
          </div>
        </div>
      )}
      {importErr?.kind === "generic" && (
        <div style={{ marginBottom: 12, padding: "8px 12px", background: "#FEF2F2", border: "1px solid #FECACA", borderRadius: 8, color: "#991B1B", fontSize: 13 }}>{importErr.message}</div>
      )}

      {/* Default payment terms */}
      <div style={{ marginBottom: 14, padding: "12px 14px", background: "#F9FAFB", border: "1px solid #E5E7EB", borderRadius: 10, display: "flex", alignItems: "center", justifyContent: "space-between", gap: 12, flexWrap: "wrap" }}>
        <div>
          <div style={{ fontSize: 11, fontWeight: 700, color: "#6B7280", textTransform: "uppercase", letterSpacing: "0.06em" }}>Default payment terms</div>
          {!editingDefaults ? (
            <div style={{ fontSize: 14, color: "#000", marginTop: 2 }}>
              <strong>{defaults.paymentTerms}</strong> · <strong>{defaults.paymentMethod}</strong>
              <span style={{ color: "#6B7280", marginLeft: 8, fontSize: 12 }}>(applies to any vendor or customer without their own override)</span>
            </div>
          ) : (
            <div style={{ display: "flex", gap: 8, alignItems: "center", marginTop: 4 }}>
              <select value={defForm.paymentTerms} onChange={(e) => setDefForm(f => ({ ...f, paymentTerms: e.target.value }))} style={{ padding: "6px 10px", borderRadius: 6, border: "1px solid #E5E7EB", background: "#fff", fontSize: 13 }}>
                {NET_TERMS.map(t => <option key={t} value={t}>{t}</option>)}
              </select>
              <select value={defForm.paymentMethod} onChange={(e) => setDefForm(f => ({ ...f, paymentMethod: e.target.value }))} style={{ padding: "6px 10px", borderRadius: 6, border: "1px solid #E5E7EB", background: "#fff", fontSize: 13 }}>
                {METHODS.map(t => <option key={t} value={t}>{t}</option>)}
              </select>
            </div>
          )}
        </div>
        {!editingDefaults ? (
          <button onClick={() => setEditingDefaults(true)} style={{ padding: "6px 12px", borderRadius: 8, border: "1px solid #E5E7EB", background: "#fff", color: "#000", fontSize: 12, fontWeight: 600, cursor: "pointer", fontFamily: "Inter, sans-serif" }}>Edit</button>
        ) : (
          <div style={{ display: "flex", gap: 6 }}>
            <button onClick={() => { setEditingDefaults(false); setDefForm(defaults); }} disabled={savingDefaults} style={{ padding: "6px 12px", borderRadius: 8, border: "1px solid #E5E7EB", background: "#fff", color: "#000", fontSize: 12, fontWeight: 600, cursor: "pointer", fontFamily: "Inter, sans-serif" }}>Cancel</button>
            <button onClick={saveDefaults} disabled={savingDefaults} style={{ padding: "6px 12px", borderRadius: 8, border: "none", background: "#000", color: "#fff", fontSize: 12, fontWeight: 600, cursor: savingDefaults ? "default" : "pointer", fontFamily: "Inter, sans-serif" }}>{savingDefaults ? "Saving…" : "Save"}</button>
          </div>
        )}
      </div>

      {/* Totals strip — each tile pairs a headline with a 12-month
          sparkline and a "vs prior 3-month avg" trend chip. */}
      <div style={{ display: "grid", gridTemplateColumns: "repeat(4, 1fr)", gap: 10, marginBottom: 14 }}>
        {[
          // direction:  "good-up" → up arrow green / down arrow red
          //             "bad-up"  → reversed (rising overdue / debt is bad)
          // suppressTrend: true → drop the % chip entirely. We do this
          //   for the unpaid-orders / due / overdue tiles because the
          //   historical series replays today's still-unpaid orders
          //   backward; orders that were unpaid back then but have
          //   since been paid never appear in the historical buckets,
          //   so the trend is structurally biased upward. Until we
          //   capture daily snapshots, showing a number here is wrong.
          { l: "Customers",    v: clients.length,           tk: "customers", money: false, filter: "all",     direction: "good-up" },
          { l: "Total orders", v: totals.orders,            tk: "orders",    money: false, filter: "orders",  direction: "good-up", suppressTrend: true },
          { l: "Total due",    v: fmtMoney(totals.due),     tk: "due",       money: true,  filter: "due",     direction: "bad-up",  suppressTrend: true },
          { l: "Overdue",      v: fmtMoney(totals.overdue), tk: "overdue",   money: true,  filter: "overdue", color: totals.overdue > 0 ? "#B91C1C" : "#000", direction: "bad-up", suppressTrend: true },
        ].map(s => {
          const trend = s.suppressTrend ? null : trendFor(s.tk);
          // Stock metrics (orders / due / overdue): show all 12 months
          // — the current month-end snapshot is meaningful and matches
          // the headline. Flow metrics (customers / new this month):
          // drop the in-progress current month so it doesn't read as
          // a crash.
          const isFlow = s.tk === "customers";
          const visibleMonths = isFlow ? series.slice(0, -1) : series;
          const sparkData = visibleMonths.map(m => m[s.tk]);
          const months = visibleMonths.map(m => m.label);
          const active = tileFilter === s.filter;
          const onClick = () => setTileFilter(t => t === s.filter ? "all" : s.filter);
          return (
            <StatTile
              key={s.l}
              label={s.l}
              value={s.v}
              valueColor={s.color}
              trend={trend}
              direction={s.direction || "good-up"}
              suppressTrend={!!s.suppressTrend}
              series={sparkData}
              money={s.money}
              months={months}
              active={active}
              onClick={onClick}
            />
          );
        })}
      </div>

      {tileFilter !== "all" && (
        <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", gap: 10, padding: "8px 12px", background: "#000", color: "#fff", borderRadius: 8, marginBottom: 10, fontSize: 12 }}>
          <span>
            Showing {sorted.length} customer{sorted.length === 1 ? "" : "s"} with{" "}
            {tileFilter === "due" ? "an open balance" : tileFilter === "overdue" ? "overdue orders" : "unpaid orders"}.
          </span>
          <button onClick={() => setTileFilter("all")} style={{ padding: "3px 10px", borderRadius: 99, border: "1px solid rgba(255,255,255,0.3)", background: "transparent", color: "#fff", fontSize: 11, fontWeight: 600, cursor: "pointer", fontFamily: "Inter, sans-serif" }}>Clear filter</button>
        </div>
      )}

      {/* Search bar — comma-separated tokens match name, email, alt
          emails, company, or unpaid order numbers. */}
      {clients.length > 0 && (
        <div style={{ display: "flex", alignItems: "center", gap: 10, marginBottom: 10 }}>
          <div style={{ flex: 1, position: "relative" }}>
            <Search size={14} strokeWidth={2} color="#9CA3AF" style={{ position: "absolute", top: "50%", left: 12, transform: "translateY(-50%)" }} />
            <input
              type="text"
              value={clientSearch}
              onChange={(e) => setClientSearch(e.target.value)}
              placeholder="Search by name, email, or order #. Comma-separate to search multiple."
              style={{ width: "100%", padding: "8px 12px 8px 34px", borderRadius: 8, border: "1px solid #E5E7EB", fontSize: 13, fontFamily: "Inter, sans-serif", color: "#000", background: "#fff", outline: "none", boxSizing: "border-box" }}
            />
            {clientSearch && (
              <button
                onClick={() => setClientSearch("")}
                title="Clear search"
                style={{ position: "absolute", top: "50%", right: 8, transform: "translateY(-50%)", background: "none", border: "none", padding: 4, color: "#6B7280", cursor: "pointer", display: "flex", alignItems: "center" }}
              >
                <X size={13} strokeWidth={2.4} />
              </button>
            )}
          </div>
          {searchTokens.length > 0 && !isOrderSearch && (
            <span style={{ fontSize: 12, color: "#6B7280", whiteSpace: "nowrap" }}>
              {sorted.length} match{sorted.length === 1 ? "" : "es"} for {searchTokens.length} term{searchTokens.length === 1 ? "" : "s"}
            </span>
          )}
          <button
            onClick={() => setCreatingNew(true)}
            style={{ padding: "8px 14px", borderRadius: 8, border: "1px solid #000", background: "#000", color: "#fff", fontSize: 13, fontWeight: 600, cursor: "pointer", fontFamily: "Inter, sans-serif", whiteSpace: "nowrap" }}
          >
            + New customer
          </button>
        </div>
      )}
      {isOrderSearch ? (
        <OrderSearchResults
          rows={orderResults}
          tokens={searchTokens}
          missing={orderResults.missing || []}
          onClear={() => setClientSearch("")}
          patchClientLocal={patchClientLocal}
          setOpenClientId={setOpenClientId}
        />
      ) : clients.length === 0 ? (
        <div style={{ background: "#FAFAFA", borderRadius: 12, padding: 48, textAlign: "center", color: "#9CA3AF", fontSize: 14, border: "1px dashed #E5E7EB" }}>
          No customers yet. Click <strong style={{ color: "#000" }}>Import from Shopify</strong> to pull your existing customers, or wait for an order to land.
        </div>
      ) : (
        <div style={{ background: "#fff", border: "1px solid #E5E7EB", borderRadius: 12, overflow: "auto" }}>
          <div style={{ display: "grid", gridTemplateColumns: "1.4fr 1.6fr 80px 90px 110px 110px 150px 80px 36px 36px", columnGap: 12, padding: "10px 16px", background: "#F9FAFB", borderBottom: "1px solid #E5E7EB" }}>
            <SortHead k="name"    label="Name" sort={sort} setSort={setSort} />
            <SortHead k="contact" label="Contact" sort={sort} setSort={setSort} />
            <SortHead k="orders"  label="Orders"   align="right" sort={sort} setSort={setSort} />
            <SortHead k="lastOrder" label="Last order" align="right" sort={sort} setSort={setSort} />
            <SortHead k="due"     label="Total due" align="right" sort={sort} setSort={setSort} />
            <SortHead k="overdue" label="Overdue"  align="right" sort={sort} setSort={setSort} />
            <SortHead k="terms"   label="Terms" sort={sort} setSort={setSort} />
            <SortHead k="method"  label="Method"   align="right" sort={sort} setSort={setSort} />
            <span /> {/* send-statement column */}
            <span /> {/* archive column */}
          </div>
          {(() => {
            // Three buckets:
            //   active    — has any orders/balance, not archived
            //   archived  — operator manually archived (collapsible)
            //   empty     — zero orders, zero balance, not archived
            // Tile filters skip the split since they already prune zeros.
            const isFiltered = tileFilter !== "all";
            const hasAny = (c) => (Number(c.orderCount) || 0) > 0
              || (Number(c.shopifyUnpaidOrdersCount) || 0) > 0
              || (Number(c.totalDue) || 0) > 0;
            const isArch = (c) => !!c.archivedAt;
            const active   = isFiltered ? sorted : sorted.filter(c => !isArch(c) && hasAny(c));
            const archived = isFiltered ? []     : sorted.filter(c => isArch(c));
            const empty    = isFiltered ? []     : sorted.filter(c => !isArch(c) && !hasAny(c));
            const renderRow = (c, opts = {}) => (
              <div
                key={c.id}
                onClick={() => setOpenClientId(c.id)}
                onMouseEnter={(e) => e.currentTarget.style.background = "#FAFAFA"}
                onMouseLeave={(e) => e.currentTarget.style.background = "transparent"}
                style={{ display: "grid", gridTemplateColumns: "1.4fr 1.6fr 80px 90px 110px 110px 150px 80px 36px 36px", columnGap: 12, alignItems: "center", padding: "12px 16px", borderTop: "1px solid #F3F4F6", fontFamily: "Inter, sans-serif", cursor: "pointer", opacity: opts.archived ? 0.7 : 1 }}>
                <div style={{ minWidth: 0 }}>
                  <div style={{ fontSize: 14, fontWeight: 600, color: "#000", whiteSpace: "nowrap", overflow: "hidden", textOverflow: "ellipsis" }}>{c.name}</div>
                  {(() => {
                    const sub = [c.reportingCompany, c.reportingDistributor].filter(Boolean).join(" — ") || c.company;
                    return sub ? <div style={{ fontSize: 11, color: "#6B7280", marginTop: 1, whiteSpace: "nowrap", overflow: "hidden", textOverflow: "ellipsis" }}>{sub}</div> : null;
                  })()}
                </div>
                <div style={{ minWidth: 0, fontSize: 12, color: "#3F3F46" }}>
                  {c.email && <div style={{ whiteSpace: "nowrap", overflow: "hidden", textOverflow: "ellipsis" }}>{c.email}</div>}
                  {c.phone && <div style={{ color: "#6B7280" }}>{c.phone}</div>}
                  {(c.shopifyLastOrderAt || c.lastStatementSentAt) && (
                    <div style={{ color: "#9CA3AF", fontSize: 11, marginTop: 2, display: "flex", gap: 10, flexWrap: "wrap" }}>
                      {c.shopifyLastOrderAt && <span>Last order {fmtShortDate(c.shopifyLastOrderAt)}</span>}
                      {c.lastStatementSentAt && <span>Reminded {fmtShortDate(c.lastStatementSentAt)}</span>}
                    </div>
                  )}
                </div>
                <span style={{ fontSize: 13, color: "#000", textAlign: "right", fontVariantNumeric: "tabular-nums" }}>{c.orderCount || 0}</span>
                {(() => {
                  // Relative-time "Last ordered" cell. Color-coded so
                  // the operator can spot dormant accounts at a glance:
                  // ≤2w fresh (black), ≤3mo neutral, >3mo muted gray.
                  const ts = c.shopifyLastOrderAt ? new Date(c.shopifyLastOrderAt).getTime() : null;
                  if (!ts || !Number.isFinite(ts)) {
                    return <span style={{ fontSize: 12, color: "#D1D5DB", textAlign: "right" }}>—</span>;
                  }
                  const days = Math.max(0, Math.round((Date.now() - ts) / 86400000));
                  const label = days < 1   ? "today"
                              : days === 1 ? "1 d ago"
                              : days < 14  ? `${days} d ago`
                              : days < 60  ? `${Math.round(days / 7)} w ago`
                              : days < 730 ? `${Math.round(days / 30)} mo ago`
                              :              `${Math.round(days / 365)} y ago`;
                  const color = days <= 14 ? "#000"
                              : days <= 90 ? "#3F3F46"
                              :              "#9CA3AF";
                  return (
                    <span style={{ fontSize: 12, color, textAlign: "right", fontVariantNumeric: "tabular-nums", whiteSpace: "nowrap" }}
                          title={new Date(ts).toLocaleDateString("en-US", { month: "short", day: "numeric", year: "numeric" })}>
                      {label}
                    </span>
                  );
                })()}
                <span style={{ fontSize: 13, color: c.totalDue > 0 ? "#000" : "#9CA3AF", textAlign: "right", fontWeight: 600, fontVariantNumeric: "tabular-nums" }}>{c.totalDue > 0 ? fmtMoney(c.totalDue) : "—"}</span>
                <span style={{ fontSize: 13, color: c.totalOverdue > 0 ? "#B91C1C" : "#9CA3AF", textAlign: "right", fontWeight: 700, fontVariantNumeric: "tabular-nums" }}>{c.totalOverdue > 0 ? fmtMoney(c.totalOverdue) : "—"}</span>
                <span style={{ fontSize: 12, color: c.paymentTerms ? "#000" : "#9CA3AF" }}>{c.paymentTerms || `${defaults.paymentTerms} (default)`}</span>
                <span style={{ fontSize: 12, color: c.paymentMethod ? "#000" : "#9CA3AF", textAlign: "right" }}>{c.paymentMethod || `${defaults.paymentMethod}`}</span>
                {(() => {
                  const due = Number(c.totalDue) || 0;
                  const noEmail = !c.email;
                  const noBalance = due <= 0;
                  const disabled = noEmail || noBalance || statementLoadingId === c.id;
                  const last = c.lastStatementSentAt ? fmtShortDate(c.lastStatementSentAt) : null;
                  const tip = noEmail ? "No email on file"
                    : noBalance ? "Nothing outstanding"
                    : last ? `Send a statement to ${c.email} — last sent ${last}`
                           : `Send a statement to ${c.email}`;
                  return (
                    <div style={{ display: "flex", flexDirection: "column", alignItems: "flex-end", gap: 2, justifySelf: "end" }}>
                      <button
                        onClick={(e) => !disabled && openStatementFromList(e, c)}
                        title={tip}
                        disabled={disabled}
                        style={{ display: "flex", alignItems: "center", justifyContent: "center", width: 28, height: 28, padding: 0, borderRadius: 6, border: "1px solid #000", background: disabled ? "#F4F4F5" : "#000", color: disabled ? "#D1D5DB" : "#fff", cursor: disabled ? "default" : "pointer" }}
                      >
                        <Send size={13} strokeWidth={2} style={{ opacity: statementLoadingId === c.id ? 0.4 : 1 }} />
                      </button>
                      {last && (
                        <span style={{ fontSize: 10, color: "#9CA3AF", fontVariantNumeric: "tabular-nums", whiteSpace: "nowrap", lineHeight: 1 }}>
                          {last}
                        </span>
                      )}
                    </div>
                  );
                })()}
                <button
                  onClick={(e) => toggleArchived(e, c)}
                  title={opts.archived ? "Restore — move back to main list" : "Archive — hide from main list (auto-restores when a new order lands)"}
                  style={{ display: "flex", alignItems: "center", justifyContent: "center", width: 28, height: 28, padding: 0, borderRadius: 6, border: "1px solid #E5E7EB", background: "#fff", color: "#6B7280", cursor: "pointer", justifySelf: "end" }}
                  onMouseEnter={(e) => { e.currentTarget.style.borderColor = "#000"; e.currentTarget.style.color = "#000"; }}
                  onMouseLeave={(e) => { e.currentTarget.style.borderColor = "#E5E7EB"; e.currentTarget.style.color = "#6B7280"; }}
                >
                  {opts.archived
                    ? <RotateCcw size={13} strokeWidth={2} />
                    : <Archive size={13} strokeWidth={2} />}
                </button>
              </div>
            );
            const foldBar = (label, count, expanded, setter) => (
              <button
                onClick={() => setter(v => !v)}
                style={{ width: "100%", display: "flex", alignItems: "center", justifyContent: "space-between", padding: "10px 16px", background: "#F9FAFB", border: "none", borderTop: "1px solid #E5E7EB", cursor: "pointer", fontFamily: "Inter, sans-serif", textAlign: "left" }}
                onMouseEnter={(e) => e.currentTarget.style.background = "#F4F4F5"}
                onMouseLeave={(e) => e.currentTarget.style.background = "#F9FAFB"}
              >
                <span style={{ fontSize: 12, fontWeight: 600, color: "#6B7280", letterSpacing: "0.02em" }}>
                  {expanded ? "▼" : "▶"}  {count} {label}
                </span>
                <span style={{ fontSize: 11, color: "#9CA3AF" }}>{expanded ? "click to collapse" : "click to expand"}</span>
              </button>
            );
            return (
              <>
                {active.map(c => renderRow(c))}
                {archived.length > 0 && (
                  <>
                    {foldBar(`archived customer${archived.length === 1 ? "" : "s"}`, archived.length, showArchived, setShowArchived)}
                    {showArchived && archived.map(c => renderRow(c, { archived: true }))}
                  </>
                )}
                {empty.length > 0 && (
                  <>
                    {foldBar(`customer${empty.length === 1 ? "" : "s"} with no orders`, empty.length, showZeroOrder, setShowZeroOrder)}
                    {showZeroOrder && empty.map(c => renderRow(c))}
                  </>
                )}
              </>
            );
          })()}
        </div>
      )}

      {/* Quick-send statement modal — opened from the per-row Send
          button. The modal handles the email-setup flow itself if the
          workspace hasn't picked a handle yet. */}
      {statementCtx && (
        <EmailStatementModal
          client={statementCtx.client}
          orders={statementCtx.orders}
          totals={statementCtx.totals}
          onClose={() => setStatementCtx(null)}
        />
      )}
      {creatingNew && (
        <NewClientModal
          defaults={defaults}
          onClose={() => setCreatingNew(false)}
          onCreated={() => {
            setCreatingNew(false);
            refresh && refresh();
          }}
        />
      )}
    </div>
  );
}

/* ── Order-search results ─────────────────────────────────────────────
   Renders a flat list of orders matched by paste-in numbers (e.g.
   "5141-WS 5142-WS 4966-WS"). The operator can mark each one paid
   in place without drilling into the customer record. Mark-paid
   uses the existing /api/clients/:cid/orders/:oid/mark-paid
   endpoint, which also syncs the payment to Shopify. */
function OrderSearchResults({ rows, tokens, missing, onClear, patchClientLocal, setOpenClientId }) {
  const [busyId, setBusyId] = useState(null);
  const [paidMap, setPaidMap] = useState({});
  // Order-detail modal — clicking the order number opens a full
  // line-items / shipping / fulfillment view in-place.
  const [detailRow, setDetailRow] = useState(null);
  // Cache of live order resolution — { rowKey → { orderId, adminUrl } }.
  // Populated by resolveOrder() so admin-↗ links and mark-paid work
  // even on legacy persisted rows that pre-date the adminUrl capture.
  const [resolvedMap, setResolvedMap] = useState({});
  const inflightRef = useRef(new Set());
  // Live Shopify fallback — when a token doesn't match a local
  // (unpaid) snapshot order, hit /api/orders/lookup-by-numbers to
  // find it in Shopify (might be paid, or just not snapshotted).
  const [liveRows, setLiveRows] = useState([]);
  const [liveLoading, setLiveLoading] = useState(false);
  useEffect(() => {
    if (!missing || missing.length === 0) { setLiveRows([]); return; }
    let cancelled = false;
    setLiveLoading(true);
    fetch(`/api/orders/lookup-by-numbers?nums=${encodeURIComponent(missing.join(","))}`, { credentials: "same-origin" })
      .then(r => r.ok ? r.json() : { orders: [] })
      .then(d => { if (!cancelled) setLiveRows(d.orders || []); })
      .catch(() => { if (!cancelled) setLiveRows([]); })
      .finally(() => { if (!cancelled) setLiveLoading(false); });
    return () => { cancelled = true; };
  }, [missing && missing.join(",")]); // eslint-disable-line react-hooks/exhaustive-deps
  // Merge: local rows first, then live fallback rows (deduped by orderId).
  const allRows = useMemo(() => {
    const have = new Set(rows.map(r => r.orderId).filter(Boolean));
    return [...rows, ...liveRows.filter(r => !r.orderId || !have.has(r.orderId))];
  }, [rows, liveRows]);
  const stillMissing = useMemo(() => {
    if (!missing || missing.length === 0) return [];
    const haveNums = new Set(allRows.map(r => String(r.number || "").toLowerCase().replace(/^#/, "")));
    return missing.filter(t => !haveNums.has(t));
  }, [missing, allRows]);

  const fmtMoney = (v) => `$${Number(v || 0).toLocaleString("en-US", { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;
  const fmtDate  = (iso) => iso ? new Date(iso).toLocaleDateString("en-US", { month: "short", day: "numeric", year: "numeric" }) : "—";
  const rowKeyFor = (row) => row.orderId || `pending:${row.clientId}:${row.number}`;

  // Lazy resolve {orderId, adminUrl} via the per-customer orders
  // endpoint. Cached per row, deduped per inflight request.
  const resolveOrder = async (row) => {
    const k = rowKeyFor(row);
    if (resolvedMap[k]) return resolvedMap[k];
    if (row.orderId && row.adminUrl) {
      const cached = { orderId: row.orderId, adminUrl: row.adminUrl };
      setResolvedMap(m => ({ ...m, [k]: cached }));
      return cached;
    }
    if (inflightRef.current.has(k)) return null;
    inflightRef.current.add(k);
    try {
      const r = await fetch(`/api/clients/${encodeURIComponent(row.clientId)}/orders`, { credentials: "same-origin" });
      if (!r.ok) return null;
      const d = await r.json();
      const want = String(row.number || "").trim().toLowerCase().replace(/^#/, "");
      const match = (d.orders || []).find(o => String(o.number || "").trim().toLowerCase().replace(/^#/, "") === want);
      if (!match) return null;
      const out = { orderId: match.id || row.orderId || null, adminUrl: match.adminUrl || row.adminUrl || null };
      setResolvedMap(m => ({ ...m, [k]: out }));
      return out;
    } catch { return null; }
    finally { inflightRef.current.delete(k); }
  };

  // Eagerly resolve any rows that don't have a usable adminUrl/orderId
  // yet, capped at 25 concurrent so a paste of 200 numbers doesn't spam
  // the server. Sequential per-customer is fine.
  useEffect(() => {
    let cancelled = false;
    (async () => {
      const need = rows.filter(r => !r.orderId || !r.adminUrl).slice(0, 25);
      for (const row of need) {
        if (cancelled) return;
        if (resolvedMap[rowKeyFor(row)]) continue;
        await resolveOrder(row);
      }
    })();
    return () => { cancelled = true; };
  }, [rows]); // eslint-disable-line react-hooks/exhaustive-deps

  const markPaid = async (row) => {
    if (!row.clientId) return;
    const k = rowKeyFor(row);
    setBusyId(k);
    try {
      const resolved = await resolveOrder(row);
      const orderId = resolved?.orderId || row.orderId;
      if (!orderId) {
        alert("Couldn't find this order in Shopify. Re-run the customer import and try again.");
        return;
      }
      setPaidMap(m => ({ ...m, [k]: true }));
      const r = await fetch(`/api/clients/${encodeURIComponent(row.clientId)}/orders/${encodeURIComponent(orderId)}/mark-paid`, {
        method: "POST",
        credentials: "same-origin",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ unpaid: false }),
      });
      if (!r.ok) {
        setPaidMap(m => ({ ...m, [k]: false }));
        alert("Couldn't mark paid — refresh and try again.");
        return;
      }
      // Patch the customer's snapshot so the headline tiles + per-row
      // totals on the customers list reflect the payment immediately,
      // without waiting for a full refresh / re-import. We drop the
      // matched order from shopifyUnpaidOrders and recompute the
      // totals from what's left.
      if (patchClientLocal) {
        patchClientLocal(row.clientId, (c) => {
          const list = Array.isArray(c.shopifyUnpaidOrders) ? c.shopifyUnpaidOrders : [];
          const want = String(row.number || "").trim().toLowerCase().replace(/^#/, "");
          const idMatch = row.rawId ? String(row.rawId) : null;
          const remaining = list.filter(o => {
            const numMatches = String(o.number || "").trim().toLowerCase().replace(/^#/, "") === want;
            const idMatches  = idMatch && o.id && String(o.id) === idMatch;
            return !(numMatches || idMatches);
          });
          // Recompute totals using the same fulfillment-cascade rules the
          // server uses (see clients.js totalsFor): pending-shipment rows
          // don't count, due date anchors on fulfilledAt → closedAt → createdAt.
          const days = (() => {
            const m = String(c.paymentTerms || c.effectivePaymentTerms || "Net 30").match(/(\d+)/);
            return m ? parseInt(m[1], 10) : 30;
          })();
          const todayMs = Date.now();
          let due = 0, overdue = 0, pending = 0;
          for (const o of remaining) {
            const out = Number(o.outstanding) || 0;
            if (out <= 0) continue;
            const fulfilledMs = o.fulfilledAt ? new Date(o.fulfilledAt).getTime() : NaN;
            const closedMs    = o.closedAt    ? new Date(o.closedAt).getTime()    : NaN;
            const createdMs   = o.createdAt   ? new Date(o.createdAt).getTime()   : NaN;
            const hasFulfillmentSignal =
              o.fulfillmentStatus === "fulfilled" ||
              o.fulfillmentStatus === "partial" ||
              Number.isFinite(fulfilledMs) ||
              Number.isFinite(closedMs);
            if (!hasFulfillmentSignal) { pending += out; continue; }
            const anchorMs = Number.isFinite(fulfilledMs) ? fulfilledMs
              : Number.isFinite(closedMs) ? closedMs : createdMs;
            if (!Number.isFinite(anchorMs)) continue;
            due += out;
            if (anchorMs + days * 86400000 < todayMs) overdue += out;
          }
          return {
            shopifyUnpaidOrders: remaining,
            shopifyUnpaidTotal: +due.toFixed(2),
            shopifyUnpaidOrdersCount: remaining.length,
            totalDue: +due.toFixed(2),
            totalOverdue: +overdue.toFixed(2),
            totalPendingShipment: +pending.toFixed(2),
          };
        });
      }
    } finally {
      setBusyId(null);
    }
  };

  // Group rows by customer so the search-results layout reads exactly
  // like a stack of mini customer-detail tables — operator gets the
  // full context (who owes what) without leaving the search.
  const groups = useMemo(() => {
    const m = new Map();
    for (const row of allRows) {
      // Live-Shopify rows without a matched local customer still need
      // a stable grouping key — use a synthetic one based on the
      // customer email or "unknown".
      const key = row.clientId || `noclient:${row.clientEmail || "unknown"}`;
      if (!m.has(key)) m.set(key, { clientId: row.clientId, clientName: row.clientName, clientEmail: row.clientEmail, orders: [] });
      m.get(key).orders.push(row);
    }
    return [...m.values()];
  }, [allRows]);

  if (allRows.length === 0 && liveLoading) {
    return (
      <div style={{ background: "#FAFAFA", borderRadius: 12, padding: 32, textAlign: "center", color: "#9CA3AF", fontSize: 13, border: "1px dashed #E5E7EB", fontFamily: "Inter, sans-serif" }}>
        Searching Shopify…
      </div>
    );
  }
  if (allRows.length === 0) {
    return (
      <div style={{ background: "#FAFAFA", borderRadius: 12, padding: 32, textAlign: "center", color: "#6B7280", fontSize: 14, border: "1px dashed #E5E7EB", fontFamily: "Inter, sans-serif" }}>
        <div style={{ color: "#000", fontWeight: 600, marginBottom: 6 }}>No matching orders found</div>
        <div style={{ fontSize: 13 }}>None of <strong>{tokens.length}</strong> pasted order number{tokens.length === 1 ? "" : "s"} matched any order on file or in Shopify.</div>
        <button onClick={onClear} style={{ marginTop: 14, padding: "7px 14px", borderRadius: 6, border: "1px solid #E5E7EB", background: "#fff", color: "#000", fontSize: 12, fontWeight: 600, cursor: "pointer" }}>Clear search</button>
      </div>
    );
  }

  return (
    <div>
      <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", padding: "10px 14px", background: "#000", color: "#fff", borderRadius: 8, marginBottom: 10, fontSize: 12, fontFamily: "Inter, sans-serif" }}>
        <span>
          <strong>{allRows.length}</strong> of {tokens.length} order{tokens.length === 1 ? "" : "s"} matched
          {liveLoading && <span style={{ marginLeft: 8, color: "rgba(255,255,255,0.65)" }}>· checking Shopify…</span>}
          {!liveLoading && stillMissing.length > 0 && (
            <span style={{ marginLeft: 8, color: "rgba(255,255,255,0.65)" }}>
              · {stillMissing.length} unmatched: {stillMissing.slice(0, 6).join(", ")}{stillMissing.length > 6 ? `, +${stillMissing.length - 6}` : ""}
            </span>
          )}
        </span>
        <button onClick={onClear} style={{ padding: "3px 10px", borderRadius: 99, border: "1px solid rgba(255,255,255,0.3)", background: "transparent", color: "#fff", fontSize: 11, fontWeight: 600, cursor: "pointer" }}>Clear</button>
      </div>
      {/* Grouped by customer — each group renders the EXACT order-row
          layout from the customer-detail page so the operator sees a
          familiar table per customer. */}
      <div style={{ display: "flex", flexDirection: "column", gap: 14 }}>
        {groups.map(group => (
          <div key={group.clientId} style={{ background: "#fff", border: "1px solid #E5E7EB", borderRadius: 12, overflow: "hidden" }}>
            <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", padding: "12px 16px", borderBottom: "1px solid #F3F4F6", background: "#FAFAFA" }}>
              <button
                onClick={() => setOpenClientId && setOpenClientId(group.clientId)}
                title="Open customer"
                style={{ background: "transparent", border: "none", padding: 0, fontSize: 14, fontWeight: 700, color: "#000", cursor: "pointer", fontFamily: "Inter, sans-serif", textAlign: "left" }}
                onMouseEnter={(e) => { e.currentTarget.style.textDecoration = "underline"; }}
                onMouseLeave={(e) => { e.currentTarget.style.textDecoration = "none"; }}
              >
                {group.clientName}
              </button>
              <span style={{ fontSize: 12, color: "#6B7280" }}>{group.orders.length} order{group.orders.length === 1 ? "" : "s"}</span>
            </div>
            {/* Header row — same column template + labels as the
                customer-detail page. */}
            <div style={{ display: "grid", gridTemplateColumns: "90px 120px 110px 110px 120px 1fr 100px 64px", columnGap: 10, padding: "10px 16px", background: "#F9FAFB", borderBottom: "1px solid #E5E7EB", fontSize: 11, fontWeight: 600, color: "#6B7280", textTransform: "uppercase", letterSpacing: 0.06 }}>
              <span>Kind</span>
              <span>Number</span>
              <span>Date</span>
              <span style={{ textAlign: "right" }}>Total</span>
              <span style={{ textAlign: "right" }}>Outstanding</span>
              <span>Status & fulfillment</span>
              <span></span>
              <span></span>
            </div>
            {group.orders.map((row, idx) => {
              const rk = rowKeyFor(row);
              const resolved = resolvedMap[rk] || {};
              const adminUrl = row.adminUrl || resolved.adminUrl || null;
              const isPaid = !!paidMap[rk];
              const isBusy = busyId === rk;
              const isFulfilled = row.fulfillmentStatus === "fulfilled" || row.fulfillmentStatus === "partial" || !!row.fulfilledAt;
              // Already paid in Shopify (came back from the live lookup
              // with no outstanding balance). Surface as a Paid pill,
              // hide Mark paid — there's nothing to do.
              const paidInShopify = !!row.alreadyPaid;
              const statusPill = (isPaid || paidInShopify)
                ? { bg: "#ECFDF5", fg: "#047857", border: "#A7F3D0", label: paidInShopify && !isPaid ? "Paid (Shopify)" : "Paid" }
                : !isFulfilled
                  ? { bg: "#F4F4F5", fg: "#52525B", border: "#E4E4E7", label: "Unfulfilled" }
                  : { bg: "#FEF3C7", fg: "#92400E", border: "#FDE68A", label: "Unpaid" };
              const fulfilledLabel = row.fulfillmentStatus === "fulfilled" && row.fulfilledAt
                ? `Fulfilled ${fmtDate(row.fulfilledAt)}`
                : row.fulfillmentStatus === "partial"
                  ? "Partially fulfilled"
                  : null;
              return (
                <div
                  key={`${row.clientId}:${row.orderId || row.number}:${idx}`}
                  style={{ display: "grid", gridTemplateColumns: "90px 120px 110px 110px 120px 1fr 100px 64px", columnGap: 10, alignItems: "center", padding: "12px 16px", borderTop: "1px solid #F3F4F6", fontFamily: "Inter, sans-serif", opacity: isPaid ? 0.55 : 1 }}
                >
                  <span style={{ fontSize: 11, fontWeight: 600, color: "#71717A", textTransform: "uppercase", letterSpacing: "0.05em" }}>Shopify</span>
                  <button
                    onClick={() => setDetailRow(row)}
                    title="View order details"
                    style={{ fontSize: 13, fontWeight: 600, color: "#000", fontVariantNumeric: "tabular-nums", whiteSpace: "nowrap", overflow: "hidden", textOverflow: "ellipsis", background: "transparent", border: "none", padding: 0, textAlign: "left", cursor: "pointer", fontFamily: "Inter, sans-serif" }}
                    onMouseEnter={(e) => { e.currentTarget.style.textDecoration = "underline"; }}
                    onMouseLeave={(e) => { e.currentTarget.style.textDecoration = "none"; }}
                  >{row.number}</button>
                  <span style={{ fontSize: 12, color: "#6B7280", fontVariantNumeric: "tabular-nums" }}>{fmtDate(row.createdAt)}</span>
                  <span style={{ fontSize: 13, color: "#3F3F46", textAlign: "right", fontVariantNumeric: "tabular-nums" }}>{fmtMoney(row.total)}</span>
                  <span style={{ fontSize: 13, fontWeight: 700, color: row.outstanding > 0 ? "#000" : "#9CA3AF", textAlign: "right", fontVariantNumeric: "tabular-nums" }}>
                    {row.outstanding > 0 ? fmtMoney(row.outstanding) : "—"}
                  </span>
                  <span style={{ minWidth: 0 }}>
                    <span style={{ display: "inline-flex", alignItems: "center", gap: 8, flexWrap: "wrap" }}>
                      <span style={{ display: "inline-block", padding: "3px 9px", borderRadius: 99, fontSize: 10, fontWeight: 700, letterSpacing: 0.4, textTransform: "uppercase", background: statusPill.bg, color: statusPill.fg, border: `1px solid ${statusPill.border}`, whiteSpace: "nowrap" }}>{statusPill.label}</span>
                      {fulfilledLabel && (
                        <span style={{ fontSize: 11, color: "#047857", fontWeight: 600, whiteSpace: "nowrap" }}>{fulfilledLabel}</span>
                      )}
                    </span>
                  </span>
                  <span style={{ textAlign: "right" }}>
                    {paidInShopify ? (
                      <span style={{ fontSize: 11, color: "#9CA3AF", fontFamily: "Inter, sans-serif" }}>Already paid</span>
                    ) : !isPaid ? (
                      <button onClick={() => markPaid(row)} disabled={isBusy} style={{ padding: "5px 11px", borderRadius: 6, border: "none", background: isBusy ? "#D1D5DB" : "#000", color: "#fff", fontSize: 12, fontWeight: 600, cursor: isBusy ? "default" : "pointer", fontFamily: "Inter, sans-serif" }}>
                        {isBusy ? "…" : "Mark paid"}
                      </button>
                    ) : (
                      <button onClick={() => setPaidMap(m => ({ ...m, [rk]: false }))} disabled={isBusy} style={{ padding: "5px 11px", borderRadius: 6, border: "1px solid #E5E7EB", background: "#fff", color: "#52525B", fontSize: 12, fontWeight: 500, cursor: "pointer", fontFamily: "Inter, sans-serif" }}>
                        Paid
                      </button>
                    )}
                  </span>
                  <span style={{ textAlign: "right" }}>
                    {adminUrl ? (
                      <a
                        href={adminUrl}
                        target="_blank"
                        rel="noreferrer noopener"
                        title="Open in Shopify admin"
                        style={{ display: "inline-flex", alignItems: "center", justifyContent: "center", width: 26, height: 26, borderRadius: 6, border: "1px solid #E5E7EB", background: "#fff", color: "#52525B", textDecoration: "none", fontSize: 13, fontWeight: 600, fontFamily: "Inter, sans-serif" }}
                      >↗</a>
                    ) : <span />}
                  </span>
                </div>
              );
            })}
          </div>
        ))}
      </div>
      {detailRow && (
        <OrderDetailModal
          row={detailRow}
          isPaid={!!paidMap[rowKeyFor(detailRow)]}
          busy={busyId === rowKeyFor(detailRow)}
          onMarkPaid={async () => { await markPaid(detailRow); }}
          onClose={() => setDetailRow(null)}
          onOpenCustomer={(cid) => { setDetailRow(null); setOpenClientId && setOpenClientId(cid); }}
        />
      )}
    </div>
  );
}

/* ── Order-detail modal ──────────────────────────────────────────────
   Opens when the operator clicks an order number in the order-search
   results. Pulls the full Shopify order (line items, shipping,
   fulfillment) from /api/orders/shopify/:rawId so we don't depend on
   what was packed into the search row. Mark-paid is exposed here too
   so the operator doesn't have to close the modal to flip status. */
function OrderDetailModal({ row, isPaid, busy, onMarkPaid, onClose, onOpenCustomer }) {
  const [data, setData] = useState(null);
  const [error, setError] = useState("");
  useEffect(() => {
    if (!row?.rawId) return;
    let cancelled = false;
    setData(null); setError("");
    fetch(`/api/orders/shopify/${encodeURIComponent(row.rawId)}`, { credentials: "same-origin" })
      .then(r => r.ok ? r.json() : r.json().then(d => Promise.reject(d.error || `HTTP ${r.status}`)))
      .then(d => { if (!cancelled) setData(d.order); })
      .catch(e => { if (!cancelled) setError(typeof e === "string" ? e : (e.message || "Couldn't load order")); });
    return () => { cancelled = true; };
  }, [row?.rawId]);

  const fmtMoney = (v) => `$${Number(v || 0).toLocaleString("en-US", { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;
  const fmtDate  = (iso) => iso ? new Date(iso).toLocaleDateString("en-US", { month: "short", day: "numeric", year: "numeric" }) : "—";
  const o = data;

  // Status pill, mirroring the order-search row logic so the modal feels
  // continuous with what the operator just clicked.
  const outstanding = o ? Number(o.outstanding || 0) : Number(row.outstanding || 0);
  const fulfillmentStatus = o ? o.fulfillmentStatus : row.fulfillmentStatus;
  const isFulfilled = fulfillmentStatus === "fulfilled" || fulfillmentStatus === "partial";
  const paidInShopify = outstanding <= 0;
  const statusPill = (isPaid || paidInShopify)
    ? { bg: "#ECFDF5", fg: "#047857", border: "#A7F3D0", label: paidInShopify && !isPaid ? "Paid (Shopify)" : "Paid" }
    : !isFulfilled
      ? { bg: "#F4F4F5", fg: "#52525B", border: "#E4E4E7", label: "Unfulfilled" }
      : { bg: "#FEF3C7", fg: "#92400E", border: "#FDE68A", label: "Unpaid" };

  const addr = o?.shippingAddress || null;

  return (
    <div style={{ position: "fixed", inset: 0, zIndex: 1000, background: "rgba(0,0,0,0.55)", display: "flex", alignItems: "center", justifyContent: "center", padding: 24 }}>
      <div style={{ background: "#fff", borderRadius: 14, padding: "26px 30px", maxWidth: 720, width: "100%", maxHeight: "90vh", overflowY: "auto", boxShadow: "0 24px 64px rgba(0,0,0,0.28)", fontFamily: "Inter, sans-serif" }}>
        <div style={{ display: "flex", alignItems: "flex-start", justifyContent: "space-between", gap: 16, marginBottom: 14 }}>
          <div style={{ minWidth: 0 }}>
            <div style={{ fontSize: 11, fontWeight: 700, color: "#9CA3AF", letterSpacing: "0.06em", textTransform: "uppercase", marginBottom: 4 }}>Order</div>
            <h2 style={{ margin: 0, fontSize: 22, fontWeight: 800, color: "#000", letterSpacing: "-0.01em", fontVariantNumeric: "tabular-nums" }}>{row.number}</h2>
            <div style={{ fontSize: 13, color: "#6B7280", marginTop: 4 }}>
              {fmtDate(o?.createdAt || row.createdAt)}
              {o?.customer && (
                <span> · <button onClick={() => row.clientId && onOpenCustomer && onOpenCustomer(row.clientId)} disabled={!row.clientId} style={{ background: "transparent", border: "none", padding: 0, color: "#000", fontWeight: 600, cursor: row.clientId ? "pointer" : "default", textDecoration: row.clientId ? "underline" : "none", fontFamily: "Inter, sans-serif", fontSize: 13 }}>{[o.customer.firstName, o.customer.lastName].filter(Boolean).join(" ") || o.customer.email || row.clientName}</button></span>
              )}
              {!o?.customer && row.clientName && <span> · {row.clientName}</span>}
            </div>
          </div>
          <div style={{ display: "flex", alignItems: "center", gap: 8 }}>
            <span style={{ display: "inline-block", padding: "4px 10px", borderRadius: 99, fontSize: 10, fontWeight: 700, letterSpacing: 0.4, textTransform: "uppercase", background: statusPill.bg, color: statusPill.fg, border: `1px solid ${statusPill.border}`, whiteSpace: "nowrap" }}>{statusPill.label}</span>
            <button onClick={onClose} title="Close" style={{ background: "transparent", border: "none", color: "#6B7280", cursor: "pointer", padding: 4, lineHeight: 1, fontSize: 18 }}>×</button>
          </div>
        </div>

        {error && (
          <div style={{ padding: "10px 12px", background: "#FEF2F2", border: "1px solid #FECACA", color: "#991B1B", fontSize: 13, borderRadius: 6, marginBottom: 14 }}>{error}</div>
        )}
        {!o && !error && (
          <div style={{ padding: "32px 16px", textAlign: "center", color: "#9CA3AF", fontSize: 13 }}>Loading order…</div>
        )}

        {o && (
          <>
            {/* Line items */}
            <div style={{ border: "1px solid #E5E7EB", borderRadius: 10, overflow: "hidden", marginBottom: 14 }}>
              <div style={{ display: "grid", gridTemplateColumns: "1fr 60px 90px 90px", columnGap: 10, padding: "10px 14px", background: "#F9FAFB", borderBottom: "1px solid #E5E7EB", fontSize: 10, fontWeight: 700, color: "#9CA3AF", textTransform: "uppercase", letterSpacing: 0.04 }}>
                <span>Item</span>
                <span style={{ textAlign: "right" }}>Qty</span>
                <span style={{ textAlign: "right" }}>Unit</span>
                <span style={{ textAlign: "right" }}>Total</span>
              </div>
              {(o.lineItems || []).length === 0 ? (
                <div style={{ padding: "16px", color: "#9CA3AF", fontSize: 13, textAlign: "center" }}>No line items.</div>
              ) : o.lineItems.map((li, i) => (
                <div key={li.id || i} style={{ display: "grid", gridTemplateColumns: "1fr 60px 90px 90px", columnGap: 10, padding: "10px 14px", borderTop: i === 0 ? "none" : "1px solid #F3F4F6", alignItems: "center" }}>
                  <div style={{ minWidth: 0 }}>
                    <div style={{ fontSize: 13, fontWeight: 600, color: "#000" }}>{li.name}{li.variant ? ` — ${li.variant}` : ""}</div>
                    {li.sku && <div style={{ fontSize: 11, color: "#9CA3AF", marginTop: 1 }}>{li.sku}</div>}
                  </div>
                  <span style={{ fontSize: 13, color: "#3F3F46", textAlign: "right", fontVariantNumeric: "tabular-nums" }}>{li.qty}</span>
                  <span style={{ fontSize: 13, color: "#3F3F46", textAlign: "right", fontVariantNumeric: "tabular-nums" }}>{fmtMoney(li.unitPrice)}</span>
                  <span style={{ fontSize: 13, fontWeight: 700, color: "#000", textAlign: "right", fontVariantNumeric: "tabular-nums" }}>{fmtMoney(li.total)}</span>
                </div>
              ))}
              {/* Totals */}
              <div style={{ padding: "10px 14px", borderTop: "1px solid #E5E7EB", background: "#FAFAFA", display: "flex", flexDirection: "column", gap: 4 }}>
                <div style={{ display: "flex", justifyContent: "space-between", fontSize: 13 }}>
                  <span style={{ color: "#6B7280" }}>Total</span>
                  <span style={{ fontWeight: 600, color: "#000", fontVariantNumeric: "tabular-nums" }}>{fmtMoney(o.total)}</span>
                </div>
                {o.paid > 0 && (
                  <div style={{ display: "flex", justifyContent: "space-between", fontSize: 13 }}>
                    <span style={{ color: "#6B7280" }}>Paid</span>
                    <span style={{ color: "#047857", fontVariantNumeric: "tabular-nums" }}>−{fmtMoney(o.paid)}</span>
                  </div>
                )}
                <div style={{ display: "flex", justifyContent: "space-between", fontSize: 14, marginTop: 4, paddingTop: 6, borderTop: "1px solid #E5E7EB" }}>
                  <span style={{ fontWeight: 700, color: "#000" }}>Outstanding</span>
                  <span style={{ fontWeight: 700, color: outstanding > 0 ? "#B91C1C" : "#000", fontVariantNumeric: "tabular-nums" }}>{fmtMoney(outstanding)}</span>
                </div>
              </div>
            </div>

            {/* Shipping + meta */}
            <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 12, marginBottom: 14 }}>
              <div style={{ border: "1px solid #E5E7EB", borderRadius: 10, padding: "12px 14px" }}>
                <div style={{ fontSize: 10, fontWeight: 700, color: "#9CA3AF", textTransform: "uppercase", letterSpacing: 0.06, marginBottom: 6 }}>Ship to</div>
                {addr ? (
                  <div style={{ fontSize: 13, color: "#000", lineHeight: 1.5 }}>
                    {[addr.name, addr.company].filter(Boolean).join(" · ") && <div style={{ fontWeight: 600 }}>{[addr.name, addr.company].filter(Boolean).join(" · ")}</div>}
                    {addr.address1 && <div>{addr.address1}</div>}
                    {addr.address2 && <div>{addr.address2}</div>}
                    {[addr.city, addr.province_code || addr.province, addr.zip].filter(Boolean).join(", ") && <div>{[addr.city, addr.province_code || addr.province, addr.zip].filter(Boolean).join(", ")}</div>}
                    {addr.country_code && <div style={{ color: "#6B7280" }}>{addr.country_code}</div>}
                  </div>
                ) : <div style={{ fontSize: 13, color: "#9CA3AF" }}>No shipping address on file.</div>}
              </div>
              <div style={{ border: "1px solid #E5E7EB", borderRadius: 10, padding: "12px 14px" }}>
                <div style={{ fontSize: 10, fontWeight: 700, color: "#9CA3AF", textTransform: "uppercase", letterSpacing: 0.06, marginBottom: 6 }}>Status</div>
                <div style={{ fontSize: 13, color: "#3F3F46", lineHeight: 1.6 }}>
                  <div>Fulfillment: <strong style={{ color: "#000" }}>{fulfillmentStatus || "unfulfilled"}</strong>{o.fulfilledAt && <> · {fmtDate(o.fulfilledAt)}</>}</div>
                  <div>Payment: <strong style={{ color: "#000" }}>{o.financialStatus || "pending"}</strong></div>
                  {o.sourceName && <div>Source: <strong style={{ color: "#000" }}>{o.sourceName}</strong></div>}
                  {o.note && <div style={{ marginTop: 6, padding: "8px 10px", background: "#F9FAFB", borderRadius: 6, fontStyle: "italic", color: "#3F3F46" }}>"{o.note}"</div>}
                </div>
              </div>
            </div>
          </>
        )}

        <div style={{ display: "flex", gap: 8, justifyContent: "flex-end", borderTop: "1px solid #F3F4F6", paddingTop: 14 }}>
          {(o?.id || row.rawId) && (
            <a
              href={`/api/orders/shopify/${encodeURIComponent(o?.id || row.rawId)}/invoice.pdf`}
              target="_blank" rel="noreferrer noopener"
              style={{ padding: "9px 14px", borderRadius: 8, border: "1px solid #E5E7EB", background: "#fff", color: "#000", fontSize: 13, fontWeight: 600, textDecoration: "none", display: "inline-flex", alignItems: "center", gap: 5 }}
            >
              <Download size={13} strokeWidth={2.2} /> Download PDF
            </a>
          )}
          {o?.adminUrl && (
            <a href={o.adminUrl} target="_blank" rel="noreferrer noopener" style={{ padding: "9px 14px", borderRadius: 8, border: "1px solid #E5E7EB", background: "#fff", color: "#000", fontSize: 13, fontWeight: 600, textDecoration: "none", display: "inline-flex", alignItems: "center", gap: 4 }}>
              Open in Shopify ↗
            </a>
          )}
          {outstanding > 0 && !isPaid && (
            <button onClick={onMarkPaid} disabled={busy} style={{ padding: "9px 16px", borderRadius: 8, border: "1px solid #000", background: busy ? "#D1D5DB" : "#000", color: "#fff", fontSize: 13, fontWeight: 600, cursor: busy ? "default" : "pointer", fontFamily: "Inter, sans-serif" }}>
              {busy ? "Marking…" : "Mark paid"}
            </button>
          )}
          <button onClick={onClose} style={{ padding: "9px 14px", borderRadius: 8, border: "1px solid #E5E7EB", background: "#fff", color: "#000", fontSize: 13, fontWeight: 600, cursor: "pointer", fontFamily: "Inter, sans-serif" }}>Close</button>
        </div>
      </div>
    </div>
  );
}

/* ── New customer modal ───────────────────────────────────────────────
   Manual create flow — used for customers who never came through
   Shopify (referrals, walk-in wholesale, transfer accounts). Only
   `name` is required; the rest is optional and editable later from
   the customer detail page. */
function NewClientModal({ defaults, onClose, onCreated }) {
  const [form, setForm] = useState({
    name: "", email: "", phone: "", company: "", address: "",
    paymentTerms: defaults?.paymentTerms || "Net 30",
    notes: "",
  });
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState("");

  const set = (k) => (e) => setForm(f => ({ ...f, [k]: e.target.value }));

  const submit = async () => {
    if (!form.name.trim()) { setError("Name is required."); return; }
    if (form.email && !/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(form.email.trim())) {
      setError("Email isn't valid.");
      return;
    }
    setBusy(true); setError("");
    try {
      const r = await fetch("/api/clients", {
        method: "POST", credentials: "same-origin",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({
          name: form.name.trim(),
          email: form.email.trim().toLowerCase() || null,
          phone: form.phone.trim() || null,
          company: form.company.trim() || null,
          address: form.address.trim() || null,
          paymentTerms: form.paymentTerms,
          notes: form.notes.trim() || null,
        }),
      });
      if (!r.ok) {
        const d = await r.json().catch(() => ({}));
        setError(d.error || "Couldn't create.");
        return;
      }
      onCreated && onCreated();
    } finally { setBusy(false); }
  };

  const fieldStyle = { width: "100%", padding: "9px 12px", borderRadius: 8, border: "1px solid #E5E7EB", fontSize: 14, fontFamily: "Inter, sans-serif", color: "#000", background: "#fff", outline: "none", boxSizing: "border-box" };
  const labelStyle = { fontSize: 11, fontWeight: 600, color: "#6B7280", textTransform: "uppercase", letterSpacing: "0.06em", marginBottom: 5, display: "block" };

  return (
    <div style={{ position: "fixed", inset: 0, zIndex: 1000, background: "rgba(0,0,0,0.55)", display: "flex", alignItems: "center", justifyContent: "center", padding: 24 }}>
      <div style={{ background: "#fff", borderRadius: 14, padding: "26px 28px", maxWidth: 500, width: "100%", boxShadow: "0 24px 64px rgba(0,0,0,0.28)", fontFamily: "Inter, sans-serif" }}>
        <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", marginBottom: 18 }}>
          <h2 style={{ margin: 0, fontSize: 18, fontWeight: 700, color: "#000" }}>New customer</h2>
          <button onClick={onClose} style={{ border: "none", background: "transparent", cursor: "pointer", color: "#6B7280", padding: 4 }}><X size={18} /></button>
        </div>
        <div style={{ display: "flex", flexDirection: "column", gap: 12 }}>
          <div>
            <label style={labelStyle}>Name *</label>
            <input style={fieldStyle} value={form.name} onChange={set("name")} placeholder="Roblox Coffee Co." autoFocus />
          </div>
          <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 10 }}>
            <div>
              <label style={labelStyle}>Email</label>
              <input style={fieldStyle} type="email" value={form.email} onChange={set("email")} placeholder="ap@roblox.com" />
            </div>
            <div>
              <label style={labelStyle}>Phone</label>
              <input style={fieldStyle} value={form.phone} onChange={set("phone")} placeholder="(415) 555-0101" />
            </div>
          </div>
          <div>
            <label style={labelStyle}>Company</label>
            <input style={fieldStyle} value={form.company} onChange={set("company")} placeholder="Roblox Coffee Co." />
          </div>
          <div>
            <label style={labelStyle}>Address</label>
            <input style={fieldStyle} value={form.address} onChange={set("address")} placeholder="123 Main St, San Francisco, CA, 94110" />
          </div>
          <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 10 }}>
            <div>
              <label style={labelStyle}>Payment terms</label>
              <select style={fieldStyle} value={form.paymentTerms} onChange={set("paymentTerms")}>
                <option>Net 0</option>
                <option>Net 7</option>
                <option>Net 15</option>
                <option>Net 30</option>
                <option>Net 45</option>
                <option>Net 60</option>
                <option>Due on receipt</option>
              </select>
            </div>
          </div>
          <div>
            <label style={labelStyle}>Notes</label>
            <textarea rows={3} style={{ ...fieldStyle, resize: "vertical", fontFamily: "Inter, sans-serif" }} value={form.notes} onChange={set("notes")} placeholder="Anything to remember about this customer." />
          </div>
        </div>
        {error && <div style={{ marginTop: 12, padding: "8px 12px", background: "#FEF2F2", border: "1px solid #FECACA", borderRadius: 6, color: "#991B1B", fontSize: 13 }}>{error}</div>}
        <div style={{ marginTop: 18, display: "flex", gap: 8, justifyContent: "flex-end" }}>
          <button onClick={onClose} disabled={busy} style={{ padding: "9px 16px", borderRadius: 8, border: "1px solid #E5E7EB", background: "#fff", color: "#000", fontSize: 13, fontWeight: 600, cursor: busy ? "default" : "pointer", fontFamily: "Inter, sans-serif" }}>Cancel</button>
          <button onClick={submit} disabled={busy} style={{ padding: "9px 18px", borderRadius: 8, border: "1px solid #000", background: "#000", color: "#fff", fontSize: 13, fontWeight: 600, cursor: busy ? "wait" : "pointer", fontFamily: "Inter, sans-serif" }}>
            {busy ? "Creating…" : "Create customer"}
          </button>
        </div>
      </div>
    </div>
  );
}


/* ── Recurring purchase orders ─────────────────────────────────────
   Rules that drop a draft PO into the workspace every N days. The
   server tick handles firing; this is just the operator UI. */
function RecurringView({ recurring, loading, onNew, onEdit, refresh }) {
  if (loading) return <div style={{ padding: 32, textAlign: "center", color: "#9CA3AF" }}>Loading…</div>;
  const fireNow = async (rule) => {
    if (!confirm(`Drop a draft PO from "${rule.name}" right now?`)) return;
    await fetch(`/api/purchasing/recurring/${rule.id}/fire-now`, { method: "POST", credentials: "same-origin" });
    refresh && refresh();
  };
  const togglePause = async (rule) => {
    await fetch(`/api/purchasing/recurring/${rule.id}`, {
      method: "PATCH",
      credentials: "same-origin",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify({ paused: !rule.paused }),
    });
    refresh && refresh();
  };
  const remove = async (rule) => {
    if (!confirm(`Delete the "${rule.name}" recurring rule? Already-created POs are kept.`)) return;
    await fetch(`/api/purchasing/recurring/${rule.id}`, { method: "DELETE", credentials: "same-origin" });
    refresh && refresh();
  };
  const fmtRel = (ts) => {
    if (!ts) return "—";
    const ms = ts - Date.now();
    const d = Math.round(ms / 86400000);
    if (d < -1) return `${Math.abs(d)}d ago`;
    if (d === -1) return "yesterday";
    if (d === 0) return "today";
    if (d === 1) return "tomorrow";
    return `in ${d}d`;
  };
  return (
    <div>
      <div style={{ display: "flex", justifyContent: "space-between", alignItems: "baseline", marginBottom: 14 }}>
        <h1 style={{ fontSize: 22, fontWeight: 700, color: "#000", margin: 0, letterSpacing: "-0.015em" }}>Recurring orders</h1>
        <button onClick={onNew} style={{ display: "inline-flex", alignItems: "center", gap: 6, padding: "8px 14px", borderRadius: 8, border: "none", background: "#000", color: "#fff", fontSize: 13, fontWeight: 600, cursor: "pointer", fontFamily: "Inter, sans-serif" }}>
          <Plus size={14} strokeWidth={2.2} /> New rule
        </button>
      </div>
      {recurring.length === 0 ? (
        <div style={{ background: "#FAFAFA", borderRadius: 12, padding: 48, textAlign: "center", color: "#9CA3AF", fontSize: 14, border: "1px dashed #E5E7EB" }}>
          No recurring rules yet. Set one up to drop a draft PO into your queue every N days.
        </div>
      ) : (
        <div style={{ background: "#fff", border: "1px solid #E5E7EB", borderRadius: 12, overflow: "hidden" }}>
          <div style={{ display: "grid", gridTemplateColumns: "1fr 160px 110px 110px 110px 130px", padding: "10px 16px", background: "#F9FAFB", fontSize: 10, fontWeight: 700, color: "#6B7280", textTransform: "uppercase", letterSpacing: "0.06em", borderBottom: "1px solid #E5E7EB" }}>
            <span>Rule</span><span>Vendor</span><span style={{ textAlign: "right" }}>Cadence</span><span>Next fire</span><span>Last fired</span><span style={{ textAlign: "right" }}>Actions</span>
          </div>
          {recurring.map(r => (
            <div
              key={r.id}
              onClick={() => onEdit(r)}
              onMouseEnter={(e) => e.currentTarget.style.background = "#FAFAFA"}
              onMouseLeave={(e) => e.currentTarget.style.background = "transparent"}
              style={{ display: "grid", gridTemplateColumns: "1fr 160px 110px 110px 110px 130px", alignItems: "center", padding: "12px 16px", borderTop: "1px solid #F3F4F6", cursor: "pointer", fontFamily: "Inter, sans-serif", opacity: r.paused ? 0.55 : 1 }}
            >
              <div style={{ minWidth: 0 }}>
                <div style={{ fontSize: 14, fontWeight: 600, color: "#000", display: "flex", alignItems: "center", gap: 8 }}>
                  {r.name}
                  {r.paused && <span style={{ fontSize: 10, fontWeight: 700, padding: "2px 7px", borderRadius: 99, background: "#F4F4F5", color: "#71717A", textTransform: "uppercase", letterSpacing: 0.4 }}>Paused</span>}
                </div>
                <div style={{ fontSize: 11, color: "#6B7280", marginTop: 1 }}>
                  {(r.lineItems || []).length} line item{(r.lineItems || []).length === 1 ? "" : "s"} · {(r.lineItems || []).filter(l => l.qtyVariable).length} variable
                </div>
              </div>
              <span style={{ fontSize: 13, color: "#000", overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>{r.vendorSnapshot?.name || "—"}</span>
              <span style={{ fontSize: 13, color: "#000", textAlign: "right", fontVariantNumeric: "tabular-nums" }}>every {r.everyDays}d</span>
              <span style={{ fontSize: 13, color: "#3F3F46" }}>{fmtRel(r.nextFireAt)}</span>
              <span style={{ fontSize: 12, color: "#6B7280" }}>{r.lastFiredAt ? new Date(r.lastFiredAt).toLocaleDateString("en-US", { month: "short", day: "numeric" }) : "—"}</span>
              <span style={{ display: "inline-flex", gap: 4, justifyContent: "flex-end" }} onClick={(e) => e.stopPropagation()}>
                <button title="Fire now" onClick={() => fireNow(r)} style={{ width: 28, height: 28, padding: 0, borderRadius: 6, border: "1px solid #E5E7EB", background: "#fff", color: "#0F766E", cursor: "pointer", display: "inline-flex", alignItems: "center", justifyContent: "center" }}><Zap size={13} strokeWidth={2} /></button>
                <button title={r.paused ? "Resume" : "Pause"} onClick={() => togglePause(r)} style={{ width: 28, height: 28, padding: 0, borderRadius: 6, border: "1px solid #E5E7EB", background: "#fff", color: "#52525B", cursor: "pointer", display: "inline-flex", alignItems: "center", justifyContent: "center" }}>{r.paused ? <Play size={13} strokeWidth={2} /> : <Pause size={13} strokeWidth={2} />}</button>
                <button title="Delete" onClick={() => remove(r)} style={{ width: 28, height: 28, padding: 0, borderRadius: 6, border: "1px solid #FECACA", background: "#fff", color: "#B91C1C", cursor: "pointer", display: "inline-flex", alignItems: "center", justifyContent: "center" }}><Trash2 size={13} strokeWidth={2} /></button>
              </span>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}

function RecurringModal({ rule, vendors, contracts = [], onClose, onSaved }) {
  const isNew = !rule;
  const [form, setForm] = useState(() => ({
    name: rule?.name || "",
    vendorId: rule?.vendorId || "",
    contractId: rule?.contractId || "",
    everyDays: rule?.everyDays || 7,
    paused: rule?.paused || false,
    notes: rule?.notes || "",
    taxPct: rule?.taxPct || 0,
    startAt: rule?.nextFireAt
      ? new Date(rule.nextFireAt).toISOString().slice(0, 10)
      : new Date(Date.now() + 7 * 86400000).toISOString().slice(0, 10),
    lineItems: rule?.lineItems?.length
      ? rule.lineItems.map(l => ({ ...l }))
      : [{ description: "", qty: 1, unit: "60kg bag", unitPrice: 0, qtyVariable: false }],
  }));
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState("");
  const setRow = (i, patch) => setForm(f => ({ ...f, lineItems: f.lineItems.map((l, j) => j === i ? { ...l, ...patch } : l) }));
  const addRow = () => setForm(f => ({ ...f, lineItems: [...f.lineItems, { description: "", qty: 1, unit: "60kg bag", unitPrice: 0, qtyVariable: false }] }));
  const removeRow = (i) => setForm(f => ({ ...f, lineItems: f.lineItems.filter((_, j) => j !== i) }));
  const validRows = form.lineItems.filter(l => l.description?.trim());
  const canSubmit = form.name.trim() && form.vendorId && validRows.length > 0;
  const eligibleContracts = (contracts || []).filter(c => c.vendorId === form.vendorId && (c.status || "active") === "active");
  const submit = async () => {
    setBusy(true); setErr("");
    try {
      const url = isNew ? "/api/purchasing/recurring" : `/api/purchasing/recurring/${rule.id}`;
      const method = isNew ? "POST" : "PATCH";
      const r = await fetch(url, {
        method,
        credentials: "same-origin",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({
          ...form,
          lineItems: validRows,
          startAt: isNew ? new Date(form.startAt + "T08:00:00").toISOString() : undefined,
        }),
      });
      const d = await r.json().catch(() => ({}));
      if (!r.ok) throw new Error(d.error || "Save failed");
      onSaved && onSaved();
    } catch (e) { setErr(e.message); }
    finally { setBusy(false); }
  };
  return (
    <ModalShell title={isNew ? "New recurring rule" : `Edit "${rule.name}"`} subtitle="Drops a draft PO every N days." onClose={onClose}>
      {err && <div style={{ marginBottom: 12, padding: "8px 12px", background: "#FEF2F2", border: "1px solid #FECACA", borderRadius: 8, color: "#991B1B", fontSize: 13 }}>{err}</div>}
      <Field label="Rule name *">
        <input style={fieldInput} value={form.name} onChange={e => setForm(f => ({ ...f, name: e.target.value }))} placeholder="Weekly Brazil reload" autoFocus />
      </Field>
      <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr 1fr", gap: 10 }}>
        <Field label="Vendor *">
          <select style={fieldInput} value={form.vendorId} onChange={e => setForm(f => ({ ...f, vendorId: e.target.value, contractId: "" }))}>
            <option value="">— pick a vendor —</option>
            {vendors.map(v => <option key={v.id} value={v.id}>{v.name}</option>)}
          </select>
        </Field>
        <Field label="Every N days *">
          <input type="number" min="1" max="365" step="1" style={fieldInput} value={form.everyDays} onChange={e => setForm(f => ({ ...f, everyDays: Math.max(1, Math.min(365, Number(e.target.value) || 1)) }))} />
        </Field>
        <Field label={isNew ? "First fire (date)" : "Next fire (date)"}>
          <input type="date" style={fieldInput} value={form.startAt} onChange={e => setForm(f => ({ ...f, startAt: e.target.value }))} />
        </Field>
      </div>
      {eligibleContracts.length > 0 && (
        <Field label="Draw down from contract (optional)">
          <select style={fieldInput} value={form.contractId} onChange={e => setForm(f => ({ ...f, contractId: e.target.value }))}>
            <option value="">— none —</option>
            {eligibleContracts.map(c => {
              const remaining = c.bagsContracted != null ? Math.max(0, Number(c.bagsContracted) - (Number(c.bagsReleased) || 0)) : null;
              const label = [c.contractNumber || c.id.slice(-6), c.coffee, remaining != null ? `${remaining} bags left` : null].filter(Boolean).join(" · ");
              return <option key={c.id} value={c.id}>{label}</option>;
            })}
          </select>
        </Field>
      )}

      <Field label="Line items">
        <div style={{ background: "#F9FAFB", border: "1px solid #E5E7EB", borderRadius: 8, padding: 8 }}>
          <div style={{ display: "grid", gridTemplateColumns: "1fr 80px 110px 100px 70px 28px", gap: 6, padding: "0 4px 6px", fontSize: 10, fontWeight: 700, color: "#6B7280", textTransform: "uppercase", letterSpacing: "0.06em" }}>
            <span>Description</span><span style={{ textAlign: "right" }}>Qty</span><span>Unit</span><span style={{ textAlign: "right" }}>$ / unit</span><span style={{ textAlign: "center" }}>Variable</span><span></span>
          </div>
          {form.lineItems.map((l, i) => (
            <div key={i} style={{ display: "grid", gridTemplateColumns: "1fr 80px 110px 100px 70px 28px", gap: 6, alignItems: "center", marginBottom: 4 }}>
              <input style={lineInput} value={l.description} onChange={e => setRow(i, { description: e.target.value })} placeholder="Brazil Cerrado natural" />
              <input type="number" min="0" step="0.5" style={{ ...lineInput, textAlign: "right" }} value={l.qty} onChange={e => setRow(i, { qty: Number(e.target.value) || 0 })} />
              <input style={lineInput} value={l.unit} onChange={e => setRow(i, { unit: e.target.value })} placeholder="60kg bag" />
              <input type="number" min="0" step="0.01" style={{ ...lineInput, textAlign: "right" }} value={l.unitPrice} onChange={e => setRow(i, { unitPrice: Number(e.target.value) || 0 })} />
              <label title="Mark this line as variable — operator should review qty before sending each draft." style={{ display: "inline-flex", justifyContent: "center", alignItems: "center", cursor: "pointer" }}>
                <input type="checkbox" checked={!!l.qtyVariable} onChange={e => setRow(i, { qtyVariable: e.target.checked })} style={{ accentColor: "#000" }} />
              </label>
              <button onClick={() => removeRow(i)} disabled={form.lineItems.length <= 1} style={{ width: 24, height: 24, padding: 0, borderRadius: 6, border: "none", background: "transparent", color: form.lineItems.length <= 1 ? "#D1D5DB" : "#9CA3AF", cursor: form.lineItems.length <= 1 ? "default" : "pointer", fontSize: 14, lineHeight: 1 }}>×</button>
            </div>
          ))}
          <button onClick={addRow} style={{ marginTop: 6, padding: "5px 10px", borderRadius: 6, border: "1px solid #E5E7EB", background: "#fff", color: "#000", fontSize: 11, fontWeight: 500, cursor: "pointer", fontFamily: "Inter, sans-serif", display: "inline-flex", alignItems: "center", gap: 4 }}>
            <Plus size={11} strokeWidth={2} /> Add row
          </button>
        </div>
      </Field>

      <Field label="Notes (added to every generated PO)" hint="e.g. quantities are typical — confirm with vendor before sending.">
        <textarea rows={2} style={{ ...fieldInput, resize: "vertical", minHeight: 56 }} value={form.notes} onChange={e => setForm(f => ({ ...f, notes: e.target.value }))} />
      </Field>

      <div style={{ display: "flex", justifyContent: "flex-end", gap: 8, marginTop: 14 }}>
        <button onClick={onClose} disabled={busy} style={{ padding: "9px 14px", borderRadius: 8, border: "1px solid #E5E7EB", background: "#fff", color: "#000", fontSize: 13, fontWeight: 500, cursor: "pointer", fontFamily: "Inter, sans-serif" }}>Cancel</button>
        <button onClick={submit} disabled={!canSubmit || busy} style={{ padding: "9px 18px", borderRadius: 8, border: "none", background: !canSubmit || busy ? "#D1D5DB" : "#000", color: "#fff", fontSize: 13, fontWeight: 600, cursor: !canSubmit || busy ? "default" : "pointer", fontFamily: "Inter, sans-serif" }}>
          {busy ? "Saving…" : (isNew ? "Create rule" : "Save changes")}
        </button>
      </div>
    </ModalShell>
  );
}

/* ── Customer detail / drill-in ───────────────────────────────────
   Per-client list of every Shopify order + every local invoice.
   Each row is "mark paid" (records a local payment) / "unpaid"
   (undo). Recomputes totals on every action. */
function ClientDetailView({ client, onBack, onUpdated, defaults, patchClientLocal }) {
  const [data, setData] = useState(null);
  const [busy, setBusy] = useState(null); // composite order id being mutated
  const [sort, setSort] = usePersistentSort("client-orders-sort", { key: "date", dir: "desc" });
  const [search, setSearch] = useState("");
  const [emailModalOpen, setEmailModalOpen] = useState(false);
  const [pdfBusy, setPdfBusy] = useState(false);
  // Order list filter: "unpaid" (default) shows only outstanding rows;
  // "all" surfaces the customer's full order history.
  const [viewFilter, setViewFilter] = useState("unpaid");

  const refresh = async () => {
    try {
      const r = await fetch(`/api/clients/${encodeURIComponent(client.id)}/orders`, { credentials: "same-origin" });
      if (r.ok) setData(await r.json());
      else setData({ orders: [], totals: { unpaid: 0, overdue: 0 } });
    } catch { setData({ orders: [], totals: { unpaid: 0, overdue: 0 } }); }
  };
  React.useEffect(() => { refresh(); }, [client.id]); // eslint-disable-line

  const togglePaid = async (o) => {
    // Optimistic flip — update the row in local state immediately so
    // the UI doesn't yank under the operator while the request is in
    // flight. Re-derive totals from the new orders array. Server
    // failure rolls back via authoritative refresh.
    const wasPaid = !!o.paidLocally;
    const nowPaid = !wasPaid;
    setBusy(o.id);
    setData(prev => {
      if (!prev) return prev;
      const orders = (prev.orders || []).map(x => {
        if (x.id !== o.id) return x;
        const next = { ...x, paidLocally: nowPaid, paidLocallyAt: nowPaid ? new Date().toISOString() : null };
        // If we just paid it, it's no longer unpaid/overdue.
        // If we un-paid it, restore the original unpaid/overdue derivation.
        if (nowPaid) {
          next.unpaid = false;
          next.overdue = false;
        } else {
          // Best-effort restore — re-derive from the other fields the
          // server returned. Outstanding > 0 + a real fulfillment date
          // means it's unpaid again; overdue derives from dueDate.
          const out = Number(next.outstanding) || 0;
          const isFulfilled = next.fulfillmentStatus === "fulfilled" || next.fulfillmentStatus === "partial" || !!next.fulfilledAt;
          next.unpaid = out > 0 && isFulfilled && !next.canceled;
          const dueMs = next.dueDate ? new Date(next.dueDate).getTime() : NaN;
          next.overdue = next.unpaid && Number.isFinite(dueMs) && dueMs < Date.now();
        }
        return next;
      });
      const totals = {
        unpaid:  +orders.filter(x => x.unpaid).reduce((s, x) => s + (Number(x.outstanding) || 0), 0).toFixed(2),
        overdue: +orders.filter(x => x.overdue).reduce((s, x) => s + (Number(x.outstanding) || 0), 0).toFixed(2),
      };
      return { ...prev, orders, totals };
    });
    try {
      const r = await fetch(`/api/clients/${encodeURIComponent(client.id)}/orders/${encodeURIComponent(o.id)}/mark-paid`, {
        method: "POST",
        credentials: "same-origin",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ unpaid: wasPaid }),
      });
      if (!r.ok) {
        // Reconcile to truth — also lets the parent's totals catch up.
        await refresh();
        return;
      }
      // Keep the parent's customer-list totals in sync WITHOUT triggering
      // a full /api/purchasing/* refetch (which would flash a loading
      // state and re-render the world). Subtract the just-paid order's
      // outstanding from totalDue / totalOverdue locally; the next real
      // refresh will reconcile against the server. If the customer just
      // dropped to $0 due, also splice the order from the persisted
      // shopifyUnpaidOrders snapshot so subsequent renders match.
      if (patchClientLocal) {
        const out = Number(o.outstanding) || 0;
        const wasOverdue = !!o.overdue;
        patchClientLocal(client.id, (c) => {
          const list = Array.isArray(c.shopifyUnpaidOrders) ? c.shopifyUnpaidOrders : [];
          const rawId = String(o.rawId || o.id || "").replace(/^shopify:/, "");
          const nextList = nowPaid
            ? list.filter(x => String(x.id || "") !== rawId)
            : list;
          const due = Math.max(0, (Number(c.totalDue) || 0) + (nowPaid ? -out : out));
          const ovd = Math.max(0, (Number(c.totalOverdue) || 0) + (nowPaid ? (wasOverdue ? -out : 0) : (wasOverdue ? out : 0)));
          return {
            totalDue: +due.toFixed(2),
            totalOverdue: +ovd.toFixed(2),
            shopifyUnpaidOrders: nextList,
            shopifyUnpaidOrdersCount: nextList.length,
          };
        });
      }
    } finally { setBusy(null); }
  };

  // Hooks must run in the same order on every render — keep all
  // useMemo / useEffect calls ABOVE any conditional returns.
  const tokens = useMemo(() => (
    search.split(",").map(t => t.trim().toLowerCase().replace(/^#/, "")).filter(Boolean)
  ), [search]);
  const orders = useMemo(() => {
    const all = (data && data.orders) || [];
    const scoped = viewFilter === "unpaid"
      ? all.filter(o => o.unpaid && !o.paidLocally)
      : all;
    if (tokens.length === 0) return scoped;
    return scoped.filter(o => {
      const num = String(o.number || "").toLowerCase().replace(/^#/, "");
      return tokens.some(t => num.includes(t));
    });
  }, [data, tokens, viewFilter]);

  // ── Monthly behaviour rollup for the three sparkline tiles. Always
  // computed over the FULL order set (ignore the unpaid/all toggle so
  // the tiles reflect real customer activity, not the table filter).
  const behaviourSeries = useMemo(() => {
    const all = (data && data.orders) || [];
    const now = new Date();
    const months = [];
    for (let i = 11; i >= 0; i--) {
      const d = new Date(now.getFullYear(), now.getMonth() - i, 1);
      months.push({
        key: `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, "0")}`,
        label: d.toLocaleDateString("en-US", { month: "short" }),
        count: 0, lb: 0, dollars: 0,
      });
    }
    const byKey = new Map(months.map(m => [m.key, m]));
    const G_TO_LB = 1 / 453.59237;
    // Fallback: parse weight from variant title / sku / name when the
    // variant.grams field is missing on Shopify. Common roastery
    // formats: "5 lb", "5lb", "12oz", "2.2 kg", "340 g".
    const parseLbFromText = (s) => {
      if (!s) return 0;
      const t = String(s).toLowerCase();
      let m;
      if ((m = t.match(/(\d+(?:\.\d+)?)\s*lb\b/)))  return parseFloat(m[1]);
      if ((m = t.match(/(\d+(?:\.\d+)?)\s*lbs?\b/))) return parseFloat(m[1]);
      if ((m = t.match(/(\d+(?:\.\d+)?)\s*oz\b/)))  return parseFloat(m[1]) / 16;
      if ((m = t.match(/(\d+(?:\.\d+)?)\s*kg\b/)))  return parseFloat(m[1]) * 2.20462;
      if ((m = t.match(/(\d+(?:\.\d+)?)\s*g\b/)))   return parseFloat(m[1]) * G_TO_LB;
      return 0;
    };
    for (const o of all) {
      const dt = o.date ? new Date(o.date) : null;
      if (!dt || isNaN(dt)) continue;
      const k = `${dt.getFullYear()}-${String(dt.getMonth() + 1).padStart(2, "0")}`;
      const m = byKey.get(k);
      if (!m) continue;
      m.count += 1;
      m.dollars += Number(o.total) || 0;
      const lis = Array.isArray(o.lineItems) ? o.lineItems : [];
      for (const li of lis) {
        const q = Number(li.quantity || li.qty) || 0;
        const g = Number(li.grams) || 0;
        // Prefer the structured grams field; fall back to parsing
        // weight out of variant / sku / name when it's missing.
        const lbPerUnit = g > 0
          ? g * G_TO_LB
          : (parseLbFromText(li.variant) || parseLbFromText(li.sku) || parseLbFromText(li.name));
        m.lb += q * lbPerUnit;
      }
    }
    // Trend = last full month vs avg of the 3 before it.
    const trendOf = (vals) => {
      if (vals.length < 4) return null;
      const last = vals[vals.length - 1];
      const prev3 = vals.slice(-4, -1);
      const avg = prev3.reduce((s, v) => s + v, 0) / prev3.length;
      if (avg <= 0 && last <= 0) return null;
      if (avg <= 0) return { dir: "up", pct: 100 };
      const pct = ((last - avg) / avg) * 100;
      return { dir: pct >= 0 ? "up" : "down", pct: Math.abs(pct).toFixed(0) };
    };
    const counts  = months.map(m => m.count);
    const lbs     = months.map(m => +m.lb.toFixed(1));
    const dollars = months.map(m => +m.dollars.toFixed(0));
    return {
      months: months.map(m => m.label),
      count:   { series: counts,  total: counts.reduce((s, v) => s + v, 0),  trend: trendOf(counts) },
      lb:      { series: lbs,     total: lbs.reduce((s, v) => s + v, 0),     trend: trendOf(lbs) },
      dollars: { series: dollars, total: dollars.reduce((s, v) => s + v, 0), trend: trendOf(dollars) },
    };
  }, [data]);
  const sorted = useMemo(() => {
    const sortKey = sort.key;
    const dir = sort.dir === "asc" ? 1 : -1;
    const get = (o) => {
      switch (sortKey) {
        case "number":  return (o.number || "").toString();
        case "date":    return o.date || "";
        case "total":   return Number(o.total) || 0;
        case "outstanding": return Number(o.outstanding) || 0;
        case "status":  return o.paidLocally ? "paid" : (o.unpaid ? (o.overdue ? "overdue" : "unpaid") : "—");
        case "kind":    return o.kind || "";
        default: return 0;
      }
    };
    const decorated = orders.map((o, i) => ({ o, k: get(o), i }));
    decorated.sort((a, b) => {
      if (a.k === b.k) return a.i - b.i;
      return (a.k < b.k ? -1 : 1) * dir;
    });
    return decorated.map(d => d.o);
  }, [orders, sort]);

  // ── Conditional renders ONLY below this line ──
  if (!data) return (
    <div>
      <BackBar onBack={onBack} client={client} />
      <div style={{ padding: 32, textAlign: "center", color: "#9CA3AF" }}>Loading orders…</div>
    </div>
  );

  // CSV export of the visible (sorted/filtered) order list.
  // Bulk-download every visible Shopify-order invoice as ONE merged
  // PDF. Same renderer the emailed statements use; sequenced server-
  // side so Shopify's rate limit doesn't trip on big customers.
  const downloadInvoicesPdf = async () => {
    const targets = sorted.filter(o => o.kind === "shopify" && o.rawId);
    if (targets.length === 0 || pdfBusy) return;
    setPdfBusy(true);
    try {
      const r = await fetch(`/api/clients/${encodeURIComponent(client.id)}/invoices.pdf`, {
        method: "POST", credentials: "same-origin",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ orderIds: targets.map(o => o.rawId) }),
      });
      if (!r.ok) {
        const txt = await r.text().catch(() => "");
        throw new Error(txt || `HTTP ${r.status}`);
      }
      const blob = await r.blob();
      const url = URL.createObjectURL(blob);
      const a = document.createElement("a");
      const safeName = (client.company || client.name || "customer").replace(/[^a-z0-9]+/gi, "-").toLowerCase();
      a.href = url;
      a.download = `${safeName}-invoices.pdf`;
      document.body.appendChild(a);
      a.click();
      a.remove();
      setTimeout(() => URL.revokeObjectURL(url), 1000);
    } catch (err) {
      console.error("Download Invoices failed:", err.message);
      window.alert(`Couldn't build the invoice bundle: ${err.message}`);
    } finally {
      setPdfBusy(false);
    }
  };

  const exportCsv = () => {
    const rows = [
      ["Order #", "Date", "Total", "Outstanding", "Status", "Fulfillment", "Fulfilled at", "Admin URL"],
      ...sorted.map(o => [
        o.number || "",
        o.date ? new Date(o.date).toISOString().slice(0, 10) : "",
        Number(o.total) || 0,
        Number(o.outstanding) || 0,
        o.paidLocally ? "Paid" : (o.overdue ? "Overdue" : (o.unpaid ? "Unpaid" : (o.financialStatus || ""))),
        o.fulfillmentStatus || "",
        o.fulfilledAt ? new Date(o.fulfilledAt).toISOString().slice(0, 10) : "",
        o.adminUrl || "",
      ]),
    ];
    const escape = (v) => {
      const s = v == null ? "" : String(v);
      return /[",\n]/.test(s) ? `"${s.replace(/"/g, '""')}"` : s;
    };
    const csv = rows.map(r => r.map(escape).join(",")).join("\n");
    // BOM so Excel detects UTF-8 cleanly. Save as .csv (Excel opens
    // natively); we'll layer xlsx + email-zip work in a follow-up.
    const blob = new Blob(["﻿" + csv], { type: "text/csv;charset=utf-8;" });
    const url = URL.createObjectURL(blob);
    const a = document.createElement("a");
    const safeName = (client.name || "customer").replace(/[^a-z0-9]+/gi, "-").toLowerCase();
    a.href = url;
    a.download = `${safeName}-orders.csv`;
    document.body.appendChild(a);
    a.click();
    a.remove();
    setTimeout(() => URL.revokeObjectURL(url), 0);
  };

  // Net terms dropdown — per-client override of the workspace
  // default. Stored on the client.paymentTerms field via PATCH.
  const NET_TERMS = ["Net 0", "Net 15", "Net 30", "Net 45", "Net 60", "Net 90"];
  const currentTerms = client.paymentTerms || defaults?.paymentTerms || "Net 30";
  const setTerms = async (value) => {
    const body = value === "__default__" ? { paymentTerms: null } : { paymentTerms: value };
    await fetch(`/api/clients/${encodeURIComponent(client.id)}`, {
      method: "PATCH",
      credentials: "same-origin",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify(body),
    });
    onUpdated && onUpdated();
  };

  const today = new Date().toISOString().slice(0, 10);

  const STATUS_PILL = (o) => {
    if (o.paidLocally) return { bg: "#ECFDF5", fg: "#047857", border: "#A7F3D0", label: "Paid" };
    if (o.overdue)     return { bg: "#FEF2F2", fg: "#B91C1C", border: "#FECACA", label: "Overdue" };
    if (o.unpaid)      return { bg: "#FEF3C7", fg: "#92400E", border: "#FDE68A", label: "Unpaid" };
    // Pending shipment → show as Unfulfilled. Financial-status
    // "pending" on a wholesale Net order just means "no payment
    // captured yet"; combined with unfulfilled, the actionable
    // status is Unfulfilled, not Pending.
    if (o.pendingShipment) return { bg: "#F4F4F5", fg: "#52525B", border: "#E4E4E7", label: "Unfulfilled" };
    if (o.kind === "shopify" && o.financialStatus === "paid") return { bg: "#F4F4F5", fg: "#52525B", border: "#E4E4E7", label: "Paid (Shopify)" };
    // Fall-through: normalize "pending" → "Unfulfilled" defensively.
    const fs = String(o.financialStatus || "").toLowerCase();
    if (fs === "pending") return { bg: "#F4F4F5", fg: "#52525B", border: "#E4E4E7", label: "Unfulfilled" };
    return { bg: "#F4F4F5", fg: "#52525B", border: "#E4E4E7", label: o.financialStatus || "—" };
  };

  const totals = data.totals || { unpaid: 0, overdue: 0 };

  return (
    <div>
      <BackBar
        onBack={onBack}
        client={client}
        actions={
          <>
            <select
              value={client.paymentTerms || "__default__"}
              onChange={(e) => setTerms(e.target.value)}
              title="Override the workspace default payment terms for this customer"
              style={{ padding: "6px 10px", borderRadius: 8, border: "1px solid #E5E7EB", background: "#fff", fontSize: 12, fontWeight: 500, color: "#000", fontFamily: "Inter, sans-serif", cursor: "pointer" }}
            >
              <option value="__default__">{`${defaults?.paymentTerms || "Net 30"} (default)`}</option>
              {NET_TERMS.map(t => <option key={t} value={t}>{t}</option>)}
            </select>
            <button onClick={exportCsv} disabled={sorted.length === 0} style={{ display: "inline-flex", alignItems: "center", gap: 6, padding: "6px 12px", borderRadius: 8, border: "1px solid #E5E7EB", background: "#fff", color: "#000", fontSize: 12, fontWeight: 600, cursor: sorted.length === 0 ? "default" : "pointer", fontFamily: "Inter, sans-serif" }}>
              <Download size={12} strokeWidth={2.2} /> Export CSV
            </button>
            <button
              onClick={downloadInvoicesPdf}
              disabled={pdfBusy || sorted.filter(o => o.kind === "shopify" && o.rawId).length === 0}
              title={sorted.filter(o => o.kind === "shopify" && o.rawId).length === 0 ? "No Shopify orders to export" : "Download all visible invoices as one PDF"}
              style={{
                display: "inline-flex", alignItems: "center", gap: 6,
                padding: "6px 12px", borderRadius: 8,
                border: "1px solid #E5E7EB", background: "#fff", color: "#000",
                fontSize: 12, fontWeight: 600,
                cursor: (pdfBusy || sorted.filter(o => o.kind === "shopify" && o.rawId).length === 0) ? "default" : "pointer",
                fontFamily: "Inter, sans-serif",
                opacity: (pdfBusy || sorted.filter(o => o.kind === "shopify" && o.rawId).length === 0) ? 0.55 : 1,
              }}
            >
              <Download size={12} strokeWidth={2.2} /> {pdfBusy ? "Building…" : "Download Invoices"}
            </button>
            <button
              onClick={() => setEmailModalOpen(true)}
              disabled={!client.email || (data?.totals?.unpaid || 0) <= 0}
              title={!client.email ? "Customer has no email on file" : ((data?.totals?.unpaid || 0) <= 0 ? "Nothing outstanding" : "Send a statement email")}
              style={{ display: "inline-flex", alignItems: "center", gap: 6, padding: "6px 12px", borderRadius: 8, border: "1px solid #000", background: !client.email || (data?.totals?.unpaid || 0) <= 0 ? "#F4F4F5" : "#000", color: !client.email || (data?.totals?.unpaid || 0) <= 0 ? "#9CA3AF" : "#fff", fontSize: 12, fontWeight: 600, cursor: !client.email || (data?.totals?.unpaid || 0) <= 0 ? "default" : "pointer", fontFamily: "Inter, sans-serif" }}
            >
              <Mail size={12} strokeWidth={2.2} /> Email statement
            </button>
          </>
        }
      />
      {emailModalOpen && (
        <EmailStatementModal
          client={client}
          orders={(data?.orders || []).filter(o => o.unpaid).map(o => ({
            number: o.number, date: o.date, total: o.total, outstanding: o.outstanding,
            adminUrl: o.adminUrl, orderStatusUrl: o.orderStatusUrl,
            fulfillmentStatus: o.fulfillmentStatus, fulfilledAt: o.fulfilledAt,
            rawId: o.rawId, id: o.id,
            overdue: !!o.overdue,
            lineItems: Array.isArray(o.lineItems) ? o.lineItems : [],
          }))}
          totals={data?.totals || { unpaid: 0, overdue: 0 }}
          onClose={() => setEmailModalOpen(false)}
        />
      )}

      {/* Associated emails — primary + alts. Inbound matching also
          checks alts, so e.g. members of a customer's shared Google
          Group can be recognized when they email from their own
          personal addresses. */}
      <ClientEmailsCard client={client} onUpdated={onUpdated} patchClientLocal={patchClientLocal} />

      {/* Top stats — scoped to this customer only. */}
      <div style={{ fontSize: 11, fontWeight: 700, color: "#9CA3AF", textTransform: "uppercase", letterSpacing: "0.07em", marginBottom: 6 }}>
        For {client.name}
      </div>
      <div style={{ display: "grid", gridTemplateColumns: "repeat(4, 1fr)", gap: 8, marginBottom: 14 }}>
        {[
          { l: "Orders & invoices", v: (data.orders || []).length },
          { l: "Total due",  v: fmtMoney(totals.unpaid),  color: totals.unpaid > 0 ? "#000" : "#9CA3AF" },
          { l: "Overdue",    v: fmtMoney(totals.overdue), color: totals.overdue > 0 ? "#B91C1C" : "#9CA3AF" },
          { l: "Terms",      v: client.paymentTerms || `${defaults?.paymentTerms || "Net 30"} (default)` },
        ].map(s => (
          <div key={s.l} style={{ background: "#fff", border: "1px solid #E5E7EB", borderRadius: 10, padding: "10px 14px" }}>
            <div style={{ fontSize: 10, fontWeight: 700, color: "#9CA3AF", textTransform: "uppercase", letterSpacing: "0.06em" }}>{s.l}</div>
            <div style={{ fontSize: 18, fontWeight: 700, color: s.color || "#000", marginTop: 2, fontVariantNumeric: "tabular-nums" }}>{s.v}</div>
          </div>
        ))}
      </div>

      {/* Customer behaviour — 12-month rollup of orders / lb roasted / $. */}
      <div style={{ display: "grid", gridTemplateColumns: "repeat(3, 1fr)", gap: 8, marginBottom: 14 }}>
        <StatTile
          label="Orders"
          value={behaviourSeries.count.total.toLocaleString()}
          series={behaviourSeries.count.series}
          months={behaviourSeries.months}
          trend={behaviourSeries.count.trend}
          unit="orders"
        />
        <StatTile
          label="Volume (lb)"
          value={behaviourSeries.lb.total.toLocaleString(undefined, { maximumFractionDigits: 0 })}
          series={behaviourSeries.lb.series}
          months={behaviourSeries.months}
          trend={behaviourSeries.lb.trend}
          unit="lb"
        />
        <StatTile
          label="Revenue"
          value={fmtMoney(behaviourSeries.dollars.total)}
          series={behaviourSeries.dollars.series}
          months={behaviourSeries.months}
          trend={behaviourSeries.dollars.trend}
          money
        />
      </div>

      {/* Order list filter + search. */}
      <div style={{ display: "flex", alignItems: "center", gap: 10, marginBottom: 10 }}>
        <div style={{ display: "inline-flex", border: "1px solid #E5E7EB", borderRadius: 8, padding: 2, background: "#fff" }}>
          {[
            { v: "unpaid", label: "Unpaid" },
            { v: "all",    label: "All" },
          ].map(opt => (
            <button
              key={opt.v}
              onClick={() => setViewFilter(opt.v)}
              style={{
                padding: "5px 12px", borderRadius: 6, border: "none",
                background: viewFilter === opt.v ? "#000" : "transparent",
                color: viewFilter === opt.v ? "#fff" : "#52525B",
                fontSize: 12, fontWeight: 600, fontFamily: "Inter, sans-serif",
                cursor: "pointer",
              }}
            >{opt.label}</button>
          ))}
        </div>
        <input
          type="text"
          value={search}
          onChange={(e) => setSearch(e.target.value)}
          placeholder="Search by order number — e.g. 1234 or 1234, 1280, 1295"
          style={{ padding: "8px 12px", borderRadius: 8, border: "1px solid #E5E7EB", fontSize: 13, fontFamily: "Inter, sans-serif", background: "#fff", outline: "none", flex: 1, boxSizing: "border-box" }}
        />
        {tokens.length > 0 && (
          <span style={{ fontSize: 12, color: "#6B7280", whiteSpace: "nowrap" }}>
            {sorted.length} match{sorted.length === 1 ? "" : "es"} for {tokens.length} term{tokens.length === 1 ? "" : "s"}
          </span>
        )}
      </div>

      {/* Order/invoice list */}
      {sorted.length === 0 ? (
        <div style={{ background: "#FAFAFA", borderRadius: 12, padding: 48, textAlign: "center", color: "#9CA3AF", fontSize: 14, border: "1px dashed #E5E7EB" }}>
          {tokens.length === 0
            ? "No orders or invoices for this customer."
            : `No orders match ${tokens.length === 1 ? `"${tokens[0]}"` : `any of ${tokens.length} search terms`}.`}
        </div>
      ) : (
        <div style={{ background: "#fff", border: "1px solid #E5E7EB", borderRadius: 12, overflow: "hidden" }}>
          <div style={{ display: "grid", gridTemplateColumns: "90px 120px 110px 110px 120px 1fr 100px 64px", columnGap: 10, padding: "10px 16px", background: "#F9FAFB", borderBottom: "1px solid #E5E7EB" }}>
            <SortHead k="kind"   label="Kind" sort={sort} setSort={setSort} />
            <SortHead k="number" label="Number" sort={sort} setSort={setSort} />
            <SortHead k="date"   label="Date" sort={sort} setSort={setSort} />
            <SortHead k="total"  label="Total" align="right" sort={sort} setSort={setSort} />
            <SortHead k="outstanding" label="Outstanding" align="right" sort={sort} setSort={setSort} />
            <SortHead k="status" label="Status & fulfillment" sort={sort} setSort={setSort} />
            <span></span>
            <span></span>
          </div>
          {sorted.map(o => {
            const tint = STATUS_PILL(o);
            const fulfilledLabel = o.fulfillmentStatus === "fulfilled" && o.fulfilledAt
              ? `Fulfilled ${new Date(o.fulfilledAt).toLocaleDateString("en-US", { month: "short", day: "numeric", year: "numeric" })}`
              : o.fulfillmentStatus === "partial"
                ? "Partially fulfilled"
                : null;
            return (
              <div key={o.id} style={{ display: "grid", gridTemplateColumns: "90px 120px 110px 110px 120px 1fr 100px 64px", columnGap: 10, alignItems: "center", padding: "12px 16px", borderTop: "1px solid #F3F4F6", fontFamily: "Inter, sans-serif" }}>
                <span style={{ fontSize: 11, fontWeight: 600, color: "#71717A", textTransform: "uppercase", letterSpacing: "0.05em" }}>{o.kind === "shopify" ? "Shopify" : "Invoice"}</span>
                <span style={{ fontSize: 13, fontWeight: 600, color: "#000", fontVariantNumeric: "tabular-nums", whiteSpace: "nowrap", overflow: "hidden", textOverflow: "ellipsis" }}>{o.number}</span>
                <span style={{ fontSize: 12, color: "#6B7280", fontVariantNumeric: "tabular-nums" }}>{o.date ? new Date(o.date).toLocaleDateString("en-US", { month: "short", day: "numeric", year: "numeric" }) : "—"}</span>
                <span style={{ fontSize: 13, color: "#3F3F46", textAlign: "right", fontVariantNumeric: "tabular-nums" }}>{fmtMoney(o.total)}</span>
                <span style={{ fontSize: 13, fontWeight: 700, color: o.outstanding > 0 ? "#000" : "#9CA3AF", textAlign: "right", fontVariantNumeric: "tabular-nums" }}>
                  {o.outstanding > 0 ? fmtMoney(o.outstanding) : "—"}
                </span>
                <span style={{ minWidth: 0 }}>
                  <span style={{ display: "inline-flex", alignItems: "center", gap: 8, flexWrap: "wrap" }}>
                    <span style={{ display: "inline-block", padding: "3px 9px", borderRadius: 99, fontSize: 10, fontWeight: 700, letterSpacing: 0.4, textTransform: "uppercase", background: tint.bg, color: tint.fg, border: `1px solid ${tint.border}`, whiteSpace: "nowrap" }}>{tint.label}</span>
                    {fulfilledLabel && (
                      <span style={{ fontSize: 11, color: "#047857", fontWeight: 600, whiteSpace: "nowrap" }}>{fulfilledLabel}</span>
                    )}
                    {o.paidLocally && o.paidLocallyAt && (
                      <span style={{ fontSize: 11, color: "#9CA3AF" }}>paid {new Date(o.paidLocallyAt).toLocaleDateString("en-US", { month: "short", day: "numeric" })}</span>
                    )}
                  </span>
                </span>
                <span style={{ textAlign: "right" }}>
                  {o.unpaid && (
                    <button onClick={() => togglePaid(o)} disabled={busy === o.id} style={{ padding: "5px 11px", borderRadius: 6, border: "none", background: busy === o.id ? "#D1D5DB" : "#000", color: "#fff", fontSize: 12, fontWeight: 600, cursor: busy === o.id ? "default" : "pointer", fontFamily: "Inter, sans-serif" }}>
                      {busy === o.id ? "…" : "Mark paid"}
                    </button>
                  )}
                  {o.paidLocally && (
                    <button onClick={() => togglePaid(o)} disabled={busy === o.id} style={{ padding: "5px 11px", borderRadius: 6, border: "1px solid #E5E7EB", background: "#fff", color: "#52525B", fontSize: 12, fontWeight: 500, cursor: busy === o.id ? "default" : "pointer", fontFamily: "Inter, sans-serif" }}>
                      {busy === o.id ? "…" : "Unpaid"}
                    </button>
                  )}
                </span>
                <span style={{ display: "inline-flex", gap: 6, justifyContent: "flex-end" }}>
                  {o.kind === "shopify" && o.rawId && (
                    <a
                      href={`/api/orders/shopify/${encodeURIComponent(o.rawId)}/invoice.pdf`}
                      target="_blank"
                      rel="noreferrer noopener"
                      title="Download invoice PDF"
                      style={{ display: "inline-flex", alignItems: "center", justifyContent: "center", width: 26, height: 26, borderRadius: 6, border: "1px solid #E5E7EB", background: "#fff", color: "#52525B", textDecoration: "none" }}
                    >
                      <Download size={13} strokeWidth={2} />
                    </a>
                  )}
                  {o.adminUrl ? (
                    <a
                      href={o.adminUrl}
                      target="_blank"
                      rel="noreferrer noopener"
                      title="Open in Shopify admin"
                      style={{ display: "inline-flex", alignItems: "center", justifyContent: "center", width: 26, height: 26, borderRadius: 6, border: "1px solid #E5E7EB", background: "#fff", color: "#52525B", textDecoration: "none", fontSize: 13, fontWeight: 600, fontFamily: "Inter, sans-serif" }}
                    >↗</a>
                  ) : null}
                </span>
              </div>
            );
          })}
        </div>
      )}
    </div>
  );
}

/* ─── Email statement modal ─────────────────────────────────────────
   Two-phase modal:
     1. SETUP — pops the first time the user clicks Email statement and
        the workspace hasn't picked an email handle yet. Same fields as
        Settings → Email (handle, display name, reply-to). After save
        the modal flips into CONFIRM mode automatically.
     2. CONFIRM — shows From / To / Subject preview + the statement
        summary. One click sends the email.
   The state machine is: loading → (setup → saving →) confirm → sending → done. */
function EmailStatementModal({ client, orders, totals, onClose }) {
  const [phase, setPhase] = useState("loading"); // loading | setup | confirm | done | error
  const [identity, setIdentity] = useState(null); // { fromHeader, replyTo, displayName, domain, configured, ... }
  const [saving, setSaving] = useState(false);
  const [sending, setSending] = useState(false);
  const [result, setResult] = useState(null); // { delivered, devMode, error, to }
  const [errors, setErrors] = useState({});
  const [form, setForm] = useState({ localPart: "", displayName: "", replyTo: "" });
  const [availability, setAvailability] = useState(null);
  const [checking, setChecking] = useState(false);
  // Optional free-form note that gets dropped at the top of the
  // statement email body. Keeps the statement template fixed but lets
  // the operator personalise the message per-send ("Hi Karina, end
  // of quarter — please settle by Friday.").
  const [customMessage, setCustomMessage] = useState("");

  useEffect(() => {
    (async () => {
      try {
        const r = await fetch("/api/workspace/email", { credentials: "same-origin" });
        const d = await r.json();
        setIdentity(d);
        if (d.configured) setPhase("confirm");
        else {
          setForm({
            localPart: "",
            displayName: d.displayName || d.teamName || "",
            replyTo: d.replyTo || "",
          });
          setPhase("setup");
        }
      } catch {
        setPhase("error");
      }
    })();
  }, []);

  // Live uniqueness check
  useEffect(() => {
    if (phase !== "setup") return;
    const lp = (form.localPart || "").trim().toLowerCase();
    if (!lp) { setAvailability(null); return; }
    setChecking(true);
    const t = setTimeout(async () => {
      try {
        const r = await fetch(`/api/workspace/email/check?localPart=${encodeURIComponent(lp)}`, { credentials: "same-origin" });
        const d = await r.json();
        setAvailability(d);
      } catch { setAvailability(null); }
      finally { setChecking(false); }
    }, 350);
    return () => { clearTimeout(t); setChecking(false); };
  }, [form.localPart, phase]);

  const domain = identity?.domain || "finance.elionlabs.org";
  const previewLocal = (form.localPart || "").trim().toLowerCase() || "your-handle";
  const previewName = (form.displayName || "").trim() || "Your Workspace";
  const previewFrom = `${previewName} <${previewLocal}@${domain}>`;

  const saveSetup = async () => {
    setErrors({});
    setSaving(true);
    try {
      const r = await fetch("/api/workspace/email", {
        method: "PUT", credentials: "same-origin",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({
          localPart: form.localPart.trim().toLowerCase(),
          displayName: form.displayName.trim(),
          replyTo: form.replyTo.trim(),
        }),
      });
      const d = await r.json();
      if (!r.ok) {
        setErrors(d.field ? { [d.field]: d.error } : { _: d.error || "Save failed." });
        return;
      }
      setIdentity(prev => ({ ...(prev || {}), ...d, configured: true }));
      setPhase("confirm");
    } finally { setSaving(false); }
  };

  const sendStatement = async (previewMode = false) => {
    setSending(true);
    setResult(null);
    try {
      const r = await fetch(`/api/clients/${encodeURIComponent(client.id)}/email-statement`, {
        method: "POST", credentials: "same-origin",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ orders, totals, previewMode, customMessage: customMessage.trim() || undefined }),
      });
      const d = await r.json();
      if (!r.ok) {
        setResult({ ok: false, error: d.error || "Send failed.", previewMode });
        setPhase("done");
        return;
      }
      setResult({ ok: true, ...d });
      setPhase("done");
    } catch (err) {
      setResult({ ok: false, error: err.message || "Send failed.", previewMode });
      setPhase("done");
    } finally { setSending(false); }
  };

  const fmtMoney = (n) => `$${(Number(n) || 0).toFixed(2)}`;
  const unpaidOrders = (orders || []).filter(o => Number(o.outstanding) > 0);

  // Shared modal frame
  const overlay = { position: "fixed", inset: 0, background: "rgba(0,0,0,0.4)", zIndex: 1000, display: "flex", alignItems: "center", justifyContent: "center", padding: 24 };
  const card = { width: "100%", maxWidth: 520, background: "#fff", borderRadius: 14, border: "1px solid #E4E4E7", overflow: "hidden", boxShadow: "0 20px 60px rgba(0,0,0,0.18)", fontFamily: "Inter, sans-serif" };
  const header = { padding: "20px 24px 12px 24px", borderBottom: "1px solid #F3F4F6", display: "flex", alignItems: "center", gap: 10 };
  const body = { padding: "20px 24px" };
  const footer = { padding: "16px 24px", borderTop: "1px solid #F3F4F6", display: "flex", justifyContent: "flex-end", gap: 10, alignItems: "center" };
  const closeBtn = { background: "none", border: "none", color: "#6B7280", cursor: "pointer", padding: 4, borderRadius: 6, display: "flex", alignItems: "center", justifyContent: "center" };
  const labelStyle = { display: "block", fontSize: 12, fontWeight: 500, letterSpacing: "0.04em", textTransform: "uppercase", color: "#6B7280", marginBottom: 6 };
  const inputStyle = { width: "100%", padding: "9px 12px", borderRadius: 8, border: "1px solid #E5E7EB", fontSize: 13, fontFamily: "Inter, sans-serif", color: "#000", outline: "none", boxSizing: "border-box" };

  return (
    <div style={overlay} onClick={onClose}>
      <div style={card} onClick={e => e.stopPropagation()}>
        <div style={header}>
          <div style={{ width: 32, height: 32, borderRadius: 8, background: "#F3F4F6", display: "flex", alignItems: "center", justifyContent: "center" }}>
            <Mail size={16} color="#000" strokeWidth={1.8} />
          </div>
          <div style={{ flex: 1, minWidth: 0 }}>
            <div style={{ fontSize: 15, fontWeight: 600, color: "#000" }}>
              {phase === "setup" ? "Set up your sending email" : "Email statement"}
            </div>
            <div style={{ fontSize: 12, color: "#6B7280", marginTop: 1 }}>
              {phase === "setup"
                ? "First time sending — pick how customers see you in their inbox."
                : `to ${client.name || client.email}`}
            </div>
          </div>
          <button onClick={onClose} style={closeBtn} aria-label="Close"><X size={16} /></button>
        </div>

        {phase === "loading" && (
          <div style={{ ...body, textAlign: "center", color: "#9CA3AF", fontSize: 13 }}>Loading…</div>
        )}

        {phase === "error" && (
          <div style={{ ...body, color: "#991B1B", fontSize: 13 }}>Couldn't load email settings.</div>
        )}

        {phase === "setup" && (
          <div style={body}>
            {/* Live preview */}
            <div style={{ marginBottom: 16, padding: "12px 14px", background: "#F9FAFB", border: "1px solid #E5E7EB", borderRadius: 10 }}>
              <div style={{ fontSize: 10, fontWeight: 500, letterSpacing: "0.06em", textTransform: "uppercase", color: "#6B7280", marginBottom: 4 }}>From header preview</div>
              <div style={{ fontSize: 13, color: "#000", fontFamily: "ui-monospace, SFMono-Regular, Menlo, monospace", wordBreak: "break-all" }}>{previewFrom}</div>
            </div>
            <div style={{ marginBottom: 14 }}>
              <label style={labelStyle}>Email handle</label>
              <div style={{ display: "flex", alignItems: "stretch", border: `1px solid ${errors.localPart || availability?.available === false ? "#FCA5A5" : "#E5E7EB"}`, borderRadius: 8, overflow: "hidden", background: "#fff" }}>
                <input
                  type="text"
                  value={form.localPart}
                  onChange={e => setForm(f => ({ ...f, localPart: e.target.value.toLowerCase().replace(/[^a-z0-9-]/g, "") }))}
                  placeholder="progeny"
                  autoFocus
                  autoCapitalize="off" autoCorrect="off" spellCheck={false}
                  style={{ ...inputStyle, border: "none", borderRadius: 0, flex: 1, fontFamily: "ui-monospace, SFMono-Regular, Menlo, monospace" }}
                />
                <div style={{ display: "flex", alignItems: "center", padding: "0 12px", background: "#F4F4F5", color: "#6B7280", fontSize: 12, fontFamily: "ui-monospace, SFMono-Regular, Menlo, monospace", borderLeft: "1px solid #E5E7EB", whiteSpace: "nowrap" }}>
                  @{domain}
                </div>
              </div>
              {errors.localPart && <div style={{ marginTop: 6, fontSize: 12, color: "#991B1B" }}>{errors.localPart}</div>}
              {!errors.localPart && checking && <div style={{ marginTop: 6, fontSize: 12, color: "#6B7280" }}>Checking…</div>}
              {!errors.localPart && !checking && availability?.available === true && <div style={{ marginTop: 6, fontSize: 12, color: "#166534" }}>Available.</div>}
              {!errors.localPart && !checking && availability && availability.available === false && <div style={{ marginTop: 6, fontSize: 12, color: "#991B1B" }}>{availability.reason}</div>}
              {!errors.localPart && !checking && !availability && <div style={{ marginTop: 6, fontSize: 12, color: "#9CA3AF" }}>Lowercase letters, numbers, dashes. 3–32 chars.</div>}
            </div>
            <div style={{ marginBottom: 14 }}>
              <label style={labelStyle}>Display name</label>
              <input type="text" value={form.displayName} onChange={e => setForm(f => ({ ...f, displayName: e.target.value }))} placeholder="Progeny Coffee" style={{ ...inputStyle, borderColor: errors.displayName ? "#FCA5A5" : "#E5E7EB" }} />
              {errors.displayName && <div style={{ marginTop: 6, fontSize: 12, color: "#991B1B" }}>{errors.displayName}</div>}
            </div>
            <div>
              <label style={labelStyle}>Reply-to email</label>
              <input type="email" value={form.replyTo} onChange={e => setForm(f => ({ ...f, replyTo: e.target.value }))} placeholder="billing@progenycoffee.com" autoCapitalize="off" autoCorrect="off" spellCheck={false} style={{ ...inputStyle, borderColor: errors.replyTo ? "#FCA5A5" : "#E5E7EB" }} />
              {errors.replyTo
                ? <div style={{ marginTop: 6, fontSize: 12, color: "#991B1B" }}>{errors.replyTo}</div>
                : <div style={{ marginTop: 6, fontSize: 12, color: "#9CA3AF" }}>Replies route here, not to Elion.</div>}
            </div>
            {errors._ && <div style={{ marginTop: 12, padding: "8px 10px", borderRadius: 8, background: "#FEE2E2", color: "#991B1B", fontSize: 12 }}>{errors._}</div>}
          </div>
        )}

        {phase === "confirm" && (
          <div style={body}>
            <div style={{ marginBottom: 14, padding: "12px 14px", background: "#F9FAFB", border: "1px solid #E5E7EB", borderRadius: 10, display: "grid", gridTemplateColumns: "auto 1fr", rowGap: 8, columnGap: 14 }}>
              <div style={{ fontSize: 11, fontWeight: 500, letterSpacing: "0.06em", textTransform: "uppercase", color: "#6B7280" }}>From</div>
              <div style={{ fontSize: 13, color: "#000", fontFamily: "ui-monospace, SFMono-Regular, Menlo, monospace", wordBreak: "break-all" }}>{identity?.fromHeader}</div>
              <div style={{ fontSize: 11, fontWeight: 500, letterSpacing: "0.06em", textTransform: "uppercase", color: "#6B7280" }}>To</div>
              <div style={{ fontSize: 13, color: "#000" }}>{client.name ? `${client.name} <${client.email}>` : client.email}</div>
              {identity?.replyTo && (<>
                <div style={{ fontSize: 11, fontWeight: 500, letterSpacing: "0.06em", textTransform: "uppercase", color: "#6B7280" }}>Reply-To</div>
                <div style={{ fontSize: 13, color: "#000" }}>{identity.replyTo}</div>
              </>)}
            </div>
            <div style={{ marginBottom: 4, fontSize: 11, fontWeight: 500, letterSpacing: "0.06em", textTransform: "uppercase", color: "#6B7280" }}>Statement summary</div>
            <div style={{ padding: "12px 14px", border: "1px solid #E5E7EB", borderRadius: 10 }}>
              <div style={{ fontSize: 13, color: "#3F3F46", lineHeight: 1.55 }}>
                {unpaidOrders.length} outstanding order{unpaidOrders.length === 1 ? "" : "s"}
              </div>
              <div style={{ display: "flex", justifyContent: "space-between", alignItems: "baseline", marginTop: 6 }}>
                <div style={{ fontSize: 12, color: "#6B7280" }}>Total outstanding</div>
                <div style={{ fontSize: 18, fontWeight: 700, color: "#000", fontVariantNumeric: "tabular-nums" }}>{fmtMoney(totals?.unpaid || 0)}</div>
              </div>
              {Number(totals?.overdue) > 0 && (
                <div style={{ display: "flex", justifyContent: "space-between", alignItems: "baseline", marginTop: 4 }}>
                  <div style={{ fontSize: 12, color: "#6B7280" }}>Of which overdue</div>
                  <div style={{ fontSize: 13, fontWeight: 600, color: "#B91C1C", fontVariantNumeric: "tabular-nums" }}>{fmtMoney(totals.overdue)}</div>
                </div>
              )}
            </div>

            {/* Optional custom message — appears at the top of the
                statement body if provided. */}
            <div style={{ marginTop: 14 }}>
              <label style={{ display: "block", fontSize: 11, fontWeight: 500, letterSpacing: "0.06em", textTransform: "uppercase", color: "#6B7280", marginBottom: 6 }}>
                Custom message <span style={{ textTransform: "none", fontWeight: 400, letterSpacing: 0, color: "#9CA3AF" }}>(optional)</span>
              </label>
              <textarea
                value={customMessage}
                onChange={(e) => setCustomMessage(e.target.value)}
                placeholder={`Add a short note — e.g. "End of quarter, please settle by Friday."`}
                rows={3}
                style={{ width: "100%", padding: "9px 12px", borderRadius: 8, border: "1px solid #E5E7EB", fontSize: 13, fontFamily: "Inter, sans-serif", color: "#000", outline: "none", resize: "vertical", boxSizing: "border-box", lineHeight: 1.45 }}
              />
              <div style={{ marginTop: 4, fontSize: 11, color: "#9CA3AF" }}>
                Appears above the orders table in the email body.
              </div>
            </div>

            {!client.email && (
              <div style={{ marginTop: 12, padding: "10px 12px", borderRadius: 8, background: "#FEE2E2", color: "#991B1B", fontSize: 12 }}>
                This customer has no email on file. Add one before sending.
              </div>
            )}
          </div>
        )}

        {phase === "done" && (
          <div style={body}>
            {result?.ok ? (
              <div style={{ padding: "14px 16px", borderRadius: 10, background: "#DCFCE7", color: "#166534", fontSize: 13, lineHeight: 1.55 }}>
                {result.devMode
                  ? <>SMTP isn't configured on this environment, so the statement was logged to the server console rather than delivered to {result.to}.</>
                  : result.delivered
                    ? (result.previewMode
                      ? <>Preview sent to <strong>{result.to}</strong>. The customer didn't receive anything.</>
                      : <>Statement sent to <strong>{result.to}</strong>.</>)
                    : <>Send returned no delivery confirmation. {result.error ? `(${result.error})` : ""}</>}
              </div>
            ) : (
              <div style={{ padding: "14px 16px", borderRadius: 10, background: "#FEE2E2", color: "#991B1B", fontSize: 13, lineHeight: 1.55 }}>
                Couldn't send: {result?.error || "Unknown error"}
              </div>
            )}
          </div>
        )}

        <div style={footer}>
          {phase === "setup" && (<>
            <button onClick={onClose} style={{ padding: "8px 14px", borderRadius: 8, border: "1px solid #E5E7EB", background: "#fff", color: "#000", fontSize: 13, fontWeight: 500, cursor: "pointer", fontFamily: "Inter, sans-serif" }}>Cancel</button>
            <button
              onClick={saveSetup}
              disabled={saving || !form.localPart.trim() || !form.displayName.trim() || !form.replyTo.trim() || (availability && availability.available === false)}
              style={{ padding: "8px 14px", borderRadius: 8, border: "1px solid #000", background: "#000", color: "#fff", fontSize: 13, fontWeight: 600, cursor: saving ? "default" : "pointer", fontFamily: "Inter, sans-serif", opacity: (saving || !form.localPart.trim() || !form.displayName.trim() || !form.replyTo.trim() || (availability && availability.available === false)) ? 0.5 : 1 }}
            >
              {saving ? "Saving…" : "Save and continue"}
            </button>
          </>)}
          {phase === "confirm" && (<>
            <button onClick={onClose} style={{ padding: "8px 14px", borderRadius: 8, border: "1px solid #E5E7EB", background: "#fff", color: "#000", fontSize: 13, fontWeight: 500, cursor: "pointer", fontFamily: "Inter, sans-serif" }}>Cancel</button>
            <button
              onClick={() => sendStatement(true)}
              disabled={sending || unpaidOrders.length === 0}
              title="Send a preview to your account email instead of the customer"
              style={{ display: "inline-flex", alignItems: "center", gap: 6, padding: "8px 14px", borderRadius: 8, border: "1px solid #E5E7EB", background: "#fff", color: "#000", fontSize: 13, fontWeight: 500, cursor: sending || unpaidOrders.length === 0 ? "default" : "pointer", fontFamily: "Inter, sans-serif", opacity: sending || unpaidOrders.length === 0 ? 0.5 : 1, marginRight: "auto" }}
            >
              <Send size={13} strokeWidth={2.2} /> Send test to me
            </button>
            <button
              onClick={() => sendStatement(false)}
              disabled={sending || !client.email || unpaidOrders.length === 0}
              style={{ display: "inline-flex", alignItems: "center", gap: 6, padding: "8px 14px", borderRadius: 8, border: "1px solid #000", background: "#000", color: "#fff", fontSize: 13, fontWeight: 600, cursor: sending || !client.email || unpaidOrders.length === 0 ? "default" : "pointer", fontFamily: "Inter, sans-serif", opacity: sending || !client.email || unpaidOrders.length === 0 ? 0.5 : 1 }}
            >
              <Send size={13} strokeWidth={2.2} /> {sending ? "Sending…" : "Send statement"}
            </button>
          </>)}
          {phase === "done" && (<>
            {result?.previewMode && result?.ok && (
              <button onClick={() => { setResult(null); setPhase("confirm"); }} style={{ padding: "8px 14px", borderRadius: 8, border: "1px solid #E5E7EB", background: "#fff", color: "#000", fontSize: 13, fontWeight: 500, cursor: "pointer", fontFamily: "Inter, sans-serif", marginRight: "auto" }}>← Back</button>
            )}
            <button onClick={onClose} style={{ padding: "8px 14px", borderRadius: 8, border: "1px solid #000", background: "#000", color: "#fff", fontSize: 13, fontWeight: 600, cursor: "pointer", fontFamily: "Inter, sans-serif" }}>Done</button>
          </>)}
          {(phase === "loading" || phase === "error") && (
            <button onClick={onClose} style={{ padding: "8px 14px", borderRadius: 8, border: "1px solid #E5E7EB", background: "#fff", color: "#000", fontSize: 13, fontWeight: 500, cursor: "pointer", fontFamily: "Inter, sans-serif" }}>Close</button>
          )}
        </div>
      </div>
    </div>
  );
}

/* ─── Associated emails editor ───────────────────────────────────
   Lets the operator manage which inbound addresses map to this client.
   Primary email = the address used for OUTBOUND statement sends (kept
   in `client.email` so existing flows stay unchanged). The alt list is
   only used when WE receive mail — covers the case where a customer
   has registered with a shared inbox / Google Group and individual
   members write in from their own addresses (sometimes external
   domains like steffy@canteen.com). */
function ClientEmailsCard({ client, onUpdated, patchClientLocal }) {
  const [adding, setAdding] = useState(false);
  const [newEmail, setNewEmail] = useState("");
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState("");

  const lc = (s) => String(s || "").trim().toLowerCase();
  const altEmails = Array.isArray(client.altEmails) ? client.altEmails : [];
  const primary = (client.email || "").trim();
  // apEmails is the explicit multi-AP list. Legacy single `apEmail` is
  // merged in for older records that haven't been edited yet.
  const explicitAps = Array.isArray(client.apEmails) ? client.apEmails.filter(Boolean) : [];
  const legacyAp = (client.apEmail || "").trim();
  const apSet = new Set(
    [...explicitAps, ...(legacyAp ? [legacyAp] : [])].map(lc)
  );
  const hasExplicitAp = apSet.size > 0;
  // Explicit CC list — outbound statement/invoice CC line.
  const ccEmails = Array.isArray(client.ccEmails) ? client.ccEmails.filter(Boolean) : [];
  const ccSet = new Set(ccEmails.map(lc));

  // When no explicit AP is set, the primary is implicitly the AP —
  // statements go to the primary by default. Reflect that in the UI
  // so the user sees what'll actually happen.
  const primaryIsAp = !hasExplicitAp || apSet.has(lc(primary));

  // Build a flat list of every associated email with its role flags so
  // we can render a single ordered table. Order: primary → APs not
  // already represented → alts (file order).
  const rows = [];
  if (primary) rows.push({ email: primary, isPrimary: true, isAp: primaryIsAp, isCc: ccSet.has(lc(primary)) });
  for (const ap of explicitAps) {
    if (lc(ap) === lc(primary)) continue;
    if (altEmails.some(a => lc(a) === lc(ap))) continue;
    rows.push({ email: ap, isPrimary: false, isAp: true, isCc: ccSet.has(lc(ap)) });
  }
  for (const a of altEmails) {
    if (lc(a) === lc(primary)) continue;
    rows.push({ email: a, isPrimary: false, isAp: apSet.has(lc(a)), isCc: ccSet.has(lc(a)) });
  }

  // Optimistic patch: update local state immediately so the UI doesn't
  // flicker through a full refetch every time the operator toggles an
  // email. On server failure we DO refetch so the row snaps back to
  // truth — same pattern as the archive button.
  const patch = async (body) => {
    setBusy(true); setError("");
    if (patchClientLocal) patchClientLocal(client.id, body);
    try {
      const r = await fetch(`/api/clients/${encodeURIComponent(client.id)}`, {
        method: "PATCH", credentials: "same-origin",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify(body),
      });
      if (!r.ok) {
        const d = await r.json().catch(() => ({}));
        setError(d.error || "Save failed");
        // Server rejected — fall back to a full refresh so the UI
        // reflects truth, not our optimistic guess.
        onUpdated && onUpdated();
        return false;
      }
      return true;
    } finally { setBusy(false); }
  };

  const addAlt = async () => {
    const e = newEmail.trim().toLowerCase();
    if (!e) { setError("Enter an email."); return; }
    if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(e)) { setError("Not a valid email."); return; }
    if (e === lc(primary)) { setError("Already the primary."); return; }
    if (altEmails.map(lc).includes(e)) { setError("Already added."); return; }
    const ok = await patch({ altEmails: [...altEmails, e] });
    if (ok) { setNewEmail(""); setAdding(false); }
  };

  const removeAlt = async (e) => {
    const body = {
      altEmails: altEmails.filter(a => lc(a) !== lc(e)),
      // Drop the AP flag if it was on this email.
      apEmails: explicitAps.filter(a => lc(a) !== lc(e)),
      // Drop CC flag too — a removed email shouldn't keep CC'ing.
      ccEmails: ccEmails.filter(a => lc(a) !== lc(e)),
    };
    // Clear the legacy single-AP field too if it pointed here.
    if (lc(legacyAp) === lc(e)) body.apEmail = null;
    await patch(body);
  };

  const makePrimary = async (e) => {
    const newAlts = [primary, ...altEmails.filter(a => lc(a) !== lc(e))].filter(Boolean);
    // The new primary is implicitly the AP — drop any explicit AP list
    // so we don't double-track. The customer's name (account holder)
    // stays untouched — it identifies the account on the customers
    // list and must be 1:1 with the orders, NOT with the AP/primary
    // email contact. Pre-filled friendly-name on the outbound "To:"
    // is handled separately at send time.
    await patch({
      email: e,
      altEmails: newAlts,
      apEmails: null,
      apEmail: null,
    });
  };

  const setCc = async (e) => {
    const already = ccSet.has(lc(e));
    const next = already
      ? ccEmails.filter(a => lc(a) !== lc(e))
      : [...ccEmails.filter(a => lc(a) !== lc(e)), e];
    await patch({ ccEmails: next });
  };

  const setAp = async (e) => {
    // Toggle membership in the apEmails set.
    const isAlready = apSet.has(lc(e));
    const baseList = (explicitAps.length > 0 || !legacyAp)
      ? explicitAps
      : [legacyAp];
    const next = isAlready
      ? baseList.filter(a => lc(a) !== lc(e))
      : [...baseList.filter(a => lc(a) !== lc(e)), e];
    // Clear the legacy field whenever we touch APs — keep the data
    // model on the new array and don't double-track.
    await patch({ apEmails: next, apEmail: null });
  };

  // Subtle role tag — small caps text, no shape. Looks like a
  // metadata column rather than a chip.
  const roleTag = (text, color) => (
    <span style={{ display: "inline-block", width: 64, fontSize: 10, fontWeight: 700, letterSpacing: "0.08em", textTransform: "uppercase", color, fontFamily: "Inter, sans-serif" }}>
      {text}
    </span>
  );
  const linkBtn = (label, onClick, accent = false) => (
    <button
      onClick={onClick}
      disabled={busy}
      style={{ background: "none", border: "none", padding: 0, margin: 0, fontSize: 11, color: accent ? "#000" : "#6B7280", cursor: busy ? "default" : "pointer", textDecoration: "underline", fontFamily: "Inter, sans-serif", fontWeight: accent ? 600 : 400 }}
    >
      {label}
    </button>
  );

  return (
    <div style={{ background: "#fff", border: "1px solid #E5E7EB", borderRadius: 10, marginBottom: 12 }}>
      <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", padding: "10px 14px", borderBottom: rows.length > 0 ? "1px solid #F3F4F6" : "none" }}>
        <div style={{ fontSize: 10, fontWeight: 700, color: "#9CA3AF", textTransform: "uppercase", letterSpacing: "0.07em" }}>Associated emails</div>
        {!adding && (
          <button
            onClick={() => { setAdding(true); setError(""); }}
            style={{ display: "inline-flex", alignItems: "center", gap: 4, background: "none", border: "1px solid #E5E7EB", borderRadius: 6, padding: "3px 8px", fontSize: 11, fontWeight: 500, color: "#000", cursor: "pointer", fontFamily: "Inter, sans-serif" }}
          >
            <Plus size={11} strokeWidth={2.2} /> Add
          </button>
        )}
      </div>

      {rows.length === 0 && !adding && (
        <div style={{ padding: "14px", fontSize: 12, color: "#9CA3AF", textAlign: "center" }}>No emails on file.</div>
      )}

      {rows.map((r, idx) => {
        // The row badge shows the EFFECTIVE AP state (including the
        // implicit primary fallback). The toggle button label uses
        // EXPLICIT membership only — clicking on the implicit-AP
        // primary adds it explicitly, click again to remove.
        const isApExplicit = apSet.has(lc(r.email));
        return (
          <div
            key={r.email}
            style={{ display: "flex", alignItems: "center", gap: 12, padding: "10px 14px", borderTop: idx === 0 ? "none" : "1px solid #F3F4F6" }}
          >
            <div style={{ display: "flex", flexDirection: "column", gap: 2, minWidth: 64 }}>
              {r.isPrimary && roleTag("Primary", "#000")}
              {r.isAp && roleTag("AP", "#2C5766")}
              {r.isCc && roleTag("CC", "#7C3AED")}
              {!r.isPrimary && !r.isAp && !r.isCc && roleTag("—", "#D1D5DB")}
            </div>
            <div style={{ flex: 1, fontSize: 13, color: "#000", fontFamily: "ui-monospace, SFMono-Regular, Menlo, monospace", wordBreak: "break-all" }}>
              {r.email}
            </div>
            <div style={{ display: "flex", gap: 12, flexShrink: 0 }}>
              {!r.isPrimary && linkBtn("Make primary", () => makePrimary(r.email))}
              {!r.isPrimary && linkBtn(r.isCc ? "Remove CC" : "CC", () => setCc(r.email))}
              {!r.isPrimary && (
                <button
                  onClick={() => removeAlt(r.email)}
                  disabled={busy}
                  title="Remove"
                  style={{ background: "none", border: "none", padding: 0, color: "#9CA3AF", cursor: busy ? "default" : "pointer", display: "inline-flex", alignItems: "center" }}
                >
                  <X size={13} strokeWidth={2.4} />
                </button>
              )}
            </div>
          </div>
        );
      })}

      {adding && (
        <div style={{ padding: "10px 14px", borderTop: rows.length > 0 ? "1px solid #F3F4F6" : "none", display: "flex", gap: 6, alignItems: "stretch" }}>
          <input
            type="email"
            value={newEmail}
            onChange={e => { setNewEmail(e.target.value); setError(""); }}
            onKeyDown={e => { if (e.key === "Enter") addAlt(); if (e.key === "Escape") { setAdding(false); setNewEmail(""); setError(""); } }}
            placeholder="alice@progenycoffee.com"
            autoFocus autoCapitalize="off" autoCorrect="off" spellCheck={false}
            style={{ flex: 1, padding: "6px 10px", borderRadius: 6, border: `1px solid ${error ? "#FCA5A5" : "#E5E7EB"}`, fontSize: 12, fontFamily: "ui-monospace, SFMono-Regular, Menlo, monospace", color: "#000", outline: "none" }}
          />
          <button
            onClick={addAlt}
            disabled={busy || !newEmail.trim()}
            style={{ padding: "6px 12px", borderRadius: 6, border: "1px solid #000", background: busy || !newEmail.trim() ? "#F4F4F5" : "#000", color: busy || !newEmail.trim() ? "#9CA3AF" : "#fff", fontSize: 12, fontWeight: 600, cursor: busy || !newEmail.trim() ? "default" : "pointer", fontFamily: "Inter, sans-serif" }}
          >
            Add
          </button>
          <button
            onClick={() => { setAdding(false); setNewEmail(""); setError(""); }}
            style={{ padding: "6px 10px", borderRadius: 6, border: "1px solid #E5E7EB", background: "#fff", color: "#000", fontSize: 12, fontWeight: 500, cursor: "pointer", fontFamily: "Inter, sans-serif" }}
          >
            Cancel
          </button>
        </div>
      )}
      {error && <div style={{ padding: "0 14px 10px 14px", fontSize: 11, color: "#991B1B" }}>{error}</div>}

      <div style={{ padding: "8px 14px 10px 14px", borderTop: "1px solid #F3F4F6", fontSize: 11, color: "#9CA3AF", lineHeight: 1.5 }}>
        <strong style={{ color: "#6B7280", fontWeight: 600 }}>Primary</strong> — used for general communications.
        <strong style={{ color: "#6B7280", fontWeight: 600, marginLeft: 8 }}>AP</strong> — receives statements & invoices. Mark multiple — they'll all be on the email (first as <em>To</em>, the rest <em>CC</em>'d). With no AP set, the primary is used by default.
        <strong style={{ color: "#6B7280", fontWeight: 600, marginLeft: 8 }}>CC</strong> — added to the <em>CC</em> line on every statement / invoice. Use for billing approvers or shared finance inboxes that need visibility.
      </div>
    </div>
  );
}

function BackBar({ onBack, client, actions }) {
  return (
    <div style={{ marginBottom: 16, display: "flex", alignItems: "center", gap: 12 }}>
      <button
        onClick={onBack}
        title="Back to customers"
        aria-label="Back to customers"
        style={{
          display: "inline-flex", alignItems: "center", justifyContent: "center",
          width: 32, height: 32, padding: 0, borderRadius: 8,
          border: "1px solid #E5E7EB", background: "#fff", color: "#52525B",
          cursor: "pointer", flexShrink: 0,
          transition: "border-color 0.12s, color 0.12s, background 0.12s",
        }}
        onMouseEnter={e => { e.currentTarget.style.borderColor = "#000"; e.currentTarget.style.color = "#000"; }}
        onMouseLeave={e => { e.currentTarget.style.borderColor = "#E5E7EB"; e.currentTarget.style.color = "#52525B"; }}
      >
        <ArrowLeft size={15} strokeWidth={2} />
      </button>
      <div style={{ minWidth: 0, flex: 1 }}>
        <h1 style={{ fontSize: 22, fontWeight: 700, color: "#000", margin: 0, letterSpacing: "-0.015em", whiteSpace: "nowrap", overflow: "hidden", textOverflow: "ellipsis" }}>{client.name}</h1>
        <div style={{ fontSize: 12, color: "#6B7280", marginTop: 2 }}>
          {(() => {
            const company = [client.reportingCompany, client.reportingDistributor].filter(Boolean).join(" — ") || client.company;
            return [client.email, client.phone, company].filter(Boolean).join(" · ") || "—";
          })()}
        </div>
        {(client.shopifyLastOrderAt || client.lastStatementSentAt) && (
          <div style={{ fontSize: 11, color: "#9CA3AF", marginTop: 2, display: "flex", gap: 12, flexWrap: "wrap" }}>
            {client.shopifyLastOrderAt && <span>Last order {fmtShortDate(client.shopifyLastOrderAt)}</span>}
            {client.lastStatementSentAt && <span>Last reminder {fmtShortDate(client.lastStatementSentAt)}</span>}
          </div>
        )}
      </div>
      {actions && <div style={{ display: "flex", gap: 8, flexShrink: 0 }}>{actions}</div>}
    </div>
  );
}

/* ── StatTile + Sparkline ───────────────────────────────────────────
   Reusable headline-stat tile with a 12-month sparkline area chart
   and a colored trend chip ("↑ 12% vs prior 3-mo avg" / "↓ 5%").
   Inline SVG so we don't need a chart library; renders zero-data
   gracefully (flat line). */
function StatTile({ label, value, valueColor, trend, direction = "good-up", suppressTrend = false, series, money, months, unit = "", active, onClick }) {
  const max = Math.max(1, ...series);
  const allZero = series.every(v => v === 0);
  const clickable = !!onClick;
  // Polarity: for "bad-up" metrics (overdue, debt) an up arrow is bad
  // and should render red; down green. Default "good-up" keeps the
  // existing semantics (more customers / orders = good).
  const upIsGood = direction !== "bad-up";
  const isPositive = trend && ((trend.dir === "up" && upIsGood) || (trend.dir === "down" && !upIsGood));
  const sparkCaveat = suppressTrend
    ? (label === "Overdue" || label === "Total due"
        ? "Snapshot of orders still unpaid today, projected back in time — historical values are biased low. Daily totals (more accurate) coming soon."
        : "Snapshot — projected from orders still unpaid today.")
    : null;
  return (
    <div
      onClick={onClick}
      style={{
        background: "#fff",
        border: `1px solid ${active ? "#000" : "#E5E7EB"}`,
        boxShadow: active ? "0 0 0 1px #000" : "none",
        borderRadius: 10, padding: "10px 14px 8px",
        cursor: clickable ? "pointer" : "default",
        transition: "border-color 0.12s, box-shadow 0.12s",
      }}
      onMouseEnter={e => { if (clickable && !active) e.currentTarget.style.borderColor = "#9CA3AF"; }}
      onMouseLeave={e => { if (clickable && !active) e.currentTarget.style.borderColor = "#E5E7EB"; }}
      title={sparkCaveat || undefined}
    >
      <div style={{ display: "flex", justifyContent: "space-between", alignItems: "baseline" }}>
        <div style={{ fontSize: 10, fontWeight: 700, color: "#9CA3AF", textTransform: "uppercase", letterSpacing: "0.06em" }}>{label}</div>
        {active && (
          <span style={{ fontSize: 9, fontWeight: 700, color: "#000", letterSpacing: "0.06em", textTransform: "uppercase" }}>● Filtering</span>
        )}
      </div>
      <div style={{ display: "flex", alignItems: "baseline", justifyContent: "space-between", gap: 6, marginTop: 2 }}>
        <div style={{ fontSize: 20, fontWeight: 700, color: valueColor || "#000", fontVariantNumeric: "tabular-nums" }}>{value}</div>
        {trend && (
          <span style={{
            display: "inline-flex", alignItems: "center", gap: 2,
            padding: "2px 7px", borderRadius: 99,
            background: isPositive ? "#ECFDF5" : "#FEF2F2",
            color: isPositive ? "#047857" : "#B91C1C",
            fontSize: 11, fontWeight: 700, lineHeight: 1.4,
            fontVariantNumeric: "tabular-nums",
            whiteSpace: "nowrap",
          }} title={`Last full month vs avg of the 3 months before that${money ? "" : " (count)"}`}>
            {trend.dir === "up" ? "↑" : "↓"} {trend.pct}%
          </span>
        )}
      </div>
      <Sparkline data={series} max={max} flat={allZero} months={months} muted={false} money={money} unit={unit} />
    </div>
  );
}

function Sparkline({ data, max, flat, months, muted = false, money = false, unit = "" }) {
  const W = 200;
  const H = 36;
  const pad = 2;
  const innerW = W - pad * 2;
  const innerH = H - pad * 2;
  const [hoverIdx, setHoverIdx] = React.useState(null);
  if (!data || data.length === 0) return <div style={{ height: H }} />;
  const stepX = data.length > 1 ? innerW / (data.length - 1) : 0;
  const yOf = (v) => pad + innerH - (max > 0 ? (v / max) * innerH : 0);
  const points = data.map((v, i) => `${pad + i * stepX},${yOf(v)}`).join(" ");
  const area = `M ${pad},${pad + innerH} L ${points.split(" ").join(" L ")} L ${pad + innerW},${pad + innerH} Z`;
  const lastIdx = data.length - 1;
  const fmtVal = (v) => {
    if (money) return `$${Math.round(v).toLocaleString()}`;
    const num = Number.isInteger(v) ? v.toLocaleString() : v.toLocaleString(undefined, { maximumFractionDigits: 1 });
    return unit ? `${num} ${unit}` : num;
  };
  // Muted styling — used on tiles where we can't honestly claim a
  // trend (see suppressTrend in StatTile). The chart is still a useful
  // shape cue but it doesn't read as a confident measurement.
  const stroke = muted ? "#9CA3AF" : "#111827";
  const fill   = muted ? "rgba(156,163,175,0.10)" : "rgba(17,24,39,0.06)";
  // Last-point dot rendered as an HTML element (not SVG) so the non-
  // uniform horizontal stretching of preserveAspectRatio="none" doesn't
  // squash the circle into an oval.
  const dotPctX = `${((pad + lastIdx * stepX) / W) * 100}%`;
  const dotPctY = `${(yOf(data[lastIdx]) / H) * 100}%`;
  return (
    <div style={{ position: "relative", marginTop: 6 }}>
    <svg width="100%" height={H} viewBox={`0 0 ${W} ${H}`} preserveAspectRatio="none" style={{ display: "block" }}>
      {!flat && (
        <>
          <path d={area} fill={fill} />
          <polyline points={points} fill="none" stroke={stroke} strokeWidth="1.4" strokeLinejoin="round" strokeLinecap="round" strokeDasharray={muted ? "3 3" : undefined} vectorEffect="non-scaling-stroke" />
        </>
      )}
      {flat && (
        <line x1={pad} y1={pad + innerH - 1} x2={pad + innerW} y2={pad + innerH - 1} stroke="#E5E7EB" strokeWidth="1" strokeDasharray="3 3" vectorEffect="non-scaling-stroke" />
      )}
      {/* Invisible hit-areas — one per data point — drive the hover
          state. Wider than the data points so it's easy to hit without
          aiming precisely. */}
      {!flat && data.map((_, i) => (
        <rect
          key={i}
          x={pad + i * stepX - stepX / 2}
          y={0}
          width={stepX || innerW}
          height={H}
          fill="transparent"
          style={{ cursor: "crosshair" }}
          onMouseEnter={() => setHoverIdx(i)}
          onMouseLeave={() => setHoverIdx(null)}
        />
      ))}
      {/* Hover guideline */}
      {hoverIdx != null && !flat && (
        <line
          x1={pad + hoverIdx * stepX} x2={pad + hoverIdx * stepX}
          y1={pad} y2={pad + innerH}
          stroke="#9CA3AF" strokeWidth="1" strokeDasharray="2 2"
          vectorEffect="non-scaling-stroke"
        />
      )}
    </svg>
      {!flat && hoverIdx == null && (
        <span style={{
          position: "absolute",
          left: dotPctX, top: dotPctY,
          width: 5, height: 5, borderRadius: "50%",
          background: stroke,
          transform: "translate(-50%, -50%)",
          pointerEvents: "none",
        }} />
      )}
      {!flat && hoverIdx != null && (
        <>
          <span style={{
            position: "absolute",
            left: `${((pad + hoverIdx * stepX) / W) * 100}%`,
            top: `${(yOf(data[hoverIdx]) / H) * 100}%`,
            width: 7, height: 7, borderRadius: "50%",
            background: "#fff",
            border: `2px solid ${stroke}`,
            transform: "translate(-50%, -50%)",
            pointerEvents: "none",
            boxShadow: "0 1px 3px rgba(0,0,0,0.15)",
          }} />
          {/* Tooltip — pinned above the hover dot, auto-flips below
              when there's not enough headroom. */}
          {(() => {
            const xPct = ((pad + hoverIdx * stepX) / W) * 100;
            const yPct = (yOf(data[hoverIdx]) / H) * 100;
            const flipBelow = yPct < 50;
            return (
              <span style={{
                position: "absolute",
                left: `${xPct}%`,
                top: flipBelow ? `calc(${yPct}% + 10px)` : `calc(${yPct}% - 10px)`,
                transform: flipBelow ? "translate(-50%, 0)" : "translate(-50%, -100%)",
                background: "#000",
                color: "#fff",
                fontSize: 11,
                fontWeight: 600,
                fontFamily: "Inter, sans-serif",
                padding: "4px 8px",
                borderRadius: 6,
                whiteSpace: "nowrap",
                pointerEvents: "none",
                fontVariantNumeric: "tabular-nums",
                zIndex: 5,
              }}>
                {months && months[hoverIdx] ? `${months[hoverIdx]} · ` : ""}{fmtVal(data[hoverIdx])}
              </span>
            );
          })()}
        </>
      )}
    </div>
  );
}
